// BankProcessingPage — 銀行取引処理ページ (2026-04-22, Phase 3 — UI/UX redesign)
//
// Reader B の "1 Row = 1 Decision" 原則に沿って全面リデザイン:
//   - 14列テーブル → 7列 + SidePanel + 行Expand廃止
//   - KPIカード5枚 + Filter8個 → ProgressBar + 6個 SegmentControl chip に統合 ("KPI-as-Filter")
//   - 5-Dot Progress indicator column (パイプライン可視化)
//   - "次アクション" button は動詞ではなく次状態予告形式
//   - window.confirm/prompt → Vibes `MessageDialogConfirm` / `TaskDialog`
//   - Empty/Loading/Error は Vibes `NoDataIllust` / `SkeletonBase` 相当を使用
//   - SidePanel に J/K prev/next ナビゲーション
//   - Bug-1 fix: freee_deal が not_reconciled flag の場合 "仕訳データ未同期 + Reconcile" ボタンを描画
//   - Bug-2 fix: `<th>` クリックで sort トグル、arrow indicator 描画
//
// 注: reconcile 実行は user 手動 (自発呼出は絶対禁止)。仕訳 POST は Claude A2A 経由。

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { MdSync, MdCheckCircle, MdError, MdStorage } from 'react-icons/md';
import {
  effectiveReconcileState,
  isReconcilePendingResponse,
  isReconcilePendingStale,
  reconcilePendingToastText,
  RECONCILE_STALE_LABEL,
} from '../lib/bankReconcilePending';
import {
  Container,
  PageTitle,
  SubSectionTitle,
  CardBase,
  ColumnBase,
  HStack,
  VStack,
  Button,
  SearchField,
  SelectBox,
  CheckBox,
  Text,
  Note,
  Breadcrumbs,
  MessageBlock,
  FloatingMessageBlock,
  TabBar,
  ProgressBar,
  SegmentControlButton,
  MessageDialogConfirm,
  TaskDialog,
  TextArea,
  NoDataIllust,
  NoSearchResultsIllust,
  AlertSwallow,
  InlineSpinner,
  StatusIcon,
} from '@freee_jp/vibes';
import {
  fetchBankWalletTxns,
  fetchBankSummary,
  fetchBankWalletTxnDetail,
  fetchBankFreeeDeal,
  fetchBankAuditLog,
  enqueueBankAnalysis,
  approveBankWalletTxn,
  rejectBankWalletTxn,
  retryBankRegister,
  rerunBankAnalysis,
  bulkEnqueueBankAnalysis,
  openSessionForBank,
  validateSession,
  fetchLearningMetrics,
  fetchBankLearningEvidence,
  mechanicalApproveBankWalletTxn,
  fetchFreeeReceipts,
  uploadFreeeReceipt,
  attachReceiptsToWalletTxn,
  detachReceiptFromWalletTxn,
  fetchFreeeReceiptDetail,
  freeeReceiptViewUrl,
  freeeReceiptDownloadUrl,
  autoAttachJcbPdf,
  stageJcbForWalletTxn,
  stageNicosForWalletTxn,
  stageManualUpload,
  listPendingReceipts,
  pendingReceiptPreviewUrl,
  commitPendingReceipt,
  deletePendingReceipt,
  fetchBankCorrections,
  createBankCorrection,
  updateBankCorrectionStatus,
  fetchBankEvidenceCandidates,
  stageAricaEvidence,
  attachReceiptNumberToWalletTxn,
  requestBankReceiptVision,
  fetchBankReceiptVision,
  learnVendorDefaultSection,
  fetchAccountItemsMap,
  fetchSectionsMap,
  fetchTaxCodesMap,
  fetchItemsMap,
  fetchTagsMap,
  fetchPartnersMap,
  FreeeReceipt,
  FreeeReceiptDetail,
  PendingReceipt,
  BankCorrection,
  BankEvidenceCandidate,
  BankReceiptVisionResult,
  BankReceiptVisionRefKind,
  BankWalletTxnRow,
  BankSummary,
  BankWalletTxnDetail,
  BankProcessingInfo,
  BankEvidenceSource,
  BankFreeeDealView,
  BankAuditEvent,
  BankWalletTxnsListOrder,
  LearningMetrics,
  BankLearningEvidence,
} from '../api/client';
import { EmptyDescPatternPicker } from '../components/EmptyDescPatternPicker';
import { useEventPolling } from '../hooks/useEventPolling';
import {
  bankReconcileStateToUnified,
  correctionStatusToUnified,
  unifiedHint,
  UnifiedBadge,
  UNIFIED_COLOR,
} from '../lib/statusVocab';

// ---------------------------------------------------------------------------
// Walletable metadata
// ---------------------------------------------------------------------------

interface WalletableMeta {
  walletable_id: number;
  walletable_type: 'bank_account' | 'credit_card' | 'wallet';
  name: string;
  tier: 'HIGH' | 'MID' | 'LOW' | 'DORMANT';
}

const WALLETABLES: WalletableMeta[] = [
  { walletable_id: 3838033, walletable_type: 'bank_account', name: '三井住友銀行普通預金', tier: 'HIGH' },
  { walletable_id: 3838050, walletable_type: 'bank_account', name: '三井住友銀行新宿口', tier: 'HIGH' },
  { walletable_id: 2122229, walletable_type: 'credit_card', name: '三井住友VISAカード', tier: 'HIGH' },
  { walletable_id: 3838074, walletable_type: 'bank_account', name: '三菱東京UFJ渋谷普通', tier: 'MID' },
  { walletable_id: 3838203, walletable_type: 'bank_account', name: '三菱東京UFJ喜乃字屋', tier: 'MID' },
  { walletable_id: 3838100, walletable_type: 'bank_account', name: '西武信用金庫現金管理', tier: 'MID' },
  { walletable_id: 3845767, walletable_type: 'bank_account', name: '商工組合中央金庫普通', tier: 'MID' },
  { walletable_id: 3838051, walletable_type: 'bank_account', name: '三井住友銀行当座預金', tier: 'LOW' },
  { walletable_id: 3838049, walletable_type: 'bank_account', name: '三井住友現金管理口座', tier: 'LOW' },
  { walletable_id: 3838184, walletable_type: 'bank_account', name: '三菱東京UFJ新宿普通', tier: 'LOW' },
  { walletable_id: 6447554, walletable_type: 'wallet', name: '未払費用（Amazon）', tier: 'LOW' },
  { walletable_id: 3838143, walletable_type: 'bank_account', name: '西武信用金庫当座預金', tier: 'DORMANT' },
  { walletable_id: 3838144, walletable_type: 'bank_account', name: '西武信用金庫普通預金', tier: 'DORMANT' },
  { walletable_id: 3838176, walletable_type: 'bank_account', name: '三菱東京UFJ秋葉原', tier: 'DORMANT' },
  { walletable_id: 3843633, walletable_type: 'bank_account', name: 'PayPay銀行普通預金', tier: 'DORMANT' },
  { walletable_id: 2122272, walletable_type: 'credit_card', name: 'JCBカード', tier: 'DORMANT' },
  { walletable_id: 6542846, walletable_type: 'wallet', name: 'Square（API）', tier: 'DORMANT' },
  { walletable_id: 6543034, walletable_type: 'wallet', name: 'Square売掛金', tier: 'DORMANT' },
  { walletable_id: 6543035, walletable_type: 'wallet', name: 'Squareその他', tier: 'DORMANT' },
];

// ---------------------------------------------------------------------------
// Filter Chip + advanced filter options
// ---------------------------------------------------------------------------

type ChipKey = 'all' | 'needs_review' | 'error' | 'pending' | 'analyzing' | 'mechanical_suggest' | 'mechanical_reconcile' | 'rule_waiting' | 'registered' | 'approved_unregistered';

// 状態語彙は lib/statusVocab.ts (正本) 準拠 — suggested/needs_review = AI提案の承認待ち (エラーではない)
const CHIP_DEFS: { key: ChipKey; label: string; accent: string }[] = [
  { key: 'all',                 label: '全て',         accent: '#2e7cf6' },
  { key: 'analyzing',           label: '分析中',       accent: '#48859d' },
  { key: 'mechanical_suggest',  label: '機械提案',     accent: '#7c5cff' },
  { key: 'mechanical_reconcile',label: '消込のみ',     accent: '#0fa19c' },
  { key: 'needs_review',        label: '対応待ち(承認)', accent: '#a46a12' },
  { key: 'error',               label: '対応待ち(処理失敗)', accent: '#a46a12' },
  { key: 'pending',             label: '未処理',       accent: '#8a8580' },
  { key: 'rule_waiting',        label: '自動登録待ち', accent: '#22b8cf' },
  // 2026-08-25 P1 fix (ぐるなび¥100 incident): 承認済だが register-after-approve が
  //   未実行/失敗のまま freee 未登録で滞留する行。従来どの chip にも入らず不可視だった。
  { key: 'approved_unregistered', label: '承認済(freee未登録)', accent: '#c1440e' },
  { key: 'registered',          label: '登録済',       accent: '#338650' },
];

// 2026-07-13: 一括サジェストの client 側チャンクサイズ (worker の ≤50件/回 400 ガードに合わせる)
const BULK_ENQUEUE_CHUNK_SIZE = 50;

const WTYPE_OPTIONS = [
  { name: 'すべての口座種別', value: 'all' },
  { name: '銀行口座', value: 'bank_account' },
  { name: 'クレジットカード', value: 'credit_card' },
  { name: 'ウォレット', value: 'wallet' },
];

const FLOW_OPTIONS = [
  { name: 'すべての分類', value: 'all' },
  { name: '銀行引落 (bank_auto_debit)', value: 'bank_auto_debit' },
  { name: '銀行入金 (bank_transfer_in)', value: 'bank_transfer_in' },
  { name: 'クレカ/立替 (card_payment)', value: 'card_payment' },
  { name: '振替 (transfer)', value: 'transfer' },
  { name: '未判定 (unknown)', value: 'unknown' },
];

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const S = {
  page: {
    background: '#f7f5f5',
    minHeight: '100vh',
    paddingBottom: '3rem',
  } as React.CSSProperties,

  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0',
    transition: 'grid-template-columns 0.25s ease',
  } as React.CSSProperties,

  mainLayoutWithPanel: {
    display: 'grid',
    // 2026-04-23: panel幅 540px (狭い panel 改善)。進捗/次アクション列は panel 内で表示するためテーブルから除外。
    // 2026-06-09 Reader A: 提案仕訳 8列が窮屈なため panel を 540px→760px に拡幅 (+220px)。
    // 左の取引一覧 (minmax(0,1fr)) はその分自動で縮小。取引内容(account, width:auto)列が縮み
    // 余白を panel に回す。横スクロールは bp-table-container の container query が吸収。
    gridTemplateColumns: 'minmax(0, 1fr) 760px',
    gap: '16px',
    alignItems: 'stretch',
  } as React.CSSProperties,

  thCell: {
    padding: '10px 12px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: '#6b6560',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap' as const,
    borderBottom: '2px solid #dbd9d7',
    background: '#fafaf8',
    userSelect: 'none' as const,
  } as React.CSSProperties,

  thSortable: {
    cursor: 'pointer',
  } as React.CSSProperties,

  thSortActive: {
    color: '#2e7cf6',
  } as React.CSSProperties,

  tdCell: {
    padding: '12px 12px',
    fontSize: '0.8125rem',
    verticalAlign: 'middle' as const,
    borderBottom: '1px solid #edebea',
  } as React.CSSProperties,

  row: (isSelected: boolean, isActive: boolean, isFocused: boolean) => ({
    cursor: 'pointer',
    transition: 'background 0.12s ease, box-shadow 0.12s ease',
    background: isActive ? '#ebf3ff' : isSelected ? '#f0f8ff' : 'transparent',
    outline: isFocused ? '2px solid #2e7cf6' : 'none',
    outlineOffset: '-2px',
    // 2026-04-23 Reader A: left-border indicator on active / focused row
    borderLeft:
      isActive ? '3px solid #2e7cf6'
      : isFocused ? '3px solid rgba(46,124,246,0.4)'
      : '3px solid transparent',
  }),

  badge: (bg: string, color: string) => ({
    display: 'inline-block',
    fontSize: '0.625rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    lineHeight: '1.4',
    background: bg,
    color,
    whiteSpace: 'nowrap' as const,
  }),

  sidePanel: {
    position: 'sticky' as const,
    top: '16px',
    maxHeight: 'calc(100vh - 32px)',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    background: '#fff',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #e5e4e2',
    // 2026-04-23 Reader A: fade-in on panel open
    animation: 'bp-panel-in 0.22s ease-out',
  } as React.CSSProperties,

  sidePanelHeader: {
    padding: '16px 20px 12px',
    borderBottom: '1px solid #edebea',
    background: '#fff',
  } as React.CSSProperties,

  sidePanelBody: {
    flex: 1,
    overflowY: 'auto' as const,
    background: '#fff',
  } as React.CSSProperties,

  sidePanelFooter: {
    padding: '12px 16px',
    borderTop: '1px solid #edebea',
    background: '#fafaf8',
    position: 'sticky' as const,
    bottom: 0,
    // 2026-04-23 Reader A: subtle shadow for footer depth
    boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
  } as React.CSSProperties,

  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '6px 0',
    borderBottom: '1px solid #f5f3f2',
    fontSize: '0.75rem',
  } as React.CSSProperties,

  metaLabel: {
    color: '#8a8580',
    fontSize: '0.6875rem',
    flexShrink: 0,
    marginRight: '12px',
  } as React.CSSProperties,

  metaValue: {
    color: '#252525',
    fontSize: '0.75rem',
    fontWeight: 500,
    textAlign: 'right' as const,
    wordBreak: 'break-all' as const,
  } as React.CSSProperties,

  detailTh: {
    padding: '6px 10px',
    fontSize: '0.625rem',
    fontWeight: 600,
    color: '#8a8580',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    borderBottom: '1px solid #dbd9d7',
    background: '#f7f5f5',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  detailTd: {
    padding: '8px 10px',
    fontSize: '0.75rem',
    borderBottom: '1px solid #edebea',
    verticalAlign: 'top' as const,
  } as React.CSSProperties,

  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  chipRow: {
    // 2026-04-23: Vibes SegmentControlButton は sibling が隣接 (gap=0) することで
    // border-right-width:0 + first/last-child radius が正しく働く設計。
    // gap を入れると真ん中のボタンの右端が見えなくなる (user 報告)。
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  skeletonRow: {
    padding: '14px 12px',
    borderBottom: '1px solid #edebea',
  } as React.CSSProperties,

  skeletonBar: (w: string) => ({
    display: 'inline-block',
    height: '10px',
    background: 'linear-gradient(90deg, #f0efed 0%, #e5e4e2 50%, #f0efed 100%)',
    backgroundSize: '200% 100%',
    animation: 'bp-skel 1.2s ease-in-out infinite',
    borderRadius: '4px',
    width: w,
  }),
};

// ---------------------------------------------------------------------------
// Hash parsing
// ---------------------------------------------------------------------------

interface BankHash {
  month: string | null;
  walletableId: number | null;
  wtId: number | null;
}

function parseBankHash(): BankHash {
  if (typeof window === 'undefined') return { month: null, walletableId: null, wtId: null };
  const h = (window.location.hash || '').toLowerCase();
  const m = h.match(/^#?bank(?:\/([\d-]*))?(?:\/(\d+))?(?:\/(\d+))?$/);
  if (!m) return { month: null, walletableId: null, wtId: null };
  const monthRaw = m[1] || '';
  const month = /^\d{4}-\d{2}$/.test(monthRaw) ? monthRaw : null;
  return {
    month,
    walletableId: m[2] ? Number(m[2]) : null,
    wtId: m[3] ? Number(m[3]) : null,
  };
}

function buildBankHash(month: string | null, walletableId: number | null, wtId: number | null): string {
  let h = '#bank';
  if (month) h += `/${month}`;
  else if (walletableId || wtId) h += '/';
  if (walletableId) h += `/${walletableId}`;
  else if (wtId) h += '/';
  if (wtId) h += `/${wtId}`;
  return h;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n: number | null | undefined) => (n == null ? '—' : (n < 0 ? '-' : '') + '¥' + Math.abs(n).toLocaleString('ja-JP'));

const fmtSigned = (amount: number | null | undefined, entrySide: 'income' | 'expense') => {
  if (amount == null) return '—';
  const sign = entrySide === 'income' ? '+' : '-';
  return `${sign}¥${Math.abs(amount).toLocaleString('ja-JP')}`;
};

// 2026-04-23 Reader A: income / expense rendered in separate columns
// Only the side matching entry_side shows a value; other side shows muted em-dash.
const fmtIncomeCell = (r: BankWalletTxnRow): React.ReactNode => {
  if (r.entry_side !== 'income' || r.amount == null) {
    return <span style={{ color: '#c4c0bc' }}>—</span>;
  }
  return <span style={{ color: '#338650' }}>{`¥${Math.abs(r.amount).toLocaleString('ja-JP')}`}</span>;
};

const fmtExpenseCell = (r: BankWalletTxnRow): React.ReactNode => {
  if (r.entry_side !== 'expense' || r.amount == null) {
    return <span style={{ color: '#c4c0bc' }}>—</span>;
  }
  return <span style={{ color: '#c33939' }}>{`¥${Math.abs(r.amount).toLocaleString('ja-JP')}`}</span>;
};

const todayYyyyMm = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthToRange = (m: string | null): { start: string; end: string } | null => {
  if (!m) return null;
  const [y, mm] = m.split('-').map(Number);
  const last = new Date(Date.UTC(y, mm, 0)).getUTCDate();
  return {
    start: `${y}-${String(mm).padStart(2, '0')}-01`,
    end: `${y}-${String(mm).padStart(2, '0')}-${String(last).padStart(2, '0')}`,
  };
};

const WEEKDAY_JP = ['日', '月', '火', '水', '木', '金', '土'];
const formatDateMD = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [yy, mm, dd] = iso.split('-').map(Number);
  const d = new Date(Date.UTC(yy, mm - 1, dd));
  return `${String(mm).padStart(2, '0')}/${String(dd).padStart(2, '0')} (${WEEKDAY_JP[d.getUTCDay()]})`;
};

// 2026-04-23 Reader A: relative date label (今日/昨日/N日前/N週間前/N ヶ月前) + ISO tooltip
interface DateDisplay { primary: string; secondary: string; tooltip: string; }
const formatDateMD_v2 = (iso: string | null | undefined): DateDisplay => {
  if (!iso) return { primary: '—', secondary: '', tooltip: '' };
  const [yy, mm, dd] = iso.split('-').map(Number);
  const d = new Date(Date.UTC(yy, mm - 1, dd));
  const today = new Date();
  const todayUtc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const diffDays = Math.round((todayUtc.getTime() - d.getTime()) / 86400000);
  const weekday = WEEKDAY_JP[d.getUTCDay()];
  const primary = `${String(mm).padStart(2, '0')}/${String(dd).padStart(2, '0')} (${weekday})`;
  let secondary = '';
  if (diffDays === 0) secondary = '今日';
  else if (diffDays === 1) secondary = '昨日';
  else if (diffDays > 0 && diffDays < 7) secondary = `${diffDays}日前`;
  else if (diffDays >= 7 && diffDays < 30) secondary = `${Math.floor(diffDays/7)}週間前`;
  else if (diffDays >= 30 && diffDays < 365) secondary = `${Math.floor(diffDays/30)}ヶ月前`;
  else if (diffDays >= 365) secondary = `${Math.floor(diffDays/365)}年前`;
  else if (diffDays < 0) secondary = `${-diffDays}日後`;
  return { primary, secondary, tooltip: iso };
};

const walletableName = (id: number | null): string => {
  if (!id) return '全口座';
  const w = WALLETABLES.find((x) => x.walletable_id === id);
  return w ? w.name : `wallet_id:${id}`;
};

// Classify a row into one of 5 pipeline stages for the 5-dot indicator.
// 0: pending / 1: analyzing / 2: suggested / 3: approved / 4: registered
// Plus 'error' / 'skipped' outliers.
type PipelineStage = 0 | 1 | 2 | 3 | 4;
interface PipelineState {
  stage: PipelineStage;
  error: boolean;
  skipped: boolean;
  label: string;
  colorClass: 'neutral' | 'analysis' | 'review' | 'approved' | 'done' | 'error' | 'skipped';
}

const classifyPipeline = (r: BankWalletTxnRow): PipelineState => {
  const s = r.processing?.processing_status;
  // 行動レイヤの語は statusVocab.ts (bankPipelineToUnified) と同一。処理失敗の理由は行内の事実表示に委ねる
  if (s === 'error') return { stage: 1, error: true, skipped: false, label: '対応待ち(処理失敗)', colorClass: 'error' };
  if (s === 'skipped') return { stage: 0, error: false, skipped: true, label: 'スキップ', colorClass: 'skipped' };
  if (s === 'registered' || r.status_raw === 2) {
    return { stage: 4, error: false, skipped: false, label: '登録完了', colorClass: 'done' };
  }
  if (s === 'approved') return { stage: 3, error: false, skipped: false, label: '承認済', colorClass: 'approved' };
  // schema-v33: Stage 1/2 機械的判定 (= AI skip)
  if (s === 'mechanical_suggest_stage1') {
    return { stage: 2, error: false, skipped: false, label: '機械提案 (D1)', colorClass: 'review' };
  }
  if (s === 'mechanical_reconcile_stage2') {
    return { stage: 2, error: false, skipped: false, label: '消込のみ (freee)', colorClass: 'review' };
  }
  if (s === 'suggested' || s === 'needs_review') {
    // AI提案済みで人の確認待ち = 承認系 (旧「要確認」— 修正ではない)
    return { stage: 2, error: false, skipped: false, label: '対応待ち(承認)', colorClass: 'review' };
  }
  if (s === 'queued' || s === 'processing') {
    return { stage: 1, error: false, skipped: false, label: '分析中', colorClass: 'analysis' };
  }
  return { stage: 0, error: false, skipped: false, label: '未処理', colorClass: 'neutral' };
};

// 2026-04-23: Vibes StatusIcon 準拠。PipelineState → StatusType 変換。
type VibesStatusType = 'done' | 'success' | 'progress' | 'required' | 'disabled' | 'emphasis' | 'warning' | 'error';
const pipelineToStatusType = (p: PipelineState): VibesStatusType => {
  if (p.error) return 'error';
  if (p.skipped) return 'disabled';
  switch (p.colorClass) {
    case 'done': return 'done';
    case 'approved': return 'emphasis';
    case 'review': return 'warning';
    case 'analysis': return 'progress';
    default: return 'required';
  }
};

// ---------------------------------------------------------------------------
// 2026-04-23: FiveDotProgress 廃止 → Vibes StatusIcon (pipelineToStatusType) で統一
// ---------------------------------------------------------------------------
// Sort state
// ---------------------------------------------------------------------------

type SortKey =
  | 'date' | 'amount' | 'balance' | 'updated_at' | 'fetched_at'
  | 'status' | 'account'
  // 2026-04-23 Reader A: income / expense split
  | 'income' | 'expense';
type SortDir = 'asc' | 'desc';

function buildOrderParam(key: SortKey, dir: SortDir): BankWalletTxnsListOrder {
  return `${key}_${dir}` as BankWalletTxnsListOrder;
}

// 2026-04-23 Reader A + B: 取引先 (partner) display with 5-way resolution
type PartnerDisplay =
  | { kind: 'confirmed'; name: string; freeeDealId?: number | null }
  | { kind: 'suggested'; name: string; confidence: number; sourceCode: 'A' | 'B' | 'C'; sourceLabel: string; evidenceNote: string }
  | { kind: 'auto_rule'; note: string }
  | { kind: 'unknown'; description: string };

const SOURCE_LABEL: Record<'A' | 'B' | 'C', string> = {
  A: 'registry',
  C: '自動登録ルール',
  B: '履歴',
};

function resolvePartner(r: BankWalletTxnRow): PartnerDisplay {
  const proc = r.processing;
  // 1) confirmed — processing row + partner_name (canonical source)
  if (proc?.partner_name) {
    return { kind: 'confirmed', name: proc.partner_name, freeeDealId: proc.freee_deal_id };
  }
  // 1b) resolved_partner補完 (worker が ppi_wtp JOIN で返すことがある)
  const resolved = r.resolved_partner;
  if (resolved && resolved.partner_name) {
    return { kind: 'confirmed', name: resolved.partner_name, freeeDealId: proc?.freee_deal_id || null };
  }
  // 2) suggested_partner (A / C / B stages)
  const sug = r.suggested_partner;
  if (sug && sug.name) {
    const src = sug.source;
    let evNote = '';
    const ev = sug.evidence;
    if (src === 'A' && ev.match_token) evNote = `match: ${ev.match_token}`;
    else if (src === 'C') {
      const ruleId = ev.freee_rule_id || ev.rule_id;
      evNote = ev.match_token ? `pattern: ${ev.match_token}${ruleId ? ` / rule ${ruleId}` : ''}` : (ruleId ? `rule ${ruleId}` : '');
    } else if (src === 'B') {
      evNote = ev.observed_hits != null ? `履歴 ${ev.observed_hits} 件` : '';
    }
    return {
      kind: 'suggested',
      name: sug.name,
      confidence: sug.confidence,
      sourceCode: src,
      sourceLabel: SOURCE_LABEL[src] || src,
      evidenceNote: evNote,
    };
  }
  // 3) freee auto rule hint (detectable)
  if (r.freee_auto_hint?.detectable) {
    return { kind: 'auto_rule', note: r.freee_auto_hint.note || '自動で経理' };
  }
  // 4) completely unknown
  return { kind: 'unknown', description: r.description || '' };
}

// ---------------------------------------------------------------------------
// 2026-07-13: 消込副状態 (reconcile_state) — worker list API が返す予定のフィールド。
// 応答に無い場合 (processing にも top-level にも無い) は null → 通常表示に fallback
// する防御的実装。processing_status='error' の行のみ副状態を区別表示する。
// ---------------------------------------------------------------------------

const reconcileBadgeFor = (
  r: Pick<BankWalletTxnRow, 'processing' | 'reconcile_state'>,
): UnifiedBadge | null => {
  // 2026-09-14: list API は reconcile_state 未返却のため error_message + freee_deal_id から推定
  //   (worker がフィールドを返せば常にそちらが正)。自動リトライが 70 分進まない行は要確認に格上げ。
  const rs = effectiveReconcileState(r.processing, r.reconcile_state);
  if (rs === 'reconcile_pending' && isReconcilePendingStale(r.processing?.last_updated)) {
    return { status: 'action_required', sub: 'judge', label: RECONCILE_STALE_LABEL, ...UNIFIED_COLOR.action_required };
  }
  return bankReconcileStateToUnified(rs);
};

// statusVocab 4色に従う小型チップ (bg/fg/bd は UnifiedBadge が持つ正本値)
const UnifiedChip: React.FC<{ b: UnifiedBadge }> = ({ b }) => (
  <span
    title={unifiedHint(b)}
    style={{
      display: 'inline-block',
      fontSize: '0.625rem',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: '10px',
      lineHeight: '1.4',
      background: b.bg,
      color: b.fg,
      border: `1px solid ${b.bd}`,
      whiteSpace: 'nowrap',
    }}
  >
    {b.label}
  </span>
);

// ---------------------------------------------------------------------------
// 2026-07-14: 部門接地・不明明示・部門学習ループ (確定設計 v1 — app 側 A1/A2)
//   - 提案仕訳 details[] の部門未確定判定。設計 §3 の構造化ステータス
//     _section_status ('resolved'|'unknown'|'not_applicable') + invoice 側の既存慣行
//     'unknown_needs_user_action' を許容し、マーカー無しの section_id=null も未確定扱い。
//   - 原則: 不明は不明のまま明示 (推測での自動補填はしない)。確定値は user 指定のみ。
// ---------------------------------------------------------------------------

const isDetailSectionUnknown = (dd: any): boolean => {
  const st = dd?._section_status;
  if (st === 'resolved' || st === 'not_applicable') return false;
  if (typeof st === 'string' && st.indexOf('unknown') === 0) return true;
  return dd?.section_id == null;
};

// user の部門指定 (edits: detail index → section_id) を details に適用した新配列を返す。
// 実変更が 1 行も無ければ null (= modifications 送信不要 → approved_as_is のまま)。
// 適用行は _section_status='resolved' 化し、stale になる名称/理由 field を除去する
// (登録は section_id が正、名称は worker / UI が sections マスタで再解決する)。
const buildSectionModifiedDetails = (dets: any[], edits?: Map<number, number>): any[] | null => {
  if (!edits || edits.size === 0) return null;
  let changed = false;
  const out = dets.map((dd: any, i: number) => {
    const sid = edits.get(i);
    if (sid == null || (dd?.section_id != null && Number(dd.section_id) === sid)) return dd;
    changed = true;
    const nd: any = { ...dd, section_id: sid, _section_status: 'resolved' };
    delete nd.section_unknown_reason;
    delete nd.sectionName;
    delete nd.section_name;
    return nd;
  });
  return changed ? out : null;
};

// user 指定を重ねた後も部門未確定のまま残る detail index 一覧
const unresolvedSectionIndexes = (dets: any[], edits?: Map<number, number>): number[] => {
  const out: number[] = [];
  dets.forEach((dd: any, i: number) => {
    if (isDetailSectionUnknown(dd) && !(edits && edits.has(i))) out.push(i);
  });
  return out;
};

// 2026-09-24 W13 追補: verdict='disagree' の行のうち「人間が部門を確定すれば承認できる」行の
//   判定 (worker 側 bankSectionOnlyOverrideEligible と同一述語のクライアント写し)。
//   サーバが最終権威 (fail-closed) で、これは「越えられない行に ack チェックを出さない」ための
//   前段表示制御。2026-09-24 wt 2422343797 incident: 部門は確定済みで AI の疑いが税区分だった行に
//   ack チェックを出してしまい、チェックしても 409 になる袋小路を作っていた。
type DisagreeUnlock = { eligible: boolean; reason: 'ok' | 'core_incomplete' | 'no_section_gap' };
const disagreeUnlockState = (pdj: any, dets: any[]): DisagreeUnlock => {
  const coreFilled = (d: any) => d && typeof d === 'object'
    && Number(d.account_item_id) > 0
    && d.tax_code != null && d.tax_code !== '' && !Number.isNaN(Number(d.tax_code))
    && Number(d.amount) > 0;
  if (!(Number(pdj?.partner_id) > 0) || !dets.length || !dets.every(coreFilled)) {
    return { eligible: false, reason: 'core_incomplete' };
  }
  const hasGap = dets.some((d: any) => !(Number(d?.section_id) > 0 || d?._section_status === 'not_applicable'));
  return hasGap ? { eligible: true, reason: 'ok' } : { eligible: false, reason: 'no_section_gap' };
};

// details + user 指定から実効部門集合を得る (「この取引先は常にこの部門」学習可否の判定用。
// 分散 vendor = 複数部門なら学習不可 — vendor_default_section は 1 業者 1 部門)。
const effectiveSectionIds = (dets: any[], edits?: Map<number, number>): number[] => {
  const set = new Set<number>();
  dets.forEach((dd: any, i: number) => {
    const edited = edits ? edits.get(i) : undefined;
    const sid = edited != null ? edited : (dd?.section_id != null ? Number(dd.section_id) : null);
    if (sid != null) set.add(sid);
  });
  return Array.from(set);
};

// ---------------------------------------------------------------------------
// 2026-07-14: 根拠 (evidence_sources) 構造化表示 — worker 新契約
//   GET /api/bank-wallet-txns/:id → processing.evidence_sources (sanitize 済 array | null)。
//   - array   = 構造化根拠を主役に描画。判定理由の生テキストは <details> 折りたたみに格下げ。
//   - null    = 旧形式 (根拠構造化前) → reconcile_judge_reason をそのまま表示 (防御的 fallback)。
//   - source_type='none' は「探したが見つからなかったもの」として別掲し、修正指示 → 学習の
//     導線を案内する (確認済みで該当なし、の明示 — 黙って省略しない)。
// ---------------------------------------------------------------------------

const EVIDENCE_USED_FOR_JP: Record<string, string> = {
  partner: '取引先',
  account: '勘定科目',
  section: '部門',
  amount: '金額',
  tax: '税区分',
  item: '品目',
  split: '按分',
};

interface EvidenceTypeMeta { label: string; bg: string; fg: string; bd: string; arica: boolean }

// ARICA 系 2 種は「ARICA」を含むラベル + 専用ティール色 + DB アイコンで一目で判別できるようにする
const EVIDENCE_TYPE_META: Record<string, EvidenceTypeMeta> = {
  arica_purchase:  { label: 'ARICA購買',    bg: '#dff4f3', fg: '#0b7a75', bd: '#79cfc9', arica: true },
  arica_statement: { label: 'ARICA明細',    bg: '#dff4f3', fg: '#0b7a75', bd: '#79cfc9', arica: true },
  past_deal:       { label: '過去仕訳',     bg: '#edf8f4', fg: '#267a62', bd: '#9fdbc7', arica: false },
  learned_pattern: { label: '学習パターン', bg: '#f0edfd', fg: '#5b3fc7', bd: '#c7bcf0', arica: false },
  freee_master:    { label: 'freeeマスタ',  bg: '#f0f6ff', fg: '#33699e', bd: '#b9d2ed', arica: false },
  none:            { label: '該当なし',     bg: '#fdf3e3', fg: '#a46a12', bd: '#e3c68a', arica: false },
};

// 未知の source_type が来ても壊れない (グレーでそのまま表示)
const evidenceTypeMeta = (st: string): EvidenceTypeMeta =>
  EVIDENCE_TYPE_META[st] || { label: st, bg: '#f1f4f7', fg: '#5e7083', bd: '#ccd6df', arica: false };

// server sanitize 済みが前提だが、UI 側でも最低限の防御 (object + ja_summary 必須) を行う。
// 有効 0 件は null (= 旧形式扱い)。
const normalizeEvidenceSources = (v: unknown): BankEvidenceSource[] | null => {
  if (!Array.isArray(v)) return null;
  const out = v.filter((e: any): e is BankEvidenceSource =>
    !!e && typeof e === 'object'
    && typeof e.source_type === 'string'
    && typeof e.ja_summary === 'string' && e.ja_summary.trim() !== '');
  return out.length > 0 ? out : null;
};

// detail 応答から根拠を取り出す。processing.evidence_sources が正、
// audit_summary.evidence_sources (D1 正本 mirror) は防御的 fallback。
const bankEvidenceOf = (proc: BankProcessingInfo | null | undefined): BankEvidenceSource[] | null => {
  if (!proc) return null;
  return normalizeEvidenceSources(proc.evidence_sources)
    || normalizeEvidenceSources(proc.audit_summary?.evidence_sources);
};

const EvidenceBadge: React.FC<{ sourceType: string }> = ({ sourceType }) => {
  const m = evidenceTypeMeta(sourceType);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        fontSize: '0.625rem',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '10px',
        lineHeight: '1.5',
        background: m.bg,
        color: m.fg,
        border: `1px solid ${m.bd}`,
        whiteSpace: 'nowrap',
      }}
    >
      {m.arica && <MdStorage size={11} aria-hidden />}
      {m.label}
    </span>
  );
};

const EvidenceUsedForChip: React.FC<{ usedFor: string }> = ({ usedFor }) => (
  <span
    style={{
      display: 'inline-block',
      fontSize: '0.5625rem',
      fontWeight: 600,
      padding: '1px 6px',
      borderRadius: '8px',
      lineHeight: '1.5',
      background: '#f1f0ee',
      color: '#6b6560',
      border: '1px solid #e0deda',
      whiteSpace: 'nowrap',
    }}
  >
    {EVIDENCE_USED_FOR_JP[usedFor] || usedFor}
  </span>
);

interface EvidenceSourcesSectionProps {
  sources: BankEvidenceSource[] | null;
  judgeReason: string | null | undefined;
}

const EvidenceSourcesSection: React.FC<EvidenceSourcesSectionProps> = ({ sources, judgeReason }) => {
  const hasStructured = !!sources && sources.length > 0;
  const reason = (judgeReason != null && String(judgeReason).trim() !== '') ? String(judgeReason) : null;
  if (!hasStructured && !reason) return null;

  const found = hasStructured ? sources!.filter((e) => e.source_type !== 'none') : [];
  const notFound = hasStructured ? sources!.filter((e) => e.source_type === 'none') : [];

  return (
    <div style={{ marginBottom: '12px' }}>
      <SubSectionTitle headlineLevel={4}>根拠</SubSectionTitle>
      {hasStructured ? (
        <div style={{ marginTop: '6px' }}>
          {found.map((e, i) => {
            const m = evidenceTypeMeta(e.source_type);
            const usedFor = Array.isArray(e.used_for) ? e.used_for : [];
            const metaBits: string[] = [];
            if (e.family) metaBits.push(`family: ${e.family}`);
            if (e.ref) metaBits.push(`ref: ${e.ref}`);
            if (e.deal_id != null) metaBits.push(`deal #${e.deal_id}`);
            if (e.pattern_key) metaBits.push(`pattern: ${e.pattern_key}`);
            return (
              <div
                key={i}
                style={{
                  padding: '8px 10px',
                  background: m.arica ? '#f4fbfa' : '#fafaf8',
                  border: '1px solid #edebea',
                  borderLeft: `3px solid ${m.bd}`,
                  borderRadius: '4px',
                  marginBottom: '6px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                  <EvidenceBadge sourceType={e.source_type} />
                  {usedFor.map((u, ui) => <EvidenceUsedForChip key={ui} usedFor={u} />)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#252525', fontWeight: 500, lineHeight: 1.5 }}>
                  {e.ja_summary}
                </div>
                {e.matched && (
                  <div style={{ fontSize: '0.6875rem', color: '#6b6560', marginTop: '2px', wordBreak: 'break-all' }}>
                    一致: {e.matched}
                  </div>
                )}
                {metaBits.length > 0 && (
                  <div style={{ fontSize: '0.625rem', color: '#8a8580', marginTop: '2px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {metaBits.join(' / ')}
                  </div>
                )}
              </div>
            );
          })}

          {/* 探したが見つからなかったもの (source_type='none') — 不明の明示 + 学習導線 */}
          {notFound.length > 0 && (
            <div
              style={{
                padding: '10px 12px',
                background: UNIFIED_COLOR.action_required.bg,
                border: `1px solid ${UNIFIED_COLOR.action_required.bd}`,
                borderRadius: '6px',
                marginBottom: '6px',
              }}
            >
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: UNIFIED_COLOR.action_required.fg }}>
                探したが見つからなかったもの ({notFound.length}件)
              </div>
              {notFound.map((e, i) => (
                <div key={i} style={{ marginTop: '6px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#252525', lineHeight: 1.5 }}>{e.ja_summary}</div>
                  {(e.family || e.ref) && (
                    <div style={{ fontSize: '0.625rem', color: '#8a8580', marginTop: '1px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {[e.family ? `探索先: ${e.family}` : null, e.ref ? `ref: ${e.ref}` : null].filter(Boolean).join(' / ')}
                    </div>
                  )}
                </div>
              ))}
              <div
                style={{
                  marginTop: '8px',
                  paddingTop: '6px',
                  borderTop: `1px dashed ${UNIFIED_COLOR.action_required.bd}`,
                  fontSize: '0.6875rem',
                  color: '#6b6560',
                }}
              >
                上記は確認した上で該当がなかったものです。正しい内容がわかる場合は、修正指示を送ると学習し、次回から自動処理されます。
              </div>
            </div>
          )}

          {/* 構造化根拠が主役 — 判定理由の生テキストは折りたたみに格下げ */}
          {reason && (
            <details style={{ marginTop: '4px' }}>
              <summary style={{ fontSize: '0.6875rem', color: '#8a8580', cursor: 'pointer', userSelect: 'none' }}>
                判定理由の生テキストを表示
              </summary>
              <pre
                style={{
                  marginTop: '4px',
                  padding: '8px 10px',
                  background: '#f7f5f5',
                  borderRadius: '6px',
                  fontSize: '0.6875rem',
                  color: '#6b6560',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {reason}
              </pre>
            </details>
          )}
        </div>
      ) : (
        // 旧形式 (evidence_sources 無し) — 従来どおり判定理由をそのまま表示 (防御的 fallback)
        <div style={{ marginTop: '6px' }}>
          <div style={{ fontSize: '0.625rem', color: '#8a8580', marginBottom: '4px' }}>
            旧形式 (根拠構造化前) — 判定理由をそのまま表示しています
          </div>
          <div
            style={{
              padding: '8px 10px',
              background: '#fafaf8',
              border: '1px solid #edebea',
              borderRadius: '4px',
              fontSize: '0.75rem',
              color: '#252525',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.5,
            }}
          >
            {reason}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

interface Toast {
  id: number;
  kind: 'success' | 'error' | 'notice' | 'info';
  text: string;
}

// ---------------------------------------------------------------------------
// Confirmation dialog state (Vibes MessageDialogConfirm / TaskDialog replacement
// for window.confirm / window.prompt)
// ---------------------------------------------------------------------------

interface ConfirmDialogState {
  open: boolean;
  title: string;
  body: React.ReactNode;
  primaryLabel: string;
  danger?: boolean;
  onConfirm: () => void;
}

interface PromptDialogState {
  open: boolean;
  title: string;
  label: string;
  placeholder?: string;
  primaryLabel: string;
  danger?: boolean;
  onSubmit: (value: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BankProcessingPage: React.FC = () => {
  const initialHash = useMemo(() => parseBankHash(), []);

  const [selectedMonth, setSelectedMonth] = useState<string>(initialHash.month || todayYyyyMm());
  const [isAllPeriods, setIsAllPeriods] = useState<boolean>(!initialHash.month && (initialHash.walletableId !== null || initialHash.wtId !== null));
  const [selectedWalletable, setSelectedWalletable] = useState<number | null>(initialHash.walletableId);
  const [selectedWalletableType, setSelectedWalletableType] = useState<string>('all');
  const [chipKey, setChipKey] = useState<ChipKey>('all');
  const [flowFilter, setFlowFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [entrySideFilter, setEntrySideFilter] = useState('all');

  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [rows, setRows] = useState<BankWalletTxnRow[]>([]);
  // 2026-07-13: server 応答に無いキー (analyzing 等) があっても壊れないよう Partial で保持し、
  // 表示時に local 集計とマージする (undefined 件数表示の防止)。
  const [serverChipCounts, setServerChipCounts] = useState<Partial<Record<ChipKey, number>> | null>(null);
  const [summary, setSummary] = useState<BankSummary | null>(null);
  const [learningMetrics, setLearningMetrics] = useState<LearningMetrics | null>(null);
  const [total, setTotal] = useState(0);
  // 2026-07-11 監査 fix (⑧型 pagination): 「もっと見る」追加読込用 state
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const listGenRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // freee マスタ map (proposed_deal_json の 生 ID 解決用)。読取専用・1 回取得 cache。
  const [accountItemsMap, setAccountItemsMap] = useState<Map<number, string>>(new Map());
  const [sectionsMap, setSectionsMap] = useState<Map<number, string>>(new Map());
  const [taxCodesMap, setTaxCodesMap] = useState<Map<number, string>>(new Map());
  const [itemsMap, setItemsMap] = useState<Map<number, string>>(new Map());
  const [tagsMap, setTagsMap] = useState<Map<number, string>>(new Map());
  const [partnersMap, setPartnersMap] = useState<Map<number, string>>(new Map());

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [activePanelId, setActivePanelId] = useState<number | null>(initialHash.wtId || null);
  const [sidePanelTab, setSidePanelTab] = useState(0);
  const [focusedRowId, setFocusedRowId] = useState<number | null>(null);

  const [panelDetail, setPanelDetail] = useState<BankWalletTxnDetail | null>(null);
  const [panelFreeeDeal, setPanelFreeeDeal] = useState<BankFreeeDealView | null>(null);
  const [panelFreeeDealError, setPanelFreeeDealError] = useState<string | null>(null);
  const [panelAudit, setPanelAudit] = useState<BankAuditEvent[]>([]);
  const [panelLearning, setPanelLearning] = useState<BankLearningEvidence | null>(null);

  const [workingIds, setWorkingIds] = useState<Set<number>>(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  // 2026-07-11 監査 fix (①型 エラー握り潰し): 一括承認/却下の per-item 失敗を
  // 6秒 toast の件数だけで流さず、どの行がなぜ失敗したかを永続 banner で表示する。
  interface BulkFailure { id: number; error: string }
  interface BulkReport {
    kind: '一括承認→登録' | '一括却下';
    ok: number;
    /** freee 登録済・消込だけ自動リトライ待ち (失敗には数えない = 再試行対象外) */
    pending?: number;
    failures: BulkFailure[];
    finishedAt: string;
    /** 一括却下の「失敗分のみ再試行」用に入力済み却下理由を保持 */
    reason?: string;
  }
  const [bulkReport, setBulkReport] = useState<BulkReport | null>(null);
  // 2026-07-13: 一括サジェストの client 側自動チャンク化 (worker の ≤50件/回 400 ガード対応)。
  // 進捗 (チャンク m/M) を永続 banner で表示し、失敗チャンクのみ再試行できる。
  interface EnqueueChunkFailure { ids: number[]; error: string }
  interface EnqueueProgress {
    active: boolean;
    doneChunks: number;
    totalChunks: number;
    queued: number;      // queued 済み件数 (応答に queued キーが無い場合は results から集計)
    requested: number;   // 依頼した総件数
    failures: EnqueueChunkFailure[];
    finishedAt: string | null;
    // 2026-07-14 (incident 2026-07-13 18:36): ensureSession 失敗等でチャンク未送信のまま中断した
    // 場合の理由。旧実装は外側 catch でも「一括サジェスト完了: 0/N件」と成功様式で表示され、
    // audit blocking (session/open 409) による全滅が完了と誤読された。error 設定時は失敗表示。
    error: string | null;
  }
  const [enqueueProgress, setEnqueueProgress] = useState<EnqueueProgress | null>(null);
  // 2026-07-11 監査 fix (③型 楽観表示排除): register-after-approve の per-row 実結果。
  // 失敗行は理由を行内に赤で永続表示 (成功 or 再試行開始で解除)。「登録キュー中…」のような
  // 実在しない state の表示は禁止 — 表示は常に実 state (workingIds / registerErrors /
  // processing_status) のみから導出する。
  const [registerErrors, setRegisterErrors] = useState<Map<number, string>>(new Map());
  const setRegisterError = useCallback((id: number, msg: string | null) => {
    setRegisterErrors((prev) => {
      if (msg == null && !prev.has(id)) return prev;
      const next = new Map(prev);
      if (msg == null) next.delete(id);
      else next.set(id, msg);
      return next;
    });
  }, []);
  const [reconcileRunning, setReconcileRunning] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // 2026-07-14 部門接地 (確定設計 v1 A1/A2):
  //   sectionEdits        = panel 内 部門編集 (wtId → detail index → section_id)。
  //                         承認時に modifications.details として送信され、worker が
  //                         approved_with_modification として学習記録に残す (修正承認=学習の正イベント)。
  //   sectionUnknownAcks  = 「部門は不明のまま登録する」明示 ack (wtId 単位)。
  //                         ack なしで未確定行が残る承認は UI が拒否する (黙って部門なし登録しない)。
  //   learnVendorChecks   = 「この取引先は常にこの部門」学習 opt-in (wtId 単位)。
  //                         承認成功後に POST /api/learn kind=vendor_default_section (user 明示のみ)。
  // -------------------------------------------------------------------------
  //   disagreeAcks        = 2026-09-19 W13: AI が verdict='disagree' (合議不成立) とした行を、
  //                         指摘内容 (missing_evidence) を読んだうえで承認する明示 ack (wtId 単位)。
  //                         ack した承認のみ override_reconcile_disagree を送る。worker 側は
  //                         「部門だけ未確定」の行に限って受理し、それ以外は 409 のまま。
  //                         一括承認はこの ack を送らない (= 一括では disagree 行を越えられない)。
  const [sectionEdits, setSectionEdits] = useState<Map<number, Map<number, number>>>(new Map());
  const [sectionUnknownAcks, setSectionUnknownAcks] = useState<Set<number>>(new Set());
  const [learnVendorChecks, setLearnVendorChecks] = useState<Set<number>>(new Set());
  const [disagreeAcks, setDisagreeAcks] = useState<Set<number>>(new Set());

  const setSectionEdit = useCallback((wt: number, detailIndex: number, secId: number | null) => {
    setSectionEdits((prev) => {
      const next = new Map(prev);
      const m = new Map(next.get(wt) || []);
      if (secId == null) m.delete(detailIndex);
      else m.set(detailIndex, secId);
      if (m.size === 0) next.delete(wt);
      else next.set(wt, m);
      return next;
    });
  }, []);

  const toggleSectionUnknownAck = useCallback((wt: number) => {
    setSectionUnknownAcks((prev) => {
      const next = new Set(prev);
      if (next.has(wt)) next.delete(wt);
      else next.add(wt);
      return next;
    });
  }, []);

  const toggleDisagreeAck = useCallback((wt: number) => {
    setDisagreeAcks((prev) => {
      const next = new Set(prev);
      if (next.has(wt)) next.delete(wt);
      else next.add(wt);
      return next;
    });
  }, []);

  const toggleLearnVendorCheck = useCallback((wt: number) => {
    setLearnVendorChecks((prev) => {
      const next = new Set(prev);
      if (next.has(wt)) next.delete(wt);
      else next.add(wt);
      return next;
    });
  }, []);

  // 承認成功後の後片付け (編集・ack・学習チェックは 1 承認で消費)
  const clearSectionUiState = useCallback((wt: number) => {
    setSectionEdits((prev) => { if (!prev.has(wt)) return prev; const n = new Map(prev); n.delete(wt); return n; });
    setSectionUnknownAcks((prev) => { if (!prev.has(wt)) return prev; const n = new Set(prev); n.delete(wt); return n; });
    setLearnVendorChecks((prev) => { if (!prev.has(wt)) return prev; const n = new Set(prev); n.delete(wt); return n; });
    setDisagreeAcks((prev) => { if (!prev.has(wt)) return prev; const n = new Set(prev); n.delete(wt); return n; });
  }, []);

  // 「最新の状態に更新」 専用 progress state (= 2026-05-08 修正、 並列化 + persistent banner)
  type RefreshState = {
    active: boolean;
    total: number;
    done: number;
    fetched: number;
    drifts: number;
    errors: number;
    startedAt: string | null;
    finishedAt: string | null;
    errorMsgs: string[];
  };
  const [refreshing, setRefreshing] = useState<RefreshState | null>(null);
  const refreshAutoDismissRef = useRef<number | null>(null);

  const [toasts, setToasts] = useState<Toast[]>([]);

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false, title: '', body: null, primaryLabel: 'OK', onConfirm: () => {},
  });
  const [promptDialog, setPromptDialog] = useState<PromptDialogState>({
    open: false, title: '', label: '', primaryLabel: 'OK', onSubmit: () => {},
  });
  const [promptValue, setPromptValue] = useState('');

  const listElRef = useRef<HTMLTableElement>(null);

  // ---------------------------------------------------------------------------
  // Realtime: /api/timeline polling で freee 登録完了を即時検知 (2026-04-27)
  // worker の markD1ReconcileSuccess + mark-registered handler が emit する
  // event_type='freee_registered' を購読し、 該当 wallet_txn_id の行を即更新。
  // ---------------------------------------------------------------------------
  const polling = useEventPolling({ enabled: true, intervalMs: 4000 });
  const handledEventIds = useRef<Set<string>>(new Set());

  // Toast helper
  const pushToast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 6000);
  }, []);

  // Dialog helpers
  const askConfirm = useCallback((
    params: Omit<ConfirmDialogState, 'open'>
  ) => {
    setConfirmDialog({ ...params, open: true });
  }, []);

  const askPrompt = useCallback((
    params: Omit<PromptDialogState, 'open'>
  ) => {
    setPromptValue('');
    setPromptDialog({ ...params, open: true });
  }, []);

  // Session
  // 2026-08-15 fix (wt 2383168280 「承認は通ったが登録されない」incident):
  //   旧実装は一度取った session_id を state に持ち続け、失効を検査しなかった。
  //   lease は 240 分で切れるため、画面を開いたまま時間が経つと
  //     approve → OK / register-after-approve → 401 invalid_or_expired_session
  //   となり「承認済なのに freee 未登録・error_message も無い」状態で止まる
  //   (freee 側に何も送られないので audit にも痕跡が残らない = 気づけない)。
  //   → 使う直前に validate し、無効なら開き直す。
  const ensureSession = useCallback(async (opts?: { forceNew?: boolean }): Promise<string> => {
    if (sessionId && !opts?.forceNew) {
      if (await validateSession(sessionId)) return sessionId;
      setSessionId(null);
    }
    try {
      const s = await openSessionForBank();
      setSessionId(s.session_id);
      return s.session_id;
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `session open failed: ${e instanceof Error ? e.message : String(e)}` });
      throw e;
    }
  }, [sessionId, pushToast]);

  // -------------------------------------------------------------------------
  // Load rows (triggered by state change)
  // -------------------------------------------------------------------------
  // 2026-07-11 監査 fix (⑧型 UI不可視 / pagination): 旧実装は limit=300 固定 + offset=0 のみで、
  // 月次件数が 300 を超える月 (例 2025-12=363件 / 2026-06=340件) や 全期間 (5000件超) は
  // 先頭 300 件しか表示されず残りが UI から到達不能だった。
  // server cap (limit<=500) に合わせ 500/page で eager 2 page (=1000行、月次最大 ~400 を確実に
  // カバー = 月内全件が最初から見える) を取得し、それを超える分 (全期間など) は
  // 「もっと見る」ボタンで next_offset を辿って追加読込する。
  // 不可視ゼロ保証: footer / ボタン / フィルタバーに「総N件中M件」を常時表示。
  const PAGE_SIZE = 500;
  const EAGER_PAGES = 2;
  const MORE_PAGES_PER_CLICK = 4;

  const buildListParams = useCallback((): Parameters<typeof fetchBankWalletTxns>[0] => {
    const range = isAllPeriods ? null : monthToRange(selectedMonth);
    const params: Parameters<typeof fetchBankWalletTxns>[0] = {
      limit: PAGE_SIZE,
      offset: 0,
      order: buildOrderParam(sortKey, sortDir),
    };
    if (range) {
      params.start_date = range.start;
      params.end_date = range.end;
    }
    if (selectedWalletable) params.walletable_id = selectedWalletable;
    if (selectedWalletableType !== 'all') params.walletable_type = selectedWalletableType;
    if (flowFilter !== 'all') params.flow_type = flowFilter;
    if (searchText) params.search = searchText;

    // Chip -> server-side processing_status / client-side flag
    if (chipKey === 'needs_review') params.processing_status = 'needs_review';
    else if (chipKey === 'error') params.processing_status = 'error';
    else if (chipKey === 'pending') params.processing_status = 'pending';
    else if (chipKey === 'analyzing') params.processing_status = 'analyzing';
    else if (chipKey === 'registered') params.processing_status = 'registered';
    else if (chipKey === 'approved_unregistered') params.processing_status = 'approved';
    else if (chipKey === 'mechanical_suggest') params.processing_status = 'mechanical_suggest_stage1';
    else if (chipKey === 'mechanical_reconcile') params.processing_status = 'mechanical_reconcile_stage2';
    // 'rule_waiting' = pending + rule_matched → server-side: rule_matched=1, processing_status='pending'
    if (chipKey === 'rule_waiting') {
      params.processing_status = 'pending';
      params.rule_matched = 1;
    }
    return params;
  }, [selectedMonth, isAllPeriods, selectedWalletable, selectedWalletableType, chipKey, flowFilter, searchText, sortKey, sortDir]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    const gen = ++listGenRef.current;
    try {
      const range = isAllPeriods ? null : monthToRange(selectedMonth);
      const params = buildListParams();

      const fetchEagerPages = async () => {
        const first = await fetchBankWalletTxns(params);
        const acc = [...first.items];
        let next = first.next_offset;
        let page = 1;
        while (next != null && page < EAGER_PAGES) {
          const res = await fetchBankWalletTxns({ ...params, offset: next });
          if (!res.items.length) { next = null; break; }
          acc.push(...res.items);
          next = res.next_offset;
          page += 1;
        }
        return { ...first, items: acc, next_offset: next };
      };

      const [listRes, summaryRes, metricsRes] = await Promise.all([
        fetchEagerPages(),
        fetchBankSummary(range?.start, range?.end).catch(() => null),
        fetchLearningMetrics(30).catch(() => null),
      ]);
      if (gen !== listGenRef.current) return; // 条件変更で新しい load が走った → この応答は破棄
      setRows(listRes.items);
      setNextOffset(listRes.next_offset ?? null);
      setTotal(listRes.total);
      if (listRes.chip_counts) setServerChipCounts(listRes.chip_counts);
      if (summaryRes) setSummary(summaryRes);
      if (metricsRes) setLearningMetrics(metricsRes);
      setLastLoadedAt(new Date().toISOString().slice(0, 19).replace('T', ' '));
    } catch (e: unknown) {
      if (gen === listGenRef.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (gen === listGenRef.current) setLoading(false);
    }
  }, [isAllPeriods, selectedMonth, buildListParams]);

  // 「もっと見る」— next_offset から追加ページを読込 (⑧型 fix)
  const loadMoreRows = useCallback(async () => {
    if (loadingMore || nextOffset == null) return;
    setLoadingMore(true);
    const gen = listGenRef.current;
    try {
      const params = buildListParams();
      const acc: BankWalletTxnRow[] = [];
      let next: number | null = nextOffset;
      let page = 0;
      while (next != null && page < MORE_PAGES_PER_CLICK) {
        const res: Awaited<ReturnType<typeof fetchBankWalletTxns>> = await fetchBankWalletTxns({ ...params, offset: next });
        if (gen !== listGenRef.current) return; // 一覧が別条件で再読込された → 追加しない
        if (!res.items.length) { next = null; break; }
        acc.push(...res.items);
        next = res.next_offset;
        setTotal(res.total);
        page += 1;
      }
      if (gen !== listGenRef.current) return;
      if (acc.length > 0) setRows((prev) => [...prev, ...acc]);
      setNextOffset(next);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `追加読込失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, nextOffset, buildListParams, pushToast]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // freee マスタ (account_items / sections / tax_codes) を 1 回だけ取得 (読取専用)。
  // proposed_deal_json の account_item_id / section_id / tax_code を名称解決するため。
  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      fetchAccountItemsMap(), fetchSectionsMap(), fetchTaxCodesMap(),
      fetchItemsMap(), fetchTagsMap(), fetchPartnersMap(),
    ]).then(
      ([ai, se, tx, it, tg, pa]) => {
        if (!alive) return;
        if (ai.status === 'fulfilled') setAccountItemsMap(ai.value);
        if (se.status === 'fulfilled') setSectionsMap(se.value);
        if (tx.status === 'fulfilled') setTaxCodesMap(tx.value);
        if (it.status === 'fulfilled') setItemsMap(it.value);
        if (tg.status === 'fulfilled') setTagsMap(tg.value);
        if (pa.status === 'fulfilled') setPartnersMap(pa.value);
      },
    );
    return () => { alive = false; };
  }, []);

  // 部門選択肢 (canonical マスタ = fetchSectionsMap 由来。定数化禁止 — client.ts 5520 参照)
  const sectionOptions = useMemo(
    () => Array.from(sectionsMap.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ja')),
    [sectionsMap],
  );

  // ---------------------------------------------------------------------------
  // Realtime reflection: timeline event を deterministic 反映 (2 種類)
  //   - 'freee_registered' → 行を「登録済」に更新
  //   - 'wallet_txn_status_changed' → drift 検出 → loadRows() で再取得
  // worker 側は markD1ReconcileSuccess + handleBankFreshen* で emit。
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!polling.items.length) return;
    let touched = false;
    let needsFullReload = false;
    const targetWalletTxnIds = new Set<number>();
    for (const it of polling.items) {
      if (handledEventIds.current.has(it.id)) continue;
      const meta: any = it.metadata || {};
      const wtId = meta.wallet_txn_id ?? meta.summary?.wallet_txn_id;
      if (it.title === 'freee_registered') {
        handledEventIds.current.add(it.id);
        if (typeof wtId === 'number') {
          targetWalletTxnIds.add(wtId);
          touched = true;
        }
      } else if (it.title === 'wallet_txn_status_changed') {
        handledEventIds.current.add(it.id);
        // drift 内容が status / balance / rule_matched 変化 → 単純に再 load
        needsFullReload = true;
      }
    }
    if (!touched && !needsFullReload) return;
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r): BankWalletTxnRow => {
        if (targetWalletTxnIds.has(r.wallet_txn_id) && r.processing?.processing_status !== 'registered') {
          changed = true;
          const baseProcessing = r.processing ?? {
            ref_number: `bank:${r.wallet_txn_id}`,
            processing_status: 'registered',
            flow_type: 'unknown',
            partner_id: null,
            partner_name: null,
            pattern_source: null,
            pattern_confidence: null,
            readers_passed: 0,
            reconcile_verdict: null,
            proposal_id: null,
            freee_deal_id: null,
            registered_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
            last_updated: new Date().toISOString().slice(0, 19).replace('T', ' '),
            auto_register_rule: null,
          } as any;
          return {
            ...r,
            status_raw: 2 as const,
            processing: { ...baseProcessing, processing_status: 'registered' as any },
          };
        }
        return r;
      });
      return changed ? next : prev;
    });
    // server-side counts/badges 反映のため軽い refetch (1 秒後 throttle)
    const handle = window.setTimeout(() => { loadRows(); }, 1000);
    return () => window.clearTimeout(handle);
  }, [polling.items, loadRows]);

  // ---------------------------------------------------------------------------
  // Event-driven freshen: user signal で freee 真値を即座に取得 (cron polling 補完)
  //   1. onMount + selectedWalletable 変更 → 表示中 walletable を即 sync
  //   2. tab focus 復帰 → 表示中を即 sync
  //   3. visibility 変化 → 同上
  // worker /api/bank-wallet-txns/freshen が drift 検出 → per-row event → 上 useEffect で反映
  // ---------------------------------------------------------------------------
  const lastFreshenAt = useRef<number>(0);
  const triggerFreshen = useCallback(async (reason: string) => {
    // 2026-05-06 fix v2: 全 walletable / 30日 を auto sync
    //   selected あれば 1 件、 none なら filter 適用 active 全件
    const ids = selectedWalletable
      ? [selectedWalletable]
      : WALLETABLES
          .filter((w) => selectedWalletableType === 'all' || w.walletable_type === selectedWalletableType)
          .map((w) => w.walletable_id);
    if (ids.length === 0) return;
    const now = Date.now();
    if (now - lastFreshenAt.current < 5000) return;
    lastFreshenAt.current = now;
    try {
      await fetch('https://invoice-review.system-0f7.workers.dev/api/bank-wallet-txns/freshen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletable_ids: ids, days: 30 }),
      });
      // processing_status auto-sync (= silent)
      await fetch('https://invoice-review.system-0f7.workers.dev/api/bank-wallet-txns/sync-status-from-freee', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
    } catch {
      // best-effort、 失敗時は polling fallback で取れる
    }
  }, [selectedWalletable, selectedWalletableType]);

  // 1) onMount + walletable 変更時に即時 freshen
  useEffect(() => { triggerFreshen('mount_or_filter'); }, [triggerFreshen]);

  // 2) tab focus / visibility 変化で freshen
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') triggerFreshen('visibility');
    };
    const onFocus = () => triggerFreshen('focus');
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [triggerFreshen]);

  // -------------------------------------------------------------------------
  // 「最新の状態に更新」 manual button handler (= 2026-05-08 完全書直し)
  //
  // 旧実装の問題:
  //  - 6 walletables × 180 days を 1 request で送信 → worker 内 sequential loop で
  //    120s timeout (= browser fetch も応答なし)、 button 永久 disabled like
  //  - loading state は table reload と共有されており button feedback 弱い
  //  - toast は短命で見落とされる
  //
  // 新実装:
  //  - 90 days (= 3 ヶ月、 99% カバー) に短縮
  //  - per-wallet 並列 fetch (= Promise.all) で 6 wallets が同時に走る
  //  - 各 wallet ごと 60s AbortController timeout
  //  - 専用 refreshing state で progress (= done/total / fetched / drifts / errors)
  //  - persistent banner (= 完了後 5 秒で auto-dismiss、 error 時は手動閉じ)
  //  - InlineSpinner + 進捗 text on button
  // -------------------------------------------------------------------------
  const performRefresh = useCallback(async () => {
    if (refreshing?.active) return;
    if (refreshAutoDismissRef.current) {
      window.clearTimeout(refreshAutoDismissRef.current);
      refreshAutoDismissRef.current = null;
    }

    const ids = selectedWalletable
      ? [selectedWalletable]
      : WALLETABLES
          .filter((w) => selectedWalletableType === 'all' || w.walletable_type === selectedWalletableType)
          .map((w) => w.walletable_id);
    if (ids.length === 0) {
      pushToast({ kind: 'notice', text: '対象 walletable なし' });
      return;
    }

    lastFreshenAt.current = 0;
    const startedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    setRefreshing({
      active: true, total: ids.length, done: 0, fetched: 0, drifts: 0, errors: 0,
      startedAt, finishedAt: null, errorMsgs: [],
    });

    const ABORT_PER_WALLET_MS = 60_000;
    const REFRESH_DAYS = 90;
    const WORKER = 'https://invoice-review.system-0f7.workers.dev';

    const calls = ids.map(async (id) => {
      const ctl = new AbortController();
      const tm = window.setTimeout(() => ctl.abort(), ABORT_PER_WALLET_MS);
      try {
        const r = await fetch(WORKER + '/api/bank-wallet-txns/freshen', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ walletable_ids: [id], days: REFRESH_DAYS }),
          signal: ctl.signal,
        });
        const d = await r.json();
        window.clearTimeout(tm);
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        const w = d.walletables?.[0];
        setRefreshing((s) =>
          s
            ? {
                ...s,
                done: s.done + 1,
                fetched: s.fetched + (w?.fetched || 0),
                drifts: s.drifts + (w?.drifts || 0),
              }
            : s,
        );
      } catch (e: unknown) {
        window.clearTimeout(tm);
        const isAbort = (e as Error)?.name === 'AbortError';
        const msg = `wallet ${id}: ${isAbort ? 'timeout (60s)' : ((e as Error)?.message || String(e))}`;
        setRefreshing((s) =>
          s
            ? {
                ...s,
                done: s.done + 1,
                errors: s.errors + 1,
                errorMsgs: [...s.errorMsgs, msg],
              }
            : s,
        );
      }
    });

    await Promise.all(calls);

    // freee → freeeman processing_status sync (silent best-effort)
    try {
      await fetch(WORKER + '/api/bank-wallet-txns/sync-status-from-freee', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
    } catch { /* tolerant */ }

    await loadRows();

    const finishedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    setRefreshing((s) => (s ? { ...s, active: false, finishedAt } : s));

    // success のみ 5 秒で auto-dismiss、 error あれば手動閉じ
    refreshAutoDismissRef.current = window.setTimeout(() => {
      setRefreshing((s) => (s && s.errors === 0 ? null : s));
      refreshAutoDismissRef.current = null;
    }, 5000);
  }, [refreshing?.active, selectedWalletable, selectedWalletableType, loadRows, pushToast]);

  useEffect(() => {
    return () => {
      if (refreshAutoDismissRef.current) {
        window.clearTimeout(refreshAutoDismissRef.current);
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // Bulk Reconcile (全19口座 × 2026-01-01 以降) — freee OAuth トークン必要
  // -------------------------------------------------------------------------
  const runBulkReconcile = useCallback(() => {
    const cached = sessionStorage.getItem('freee_oauth_token') || '';
    askPrompt({
      title: '全件 Reconcile 実行',
      label: 'freee OAuth アクセストークンを貼り付けてください。\n(全19口座 × 2026-01-01〜今日 を reconcile します)',
      placeholder: 'Bearer xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      primaryLabel: '実行',
      onSubmit: async (token) => {
        const raw = (token || cached).trim();
        if (!raw) { pushToast({ kind: 'error', text: 'トークンが空です' }); return; }
        const bearer = raw.toLowerCase().startsWith('bearer ') ? raw : `Bearer ${raw}`;
        sessionStorage.setItem('freee_oauth_token', bearer);
        setReconcileRunning(true);
        pushToast({ kind: 'info', text: '全件 Reconcile 実行中 (数分かかります)…' });
        try {
          const today = new Date().toISOString().slice(0, 10);
          let totalMatched = 0, totalCreated = 0, totalUpdated = 0, totalSkipped = 0, totalFreee = 0;
          for (const w of WALLETABLES) {
            try {
              const res = await fetch('https://invoice-review.system-0f7.workers.dev/api/bank-wallet-txns/reconcile-freee-deals', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Freee-Token': bearer },
                body: JSON.stringify({
                  walletable_id: w.walletable_id,
                  walletable_type: w.walletable_type,
                  start_date: '2026-01-01',
                  end_date: today,
                }),
              });
              if (!res.ok) {
                const txt = await res.text();
                pushToast({ kind: 'error', text: `${w.name} reconcile failed: ${res.status} ${txt.slice(0, 120)}` });
                continue;
              }
              const j = await res.json() as { matched?: number; created?: number; updated?: number; skipped?: number; freee_deals_scanned?: number };
              totalMatched += j.matched || 0;
              totalCreated += j.created || 0;
              totalUpdated += j.updated || 0;
              totalSkipped += j.skipped || 0;
              totalFreee   += j.freee_deals_scanned || 0;
            } catch (e) {
              pushToast({ kind: 'error', text: `${w.name} reconcile error: ${e instanceof Error ? e.message : String(e)}` });
            }
          }
          pushToast({
            kind: 'success',
            text: `全件 Reconcile 完了: freee scan ${totalFreee} / matched ${totalMatched} / created ${totalCreated} / updated ${totalUpdated} / skipped ${totalSkipped}`,
          });
          loadRows();
        } finally {
          setReconcileRunning(false);
        }
      },
    });
  }, [askPrompt, pushToast, loadRows]);

  // -------------------------------------------------------------------------
  // SidePanel loader
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!activePanelId) {
      setPanelDetail(null);
      setPanelFreeeDeal(null);
      setPanelFreeeDealError(null);
      setPanelAudit([]);
      setPanelLearning(null);
      return;
    }
    // Codex review fix: 行切替時に panelLearning を必ず clear (= 前 wallet_txn の値を持ち越さない)
    setPanelLearning(null);
    let alive = true;
    (async () => {
      try {
        const d = await fetchBankWalletTxnDetail(activePanelId);
        if (!alive) return;
        setPanelDetail(d);
        const shouldFetchDeal = !!d.processing?.freee_deal_id || d.status_raw === 2;
        if (shouldFetchDeal) {
          try {
            const fd = await fetchBankFreeeDeal(activePanelId);
            if (!alive) return;
            setPanelFreeeDeal(fd);
            setPanelFreeeDealError(null);
          } catch (fdErr: unknown) {
            if (alive) {
              setPanelFreeeDeal(null);
              setPanelFreeeDealError(fdErr instanceof Error ? fdErr.message : String(fdErr));
            }
          }
        } else {
          setPanelFreeeDeal(null);
          setPanelFreeeDealError(null);
        }
      } catch (e: unknown) {
        if (alive) pushToast({ kind: 'error', text: `詳細取得失敗: ${e instanceof Error ? e.message : String(e)}` });
      }
    })();
    return () => { alive = false; };
  }, [activePanelId, pushToast]);

  useEffect(() => {
    if (!activePanelId || sidePanelTab !== 2) return;
    let alive = true;
    (async () => {
      try {
        const a = await fetchBankAuditLog(activePanelId);
        if (alive) setPanelAudit(a.events || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [activePanelId, sidePanelTab]);

  // Learning evidence loader (= 根拠 tab で表示、 schema-v33)
  useEffect(() => {
    if (!activePanelId || sidePanelTab !== 1) return;
    let alive = true;
    (async () => {
      try {
        const ev = await fetchBankLearningEvidence(activePanelId);
        if (alive) setPanelLearning(ev);
      } catch { /* ignore — endpoint may be missing */ }
    })();
    return () => { alive = false; };
  }, [activePanelId, sidePanelTab]);

  // Hash sync
  useEffect(() => {
    const hash = buildBankHash(isAllPeriods ? null : selectedMonth, selectedWalletable, activePanelId);
    if (window.location.hash !== hash) {
      window.history.replaceState(null, '', hash);
    }
  }, [selectedMonth, isAllPeriods, selectedWalletable, activePanelId]);

  // -------------------------------------------------------------------------
  // Client-side finer filter (for amount range / entrySide)
  // -------------------------------------------------------------------------
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (entrySideFilter !== 'all' && r.entry_side !== entrySideFilter) return false;
      const min = minAmount ? Number(minAmount) : null;
      const max = maxAmount ? Number(maxAmount) : null;
      if (min != null && r.amount < min) return false;
      if (max != null && r.amount > max) return false;
      return true;
    });
  }, [rows, entrySideFilter, minAmount, maxAmount]);

  // -------------------------------------------------------------------------
  // Stats for ProgressBar / Chip counts (computed from current rows)
  // -------------------------------------------------------------------------
  const chipCounts = useMemo<Record<ChipKey, number>>(() => {
    // 2026-04-23: server-side chip_counts を優先 (全件 global に正確)。
    // fallback (未取得時) は loaded rows から local 集計。
    // 2026-07-13: server 応答に無いキー (analyzing 等) は local 集計で補完してマージ
    // (= undefined 件数表示の防御。全キーが number であることを保証する)。
    const c: Record<ChipKey, number> = {
      all: rows.length,
      needs_review: 0,
      error: 0,
      pending: 0,
      analyzing: 0,
      mechanical_suggest: 0,
      mechanical_reconcile: 0,
      rule_waiting: 0,
      registered: 0,
      approved_unregistered: 0,
    };
    for (const r of rows) {
      const s = r.processing?.processing_status;
      const freeeReg = r.status_raw === 2;
      if (s === 'mechanical_suggest_stage1') c.mechanical_suggest += 1;
      else if (s === 'mechanical_reconcile_stage2') c.mechanical_reconcile += 1;
      else if (s === 'needs_review' || s === 'suggested') c.needs_review += 1;
      else if (s === 'queued' || s === 'processing' || s === 'analyzing') c.analyzing += 1;
      else if (s === 'error') c.error += 1;
      else if (s === 'registered' || (freeeReg && (!s || s === 'pending'))) c.registered += 1;
      // 2026-08-25 P1 fix (ぐるなび¥100 incident): 'approved' はどの条件にも一致しないまま
      // 集計から漏れ、「未登録なし」の誤表示を招いていた。専用 chip で可視化する。
      else if (s === 'approved') c.approved_unregistered += 1;
      else if ((!s || s === 'pending') && r.rule_matched && !freeeReg) c.rule_waiting += 1;
      else if ((!s || s === 'pending') && !freeeReg) c.pending += 1;
    }
    if (!serverChipCounts) return c;
    const merged = { ...c };
    (Object.keys(c) as ChipKey[]).forEach((k) => {
      const v = serverChipCounts[k];
      if (typeof v === 'number') merged[k] = v;
    });
    return merged;
  }, [rows, serverChipCounts]);

  const registeredCount = chipCounts.registered;
  // 進捗の分母は「全件」を使う。chip フィルタ適用時 `total` (=listRes.total) は
  // フィルタ済サブセット件数になり、分子 registeredCount は serverChipCounts の
  // global 値なので不整合 (例 808/38=2126%) になる。分母は chipCounts.all (global) を
  // 優先し、分子が分母を超えないよう clamp する。
  const progressTotal = Math.max(chipCounts.all || total || rows.length || 1, registeredCount, 1);
  const progressPct = progressTotal > 0 ? Math.min(100, Math.round((registeredCount / progressTotal) * 100)) : 0;

  // 2026-07-13: 分析進捗の可視化 — analyzing (queued/processing) が残っている間は
  // 30秒ごとに軽い再読込 (既存 chip_counts / event polling 機構の補完。分析完了は
  // ミニPC jobs 経路 → submit-suggestion で D1 だけが変わるため timeline event が
  // 届かないケースがあり、polling fallback で確実に反映する)。
  const hasAnalyzing = chipCounts.analyzing > 0;
  useEffect(() => {
    if (!hasAnalyzing) return;
    const h = window.setInterval(() => { loadRows(); }, 30000);
    return () => window.clearInterval(h);
  }, [hasAnalyzing, loadRows]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const onSortClick = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        return prev;
      }
      // new column → default direction (date/updated/fetched → desc, amount → desc, balance → desc, status/account → asc)
      setSortDir(key === 'account' || key === 'status' ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === filtered.length) return new Set();
      const s = new Set<number>();
      for (const r of filtered) s.add(r.wallet_txn_id);
      return s;
    });
  }, [filtered]);

  const handleRowClick = useCallback((r: BankWalletTxnRow) => {
    if (activePanelId === r.wallet_txn_id) {
      setActivePanelId(null);
    } else {
      setActivePanelId(r.wallet_txn_id);
      setSidePanelTab(0);
    }
    setFocusedRowId(r.wallet_txn_id);
  }, [activePanelId]);

  // SidePanel prev/next navigation
  const navigatePanel = useCallback((delta: -1 | 1) => {
    if (!activePanelId || filtered.length === 0) return;
    const idx = filtered.findIndex((x) => x.wallet_txn_id === activePanelId);
    if (idx < 0) return;
    const next = filtered[idx + delta];
    if (next) {
      setActivePanelId(next.wallet_txn_id);
      setFocusedRowId(next.wallet_txn_id);
      setSidePanelTab(0);
    }
  }, [activePanelId, filtered]);

  const onEnqueue = useCallback(async (id: number) => {
    try {
      setWorkingIds((s) => { const n = new Set(s); n.add(id); return n; });
      const sess = await ensureSession();
      const res = await enqueueBankAnalysis(id, sess);
      pushToast({ kind: 'success', text: `サジェスト作成: ${res.ref_number} → queued (readers=${res.dispatched_to})` });
      setTimeout(() => { loadRows(); }, 2500);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setWorkingIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [ensureSession, loadRows, pushToast]);

  // 2026-07-14 部門接地 (A1/A2): 承認前に提案仕訳の部門状態を実データで確認し、
  //   - 部門未確定行が残る & 「不明のまま登録」ack なし → 承認を開かず panel へ誘導 (黙って部門なし登録しない)
  //   - panel の部門編集あり → modifications.details として送信 (worker が approved_with_modification で学習)
  //   - 「この取引先は常にこの部門」opt-in → 承認成功後に POST /api/learn kind=vendor_default_section
  const onApprove = useCallback(async (id: number) => {
    // 提案仕訳の取得: panel 表示中ならその detail、未表示なら on-demand fetch (best-effort — 失敗時は従来動作)
    let pdj: any = null;
    if (panelDetail?.wallet_txn_id === id) {
      pdj = panelDetail.processing?.proposed_deal_json ?? null;
    } else {
      try {
        const det = await fetchBankWalletTxnDetail(id);
        pdj = det.processing?.proposed_deal_json ?? null;
      } catch { pdj = null; }
    }
    const dets: any[] = Array.isArray(pdj?.details) ? pdj.details : [];
    const edits = sectionEdits.get(id);
    const unresolved = unresolvedSectionIndexes(dets, edits);
    const acked = sectionUnknownAcks.has(id);
    if (unresolved.length > 0 && !acked) {
      pushToast({
        kind: 'error',
        text: `部門未確定の明細が ${unresolved.length}行 あります (wtId=${id})。詳細パネルの「部門」列で指定するか、「部門は不明のまま登録する」を選択してください。`,
      });
      setActivePanelId(id);
      setSidePanelTab(0);
      setFocusedRowId(id);
      return;
    }
    // 2026-09-19 W13: AI が合議不成立 (disagree) とした行は、指摘内容を読んだ明示 ack が
    //   ないと承認に進まない (worker 側 409 になる前に UI で理由ごと示す)。
    const rowForDisagree = rows.find((x) => x.wallet_txn_id === id);
    const procForDisagree = (panelDetail?.wallet_txn_id === id ? panelDetail.processing : rowForDisagree?.processing) as any;
    const isDisagreeRow = procForDisagree?.reconcile_verdict === 'disagree'
      && procForDisagree?.processing_status === 'needs_review';
    const disagreeAcked = disagreeAcks.has(id);
    if (isDisagreeRow) {
      // 2026-09-24: 越えられない行 (部門以外が未確定 / 部門は確定済みで AI の疑いが別項目) は
      //   承認に進ませず、次の一手 (再サジェスト / 却下+修正指示) を案内する。
      const unlock = disagreeUnlockState(pdj, dets);
      if (!unlock.eligible) {
        pushToast({
          kind: 'error',
          text: unlock.reason === 'no_section_gap'
            ? `この行は部門が確定済みで、AI の疑いは部門以外の項目です (wtId=${id})。人間承認では越えられません — 「再サジェスト」で再判定するか、「却下」して修正指示を出してください。`
            : `取引先・勘定科目・税区分・金額のいずれかが未確定のため、人間承認では越えられません (wtId=${id})。「再サジェスト」か「却下」→ 修正指示で解消してください。`,
        });
        setActivePanelId(id);
        setSidePanelTab(0);
        setFocusedRowId(id);
        return;
      }
      if (!disagreeAcked) {
        pushToast({
          kind: 'error',
          text: `AI が確証を持てなかった行です (wtId=${id})。詳細パネルで「AI の指摘」を確認し、チェックを入れてから承認してください。`,
        });
        setActivePanelId(id);
        setSidePanelTab(0);
        setFocusedRowId(id);
        return;
      }
    }
    const modifiedDetails = buildSectionModifiedDetails(dets, edits);
    const editCount = edits ? edits.size : 0;
    // 「この取引先は常にこの部門」学習の準備 (実効部門が 1 つ + 取引先名が実在する場合のみ)
    const effSections = effectiveSectionIds(dets, edits);
    const rowForLearn = rows.find((x) => x.wallet_txn_id === id);
    const pdjPartnerId: number | null = pdj?.partner_id != null ? Number(pdj.partner_id) : null;
    const partnerIdForLearn = pdjPartnerId ?? rowForLearn?.processing?.partner_id
      ?? (panelDetail?.wallet_txn_id === id ? panelDetail.processing?.partner_id ?? null : null);
    const vendorKeyForLearn: string | null =
      (partnerIdForLearn != null ? partnersMap.get(Number(partnerIdForLearn)) : undefined)
      || rowForLearn?.processing?.partner_name
      || (panelDetail?.wallet_txn_id === id ? panelDetail.processing?.partner_name ?? null : null)
      || null;
    const learnRequested = learnVendorChecks.has(id) && effSections.length === 1 && !!vendorKeyForLearn;
    const learnSectionId = learnRequested ? effSections[0] : null;

    askConfirm({
      title: '承認 → freee登録 + 消込',
      body: (
        <>
          wallet_txn_id={id} を承認し、続けて freee へ仕訳登録 + 消込まで自動実行します。
          {modifiedDetails && (
            <span style={{ display: 'block', marginTop: '6px', fontSize: '0.8125rem', color: '#1a5cc7' }}>
              部門修正 {editCount}行 を反映して承認します — 修正内容は学習記録 (approved_with_modification) に保存され、今後の提案に活用されます。
            </span>
          )}
          {unresolved.length > 0 && (
            <span style={{ display: 'block', marginTop: '6px', fontSize: '0.8125rem', color: UNIFIED_COLOR.action_required.fg, fontWeight: 600 }}>
              部門未確定 {unresolved.length}行 は不明のまま (部門なしで) 登録されます — 「部門は不明のまま登録する」選択済み。
            </span>
          )}
          {learnRequested && learnSectionId != null && (
            <span style={{ display: 'block', marginTop: '6px', fontSize: '0.8125rem', color: '#267a62' }}>
              承認後に学習: {vendorKeyForLearn} → {sectionsMap.get(learnSectionId) || `#${learnSectionId}`} (今後この取引先の既定部門として自動適用)
            </span>
          )}
        </>
      ),
      primaryLabel: '承認して登録 ▸',
      onConfirm: async () => {
        try {
          setWorkingIds((s) => { const n = new Set(s); n.add(id); return n; });
          // [pre-action freshness check] freee 真値で「まだ未消込か」を確認 (drift 防止)
          try {
            const fr = await fetch('https://invoice-review.system-0f7.workers.dev/api/bank-wallet-txns/freshen-row', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ wallet_txn_id: id }),
            });
            const frJson = await fr.json().catch(() => null);
            if (fr.ok && frJson?.after?.status_raw === 2) {
              pushToast({ kind: 'notice', text: `freee で既に登録済 (wtId=${id})。 list を更新します。` });
              loadRows();
              return;
            }
          } catch { /* freshen 失敗は best-effort、 進行 */ }
          const sess = await ensureSession();
          // 承認 (既に approved の row は cannot_approve を握り潰して登録へ進む)
          try {
            await approveBankWalletTxn(
              id, 'web_app_user', sess,
              modifiedDetails ? { details: modifiedDetails } : undefined,
              (unresolved.length > 0 || (isDisagreeRow && disagreeAcked))
                ? {
                    ...(unresolved.length > 0 ? { allowSectionUnknown: true } : {}),
                    ...(isDisagreeRow && disagreeAcked ? { overrideReconcileDisagree: true } : {}),
                  }
                : undefined,
            );
            // 学習フィードバック (設計 user要求3): 修正承認 = 学習の正イベント
            if (modifiedDetails) {
              pushToast({ kind: 'success', text: `部門修正 ${editCount}行 を反映して承認しました — 修正内容は学習されます (wtId=${id})` });
            }
          } catch (ae) {
            const am = ae instanceof Error ? ae.message : String(ae);
            if (!/cannot_approve|already|approved/i.test(am)) throw ae;
          }
          // 「この取引先は常にこの部門」学習 (user 明示 opt-in のみ / 失敗しても承認は完了済)
          if (learnRequested && learnSectionId != null && vendorKeyForLearn) {
            try {
              const lr = await learnVendorDefaultSection({
                vendorKey: vendorKeyForLearn,
                section: learnSectionId,
                instructionExcerpt: `銀行取引承認時に「この取引先は常にこの部門」を指定 (wtId=${id})`,
              });
              pushToast({
                kind: 'success',
                text: lr.changed
                  ? `学習しました: ${lr.vendor_key} → ${lr.section_name} (今後の提案に自動適用)`
                  : `学習済み (変更なし): ${lr.vendor_key} → ${lr.section_name}`,
              });
            } catch (le: unknown) {
              pushToast({ kind: 'error', text: `学習失敗 (承認は完了済): ${le instanceof Error ? le.message : String(le)}` });
            }
          }
          clearSectionUiState(id);
          // 一気通貫: 承認後そのまま freee 登録 + 消込まで実行 (UIクリック起点 = 本番書込OK)
          pushToast({ kind: 'notice', text: `freee 登録 + 消込を実行中… (wtId=${id})` });
          const callRegister = async (sid: string) => {
            const resp = await fetch(`https://invoice-review.system-0f7.workers.dev/api/bank-wallet-txns/${id}/register-after-approve`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ session_id: sid }),
            });
            return { resp, json: await resp.json().catch(() => null) };
          };
          let { resp: regResp, json: regJson } = await callRegister(sess);
          // 2026-08-15: lease 失効で登録だけが 401 になる事故 (承認済なのに freee 未登録) の自動復旧。
          //   session を開き直して 1 回だけ再試行する (承認は完了済なので冪等)。
          if (regResp.status === 401) {
            const fresh = await ensureSession({ forceNew: true });
            ({ resp: regResp, json: regJson } = await callRegister(fresh));
          }
          if (regResp.ok && regJson?.ok) {
            setRegisterError(id, null);
            pushToast({ kind: 'success', text: `登録 + 消込 完了 (deal ${regJson.freee_deal_id} / wtId=${id})` });
          } else if (isReconcilePendingResponse(regResp.status, regJson)) {
            // 2026-09-14: deal 登録済・消込だけ cron 自動リトライ待ち → 失敗扱いにしない
            setRegisterError(id, null);
            pushToast({ kind: 'notice', text: reconcilePendingToastText(regJson.freee_deal_id, id) });
          } else {
            const regErr = String(regJson?.error || `HTTP ${regResp.status}`);
            setRegisterError(id, regErr);
            pushToast({ kind: 'error', text: `登録失敗: ${regErr}（承認は完了済。wtId=${id}）` });
          }
          setTimeout(() => { loadRows(); }, 1500);
        } catch (e: unknown) {
          pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
        } finally {
          setWorkingIds((s) => { const n = new Set(s); n.delete(id); return n; });
        }
      },
    });
  }, [
    askConfirm, ensureSession, loadRows, pushToast, setRegisterError,
    panelDetail, sectionEdits, sectionUnknownAcks, learnVendorChecks, disagreeAcks,
    rows, partnersMap, sectionsMap, clearSectionUiState,
  ]);

  // A3 (2026-07-14 確定設計): 自社振替の確認 — 既存 reject endpoint (reason='self_transfer_confirmed')
  // で skipped/reversible に着地させる。freee 側の口座振替消込は user が freee UI で実施する
  // (本アプリからの自動消込・自動 freee 書込はしない)。誤操作時は「復元 ▸」で戻せる。
  const onConfirmSelfTransfer = useCallback((id: number) => {
    askConfirm({
      title: '自社振替として確認',
      body: (
        <>
          自社口座間の資金移動 (振替) として確認し、仕訳提案の対象外 (除外(スキップ)・復元可) にします。
          <span style={{ display: 'block', marginTop: '6px', fontSize: '0.8125rem', color: '#6b6560' }}>
            freee 側の消込 (口座振替) は freee UI で実施してください。誤操作時は「復元 ▸」で戻せます。
          </span>
        </>
      ),
      primaryLabel: '振替として確認 ▸',
      onConfirm: async () => {
        try {
          setWorkingIds((st) => { const n = new Set(st); n.add(id); return n; });
          await rejectBankWalletTxn(id, 'self_transfer_confirmed', 'web_app_user');
          pushToast({ kind: 'success', text: `自社振替として確認しました (wtId=${id} → 除外(スキップ)・復元可)` });
          setTimeout(() => { loadRows(); }, 1500);
        } catch (e: unknown) {
          pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
        } finally {
          setWorkingIds((st) => { const n = new Set(st); n.delete(id); return n; });
        }
      },
    });
  }, [askConfirm, loadRows, pushToast]);

  const onReject = useCallback((id: number) => {
    askPrompt({
      title: '却下',
      label: '却下理由 (任意)',
      placeholder: 'なぜ却下するかを記録できます',
      primaryLabel: '却下する',
      danger: true,
      onSubmit: async (reason) => {
        try {
          setWorkingIds((s) => { const n = new Set(s); n.add(id); return n; });
          await rejectBankWalletTxn(id, reason, 'web_app_user');
          pushToast({ kind: 'success', text: `却下しました (wtId=${id})` });
          setTimeout(() => { loadRows(); }, 1500);
        } catch (e: unknown) {
          pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
        } finally {
          setWorkingIds((s) => { const n = new Set(s); n.delete(id); return n; });
        }
      },
    });
  }, [askPrompt, loadRows, pushToast]);

  // 2026-07-11 監査 fix (②型 承認済で停滞): approved 状態の行を UI から
  // register-after-approve で直接駆動する。承認済のまま登録が走らなかった行
  // (一括承認の途中失敗 / 再実行後 など) の唯一の復旧経路。
  const runRegisterAfterApprove = useCallback(async (id: number, sess: string): Promise<{ ok: boolean; error?: string; freee_deal_id?: number; reconcilePending?: boolean }> => {
    const call = async (sid: string) => {
      const resp = await fetch(`https://invoice-review.system-0f7.workers.dev/api/bank-wallet-txns/${id}/register-after-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid }),
      });
      return { resp, json: await resp.json().catch(() => null) };
    };
    let { resp: regResp, json: regJson } = await call(sess);
    // 2026-08-15: lease 失効 (240分) による 401 は開き直して 1 回だけ再試行 (登録は冪等ガード付き)
    if (regResp.status === 401) {
      const fresh = await ensureSession({ forceNew: true });
      ({ resp: regResp, json: regJson } = await call(fresh));
    }
    if (regResp.ok && regJson?.ok) return { ok: true, freee_deal_id: regJson.freee_deal_id };
    if (isReconcilePendingResponse(regResp.status, regJson)) {
      return { ok: false, reconcilePending: true, freee_deal_id: Number(regJson.freee_deal_id), error: 'reconcile_pending' };
    }
    return { ok: false, error: String(regJson?.error || `HTTP ${regResp.status}`) };
  }, [ensureSession]);

  const onRegisterOnly = useCallback(async (id: number) => {
    try {
      setWorkingIds((s) => { const n = new Set(s); n.add(id); return n; });
      setRegisterError(id, null); // 再試行開始 → 前回失敗表示を解除
      const sess = await ensureSession();
      pushToast({ kind: 'notice', text: `freee 登録 + 消込を実行中… (wtId=${id})` });
      const r = await runRegisterAfterApprove(id, sess);
      if (r.ok) {
        setRegisterError(id, null);
        pushToast({ kind: 'success', text: `登録 + 消込 完了 (deal ${r.freee_deal_id} / wtId=${id})` });
      } else if (r.reconcilePending) {
        setRegisterError(id, null);
        pushToast({ kind: 'notice', text: reconcilePendingToastText(r.freee_deal_id ?? '', id) });
      } else {
        setRegisterError(id, r.error || 'unknown');
        pushToast({ kind: 'error', text: `登録失敗: ${r.error}（wtId=${id}）` });
      }
      setTimeout(() => { loadRows(); }, 1500);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setRegisterError(id, msg);
      pushToast({ kind: 'error', text: msg });
    } finally {
      setWorkingIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [ensureSession, runRegisterAfterApprove, loadRows, pushToast, setRegisterError]);

  // 2026-07-11 監査 fix (②型): 旧実装は error→approved に戻すだけで登録を起動せず、
  // 行が「登録キュー中…」のまま停滞した。retry 後そのまま register-after-approve まで実行。
  const onRetry = useCallback(async (id: number) => {
    try {
      setWorkingIds((s) => { const n = new Set(s); n.add(id); return n; });
      setRegisterError(id, null); // 再試行開始 → 前回失敗表示を解除
      await retryBankRegister(id);
      const sess = await ensureSession();
      pushToast({ kind: 'notice', text: `再登録 + 消込を実行中… (wtId=${id})` });
      const r = await runRegisterAfterApprove(id, sess);
      if (r.ok) {
        setRegisterError(id, null);
        pushToast({ kind: 'success', text: `再登録 完了 (deal ${r.freee_deal_id} / wtId=${id})` });
      } else if (r.reconcilePending) {
        setRegisterError(id, null);
        pushToast({ kind: 'notice', text: reconcilePendingToastText(r.freee_deal_id ?? '', id) });
      } else {
        setRegisterError(id, r.error || 'unknown');
        pushToast({ kind: 'error', text: `再登録失敗: ${r.error}（approved 状態で保持。「登録を実行 ▸」で再試行可 / wtId=${id}）` });
      }
      setTimeout(() => { loadRows(); }, 1500);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setRegisterError(id, msg);
      pushToast({ kind: 'error', text: msg });
    } finally {
      setWorkingIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [ensureSession, runRegisterAfterApprove, loadRows, pushToast, setRegisterError]);

  const onRerun = useCallback(async (id: number) => {
    try {
      setWorkingIds((s) => { const n = new Set(s); n.add(id); return n; });
      const sess = await ensureSession();
      await rerunBankAnalysis(id, sess);
      pushToast({ kind: 'success', text: `再サジェストを依頼しました (wtId=${id})` });
      setTimeout(() => { loadRows(); }, 2000);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setWorkingIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [ensureSession, loadRows, pushToast]);

  // schema-v33: Stage 1 D1 hit 行を 1-click 自動 freee 登録 (= AI skip + 自動完了)
  const onMechanicalApprove = useCallback(async (id: number) => {
    try {
      setWorkingIds((s) => { const n = new Set(s); n.add(id); return n; });
      const sess = await ensureSession();
      const r = await mechanicalApproveBankWalletTxn(id, sess);
      if (r.ok && r.freee_deal_id) {
        pushToast({ kind: 'success', text: `機械承認 → freee 登録完了 (wtId=${id} → deal #${r.freee_deal_id})` });
      } else {
        pushToast({ kind: 'error', text: `機械承認失敗: ${JSON.stringify(r).slice(0, 150)}` });
      }
      setTimeout(() => { loadRows(); }, 2000);
    } catch (e: unknown) {
      // 2026-09-14 (wallet_txn 2438715451 incident): request() は 502 を throw するが、
      //   body が reconcile_pending なら deal は登録済 → 失敗 toast にしない
      const ee = e as { status?: number; body?: any };
      if (isReconcilePendingResponse(Number(ee?.status), ee?.body)) {
        pushToast({ kind: 'notice', text: reconcilePendingToastText(ee.body.freee_deal_id, id) });
        setTimeout(() => { loadRows(); }, 2000);
      } else {
        pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
      }
    } finally {
      setWorkingIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [ensureSession, loadRows, pushToast]);

  // 2026-07-13: 一括サジェストの client 側自動チャンク化。
  // worker /bulk/enqueue-analysis は ≤50件/回 の 400 ガードがあるため、選択 N 件を
  // 50件以下のチャンクに分割して順次 POST する。進捗 (チャンク m/M) は enqueueProgress
  // banner に永続表示し、失敗チャンクのみ再試行できる。
  // toast の「undefined件」バグ修正: 応答に queued キーが無い場合は results から集計する。
  const executeBulkEnqueue = useCallback(async (targets: number[]) => {
    if (targets.length === 0) return;
    const chunks: number[][] = [];
    for (let i = 0; i < targets.length; i += BULK_ENQUEUE_CHUNK_SIZE) {
      chunks.push(targets.slice(i, i + BULK_ENQUEUE_CHUNK_SIZE));
    }
    setBulkWorking(true);
    setEnqueueProgress({
      active: true, doneChunks: 0, totalChunks: chunks.length,
      queued: 0, requested: targets.length, failures: [], finishedAt: null, error: null,
    });
    const nowStr = () => new Date().toISOString().slice(0, 19).replace('T', ' ');
    try {
      const sess = await ensureSession();
      let queuedTotal = 0;
      const failures: Array<{ ids: number[]; error: string }> = [];
      for (let i = 0; i < chunks.length; i += 1) {
        const chunk = chunks[i];
        try {
          const res = await bulkEnqueueBankAnalysis(chunk, sess);
          // 「undefined件」fix: queued キーが無い応答は results から queued 件数を集計
          const queued = typeof res.queued === 'number'
            ? res.queued
            : Array.isArray(res.results)
              ? res.results.filter((x) => x && (x.status === 'queued' || x.status === 'ok' || x.status === 'dispatched')).length
              : 0;
          queuedTotal += queued;
        } catch (e: unknown) {
          failures.push({ ids: chunk, error: e instanceof Error ? e.message : String(e) });
        }
        const done = i + 1;
        const failSnapshot = failures.slice();
        setEnqueueProgress((p) => (p ? { ...p, doneChunks: done, queued: queuedTotal, failures: failSnapshot } : p));
      }
      setEnqueueProgress((p) => (p ? { ...p, active: false, finishedAt: nowStr() } : p));
      if (failures.length === 0) {
        pushToast({
          kind: 'success',
          text: `一括サジェスト: ${queuedTotal}/${targets.length}件 queued (${chunks.length}チャンク)`,
        });
        // 全成功なら banner は 6 秒で自動片付け (失敗ありは手動閉じ)
        window.setTimeout(() => {
          setEnqueueProgress((p) => (p && !p.active && p.failures.length === 0 ? null : p));
        }, 6000);
      } else {
        const failedCount = failures.reduce((n, f) => n + f.ids.length, 0);
        pushToast({
          kind: 'error',
          text: `一括サジェスト: ${queuedTotal}件 queued / ${failedCount}件 失敗 (失敗チャンク ${failures.length}/${chunks.length})`,
        });
      }
      setSelectedIds(new Set());
      setTimeout(() => { loadRows(); }, 3000);
    } catch (e: unknown) {
      // ensureSession 失敗など、チャンク送信前の失敗 (= 1件も送信されていない)。
      // 2026-07-13 18:36 incident: audit blocking violation → session/open 409 → ここに落ち、
      // banner が「完了: 0/121件 queued」と成功様式で表示され全滅が誤読された。
      // error を設定して banner を明示的な失敗状態にする (送信済チャンク失敗とは別系統)。
      const msg = e instanceof Error ? e.message : String(e);
      setEnqueueProgress((p) => (p ? { ...p, active: false, finishedAt: nowStr(), error: msg } : p));
      pushToast({ kind: 'error', text: `一括サジェスト失敗 (未送信): ${msg}` });
    } finally {
      setBulkWorking(false);
    }
  }, [ensureSession, loadRows, pushToast]);

  const onBulkEnqueue = useCallback(() => {
    const ids = Array.from(selectedIds);
    const eligible = ids.filter((id) => {
      const r = rows.find((x) => x.wallet_txn_id === id);
      return r && (!r.processing || ['pending', 'error'].includes(r.processing.processing_status));
    });
    if (eligible.length === 0) {
      pushToast({ kind: 'info', text: '対象行 (pending/error) がありません' });
      return;
    }
    const chunkCount = Math.ceil(eligible.length / BULK_ENQUEUE_CHUNK_SIZE);
    askConfirm({
      title: '一括サジェスト作成',
      body: (
        <>
          {eligible.length}件のサジェストを一括作成します。
          {chunkCount > 1 && (
            <span style={{ display: 'block', marginTop: '4px', fontSize: '0.8125rem', color: '#6b6560' }}>
              ({BULK_ENQUEUE_CHUNK_SIZE}件ずつ {chunkCount} チャンクに分割して順次送信します)
            </span>
          )}
        </>
      ),
      primaryLabel: '一括作成 ▸',
      onConfirm: () => { executeBulkEnqueue(eligible); },
    });
  }, [selectedIds, rows, askConfirm, executeBulkEnqueue, pushToast]);

  // 2026-07-11 監査 fix (①型 エラー握り潰し): 旧実装は catch {} で per-item 失敗理由を
  // 完全に捨て、件数 toast (6秒) しか出さなかった。実行本体を分離し、失敗 id + 理由を収集して
  // 永続の赤エラーブロックに表示 + 「失敗分のみ再試行」から同じ関数を再実行できるようにする。
  const executeBulkApproveRegister = useCallback(async (targets: number[]) => {
    if (targets.length === 0) return;
    try {
      setBulkWorking(true);
      setBulkReport(null);
      const sess = await ensureSession();
      let ok = 0;
      let pending = 0;
      const failures: Array<{ id: number; error: string }> = [];
      for (const id of targets) {
        setRegisterError(id, null); // 試行開始 → 前回失敗表示を解除
        try {
          // 2026-07-14 部門接地 (A1): panel で部門編集済みの行は一括承認でも編集を反映する
          // (編集の握り潰し禁止)。detail 取得に失敗した場合はその行を失敗として skip
          // (編集を黙って捨てて承認しない)。部門未確定 ack の一括 gate は worker W8
          // (bank_section_gate flag) に委ねる — 全行 detail fetch は一括経路では過重のため。
          let bulkModifications: { details: any[] } | undefined;
          const bulkEdits = sectionEdits.get(id);
          if (bulkEdits && bulkEdits.size > 0) {
            try {
              const det = await fetchBankWalletTxnDetail(id);
              const bulkDets: any[] = Array.isArray(det.processing?.proposed_deal_json?.details)
                ? det.processing!.proposed_deal_json.details : [];
              const md = buildSectionModifiedDetails(bulkDets, bulkEdits);
              if (md) bulkModifications = { details: md };
            } catch (de: unknown) {
              const dm = de instanceof Error ? de.message : String(de);
              failures.push({ id, error: `部門編集の反映に必要な detail 取得に失敗: ${dm}` });
              setRegisterError(id, `部門編集の反映に失敗 (detail 取得不可): ${dm}`);
              continue;
            }
          }
          // 承認 (既に approved の row は cannot_approve を握り潰して登録へ進む)
          try {
            await approveBankWalletTxn(id, 'web_app_user', sess, bulkModifications);
            if (bulkModifications) clearSectionUiState(id);
          } catch (ae) {
            const am = ae instanceof Error ? ae.message : String(ae);
            if (!/cannot_approve|already|approved/i.test(am)) throw ae;
          }
          // 一気通貫: 承認後そのまま freee 登録 + 消込まで実行 (UIクリック起点)
          const r = await runRegisterAfterApprove(id, sess);
          if (r.ok) {
            ok += 1;
          } else if (r.reconcilePending) {
            // 2026-09-14: deal 登録済・消込は cron 自動リトライ。再試行しても 409 already_registered になるだけなので失敗に入れない
            pending += 1;
          } else {
            console.error('[bulk-approve] register failed:', id, r.error);
            failures.push({ id, error: r.error || 'unknown' });
            setRegisterError(id, r.error || 'unknown');
          }
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e);
          console.error('[bulk-approve] failed:', id, msg);
          failures.push({ id, error: msg });
          setRegisterError(id, msg);
        }
      }
      const pendingText = pending > 0 ? ` / 消込待ち${pending}件 (freee登録済・自動回復)` : '';
      pushToast({
        kind: failures.length > 0 ? 'error' : pending > 0 ? 'notice' : 'success',
        text: `一括 登録+消込: 成功${ok}件${pendingText} / 失敗${failures.length}件`,
      });
      if (failures.length > 0) {
        setBulkReport({
          kind: '一括承認→登録',
          ok,
          pending,
          failures,
          finishedAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
        });
      }
      setSelectedIds(new Set());
      setTimeout(() => { loadRows(); }, 1500);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBulkWorking(false);
    }
  }, [ensureSession, runRegisterAfterApprove, loadRows, pushToast, setRegisterError, sectionEdits, clearSectionUiState]);

  const onBulkApprove = useCallback(() => {
    const ids = Array.from(selectedIds);
    // 2026-07-11 監査 fix (②型): 'approved' (= 承認済だが登録が走らず停滞した行) も対象に含める。
    // 承認 step は cannot_approve を握り潰して register-after-approve へ進むため安全。
    const eligible = ids.filter((id) => {
      const r = rows.find((x) => x.wallet_txn_id === id);
      return r && r.processing && ['suggested', 'needs_review', 'approved'].includes(r.processing.processing_status);
    });
    if (eligible.length === 0) {
      pushToast({ kind: 'info', text: '対象行 (suggested/needs_review/approved) がありません' });
      return;
    }
    askConfirm({
      title: '一括承認 → freee登録 + 消込',
      body: <>{eligible.length}件を承認し、freee へ登録 + 消込まで自動実行します。</>,
      primaryLabel: '一括承認して登録 ▸',
      onConfirm: () => { executeBulkApproveRegister(eligible); },
    });
  }, [selectedIds, rows, askConfirm, executeBulkApproveRegister, pushToast]);

  const executeBulkReject = useCallback(async (targets: number[], reason: string) => {
    if (targets.length === 0) return;
    try {
      setBulkWorking(true);
      setBulkReport(null);
      let ok = 0;
      // 2026-07-11 監査 fix (①型): per-item 失敗理由を収集して永続表示 + 失敗分のみ再試行
      const failures: Array<{ id: number; error: string }> = [];
      for (const id of targets) {
        try {
          await rejectBankWalletTxn(id, reason, 'web_app_user');
          ok += 1;
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e);
          console.error('[bulk-reject] failed:', id, msg);
          failures.push({ id, error: msg });
        }
      }
      pushToast({ kind: failures.length > 0 ? 'error' : 'success', text: `一括却下: 成功${ok}件 / 失敗${failures.length}件` });
      if (failures.length > 0) {
        setBulkReport({
          kind: '一括却下',
          ok,
          failures,
          finishedAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
          reason,
        });
      }
      setSelectedIds(new Set());
      setTimeout(() => { loadRows(); }, 2000);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBulkWorking(false);
    }
  }, [loadRows, pushToast]);

  const onBulkReject = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    askPrompt({
      title: '一括却下',
      label: '却下理由 (任意)',
      placeholder: `${ids.length}件の却下理由を記録できます`,
      primaryLabel: '一括却下',
      danger: true,
      onSubmit: (reason) => { executeBulkReject(ids, reason); },
    });
  }, [selectedIds, askPrompt, executeBulkReject]);

  // -------------------------------------------------------------------------
  // Keyboard shortcuts
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement;
      const tag = tgt.tagName?.toLowerCase();
      // skip when typing in an input
      if (tag === 'input' || tag === 'textarea' || tgt.isContentEditable) return;
      // skip if modifier keys (except shift for bulk)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 'Escape') {
        if (confirmDialog.open) setConfirmDialog((d) => ({ ...d, open: false }));
        else if (promptDialog.open) setPromptDialog((d) => ({ ...d, open: false }));
        else if (activePanelId) setActivePanelId(null);
        return;
      }

      if (e.key === 'j' || e.key === 'k' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = (e.key === 'j' || e.key === 'ArrowDown') ? 1 : -1;
        if (activePanelId) {
          navigatePanel(delta as -1 | 1);
        } else {
          // focused row navigation
          if (filtered.length === 0) return;
          const idx = focusedRowId ? filtered.findIndex((r) => r.wallet_txn_id === focusedRowId) : -1;
          const next = idx < 0 ? (delta > 0 ? 0 : filtered.length - 1) : Math.max(0, Math.min(filtered.length - 1, idx + delta));
          setFocusedRowId(filtered[next]?.wallet_txn_id || null);
        }
        return;
      }

      if (e.key === 'Enter' || e.key === ' ') {
        if (focusedRowId && !activePanelId) {
          e.preventDefault();
          const r = filtered.find((x) => x.wallet_txn_id === focusedRowId);
          if (r) handleRowClick(r);
        }
        return;
      }

      if (e.key === 'x') {
        if (focusedRowId) {
          e.preventDefault();
          toggleSelect(focusedRowId);
        }
        return;
      }

      if (e.key === '/') {
        e.preventDefault();
        const el = document.querySelector<HTMLInputElement>('input[type="search"], input[placeholder*="検索"]');
        if (el) el.focus();
        return;
      }

      // Chip hotkeys 1-6
      if (['1', '2', '3', '4', '5', '6'].includes(e.key)) {
        e.preventDefault();
        const idx = Number(e.key) - 1;
        const k = CHIP_DEFS[idx]?.key;
        if (k) setChipKey(k);
        return;
      }
    };
    document.addEventListener('keydown', handler);
    return () => { document.removeEventListener('keydown', handler); };
  }, [activePanelId, confirmDialog.open, promptDialog.open, focusedRowId, filtered, navigatePanel, handleRowClick, toggleSelect]);

  // -------------------------------------------------------------------------
  // Month options
  // -------------------------------------------------------------------------
  const monthOptions = useMemo(() => {
    const opts: { name: string; value: string }[] = [{ name: '全期間', value: '__all__' }];
    const start = { y: 2025, m: 10 };
    const now = new Date();
    const end = { y: now.getFullYear(), m: now.getMonth() + 1 };
    let y = start.y, m = start.m;
    while (y < end.y || (y === end.y && m <= end.m + 1)) {
      opts.push({ value: `${y}-${String(m).padStart(2, '0')}`, name: `${y}-${String(m).padStart(2, '0')}` });
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
    return opts;
  }, []);

  const walletableOptions = useMemo(() => {
    const opts: { name: string; value: string }[] = [{ name: '全口座', value: 'all' }];
    WALLETABLES.filter((w) => selectedWalletableType === 'all' || w.walletable_type === selectedWalletableType).forEach((w) => {
      opts.push({ value: String(w.walletable_id), name: `[${w.tier}] ${w.name}` });
    });
    return opts;
  }, [selectedWalletableType]);

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const renderSortArrow = (key: SortKey) => {
    if (sortKey !== key) return <span style={{ marginLeft: 4, color: '#d0cdc9', fontSize: '0.7rem' }}>↕</span>;
    return <span style={{ marginLeft: 4, color: '#2e7cf6', fontSize: '0.7rem', fontWeight: 700 }}>{sortDir === 'desc' ? '▼' : '▲'}</span>;
  };

  // 2026-07-11 監査 fix (③型): register-after-approve の per-row 失敗理由を行内に赤で表示
  const renderRegisterErrorNote = (wt: number, working: boolean) => {
    const regErr = registerErrors.get(wt);
    if (working || !regErr) return null;
    return (
      <span
        style={{ display: 'block', marginTop: '3px', fontSize: '0.625rem', color: '#c33939', fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-all', lineHeight: 1.3, maxWidth: '150px' }}
        title={regErr}
      >
        登録失敗: {regErr.length > 60 ? `${regErr.slice(0, 60)}…` : regErr}
      </span>
    );
  };

  const renderNextActionButton = (r: BankWalletTxnRow) => {
    const s = r.processing?.processing_status || 'pending';
    const wt = r.wallet_txn_id;
    const disabled = workingIds.has(wt);
    const freeeReg = r.status_raw === 2;

    if (s === 'registered' || (freeeReg && (!s || s === 'pending'))) {
      return r.processing?.freee_deal_id ? (
        <a
          href={`https://secure.freee.co.jp/deals#deal_id=${r.processing.freee_deal_id}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ ...S.badge('#e6f4ea', '#338650'), textDecoration: 'none', cursor: 'pointer' }}
        >
          登録済 ↗
        </a>
      ) : (
        <span style={{ fontSize: '0.75rem', color: '#338650', fontWeight: 600 }}>登録済 ↗</span>
      );
    }
    if (s === 'suggested' || s === 'needs_review') {
      return (
        <div style={{ display: 'inline-block' }}>
          <Button small appearance="primary" disabled={disabled} onClick={(e: any) => { e.stopPropagation(); onApprove(wt); }}>
            {disabled ? <InlineSpinner isLoading={true} /> : '承認 → 登録 ▸'}
          </Button>
          {renderRegisterErrorNote(wt, disabled)}
        </div>
      );
    }
    if (s === 'approved') {
      // 2026-07-11 監査 fix (②/③型): 旧表示「登録キュー中…」は実在しないキューを示す
      // 楽観表示だった (登録は UI click 起点の register-after-approve のみ)。
      // 表示は実 state のみから導出: 実行中 = spinner (workingIds) / 失敗 = 赤 + 理由
      // (registerErrors) / 成功 = reload 後の registered 実データ。
      return (
        <div style={{ display: 'inline-block' }}>
          <Button small appearance="primary" disabled={disabled} onClick={(e: any) => { e.stopPropagation(); onRegisterOnly(wt); }}>
            {disabled ? <InlineSpinner isLoading={true} /> : '登録を実行 ▸'}
          </Button>
          {renderRegisterErrorNote(wt, disabled)}
        </div>
      );
    }
    if (s === 'queued' || s === 'processing') {
      // 2026-07-13: jobs 経路 (ミニPC) で処理中であることを注記
      return (
        <span style={{ fontSize: '0.75rem', color: '#2e7cf6' }} title="ミニPC (jobs 経路) で分析処理中 — 完了すると自動反映されます">
          分析中 (ミニPC)…
        </span>
      );
    }
    if (s === 'error' && effectiveReconcileState(r.processing, r.reconcile_state) === 'reconcile_pending') {
      // 2026-09-14: freee deal 登録済・消込だけ cron 自動リトライ待ち。
      //   「再実行」は retry-register → register-after-approve が 409 already_registered になるだけで
      //   復旧手段にならないため出さない (代わりに freee の deal へのリンク + 状態を表示)。
      const stale = isReconcilePendingStale(r.processing?.last_updated);
      const dealId = r.processing?.freee_deal_id;
      return (
        <div style={{ display: 'inline-block' }}>
          {dealId ? (
            <a
              href={`https://secure.freee.co.jp/deals#deal_id=${dealId}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ ...S.badge('#e8f1fe', '#2e7cf6'), textDecoration: 'none', cursor: 'pointer' }}
            >
              freee 登録済 ↗
            </a>
          ) : null}
          <span
            style={{ display: 'block', marginTop: '3px', fontSize: '0.625rem', color: stale ? UNIFIED_COLOR.action_required.fg : '#2e7cf6', fontWeight: 600, whiteSpace: 'normal', lineHeight: 1.3, maxWidth: '150px' }}
          >
            {stale ? '自動消込が遅延 — freee で消込状態を確認' : '消込は自動リトライ中 (操作不要)'}
          </span>
        </div>
      );
    }
    if (s === 'error') {
      return (
        <div style={{ display: 'inline-block' }}>
          <Button small appearance="secondary" disabled={disabled} onClick={(e: any) => { e.stopPropagation(); onRetry(wt); }}>
            {disabled ? <InlineSpinner isLoading={true} /> : '再実行 ▸'}
          </Button>
          {renderRegisterErrorNote(wt, disabled)}
        </div>
      );
    }
    if (s === 'skipped') {
      return (
        <Button small appearance="tertiary" disabled={disabled} onClick={(e: any) => { e.stopPropagation(); onRerun(wt); }}>
          復元 ▸
        </Button>
      );
    }
    // 2026-08-24 罠A fix: mechanical_suggest_stage1 は pending/error 専用の enqueue-analysis
    //   では 409 already_in_progress になる (D1 パターン一致は既に済んでいるため)。
    //   strict flow (bank_auto_debit/bank_transfer_in) はさらに機械承認も RULE 31 で 409 になり
    //   デッドエンドだった (worker.js rerun-analysis コメント「罠A脱出経路」参照)。
    //   rerun-analysis は mechanical_suggest_stage1 を明示的に受理し force_ai=true で
    //   AI multi-reader レーンへ回す設計のため、ここでは onRerun (rerunBankAnalysis) を使う。
    if (s === 'mechanical_suggest_stage1') {
      return (
        <Button small appearance="secondary" disabled={disabled} onClick={(e: any) => { e.stopPropagation(); onRerun(wt); }}>
          {disabled ? <InlineSpinner isLoading={true} /> : '再分析 ▸'}
        </Button>
      );
    }
    // pending
    if (r.rule_matched) {
      return <span style={{ fontSize: '0.75rem', color: '#22b8cf' }}>freee 処理待ち</span>;
    }
    return (
      <Button small appearance="secondary" disabled={disabled} onClick={(e: any) => { e.stopPropagation(); onEnqueue(wt); }}>
        {disabled ? '…' : '分析依頼 ▸'}
      </Button>
    );
  };

  // Side panel render
  const renderSidePanel = () => {
    if (!activePanelId || !panelDetail) return null;
    const d = panelDetail;
    const proc = d.processing;
    const s = proc?.processing_status;
    const wtId = d.wallet_txn_id;
    const working = workingIds.has(wtId);
    const pipeline = classifyPipeline(d);
    // 2026-07-13: error 行の消込副状態 (reconcile_state) — panel でも区別表示
    const panelReconBadge = reconcileBadgeFor(d);
    const notReconciled = panelFreeeDeal?.not_reconciled;
    // 2026-08-20: 自社口座間振替はfreeeがdealを作らないため reconcile-freee-deals は
    // 常にunmatchedで終わる。バックエンドがこの署名を検知した場合は not_reconciled=false +
    // transfer_confirmed=true を返すので、誤った「未同期」警告の代わりに中立の案内を出す。
    const transferConfirmed = panelFreeeDeal?.transfer_confirmed;

    // 2026-07-14 部門接地 (A2): panel header/footer 用の部門未確定サマリ
    //   (proposed_deal_json は detail 応答にのみ含まれる — list 行では判定しない)
    const pdjPanel: any = proc?.proposed_deal_json;
    const detsPanel: any[] = Array.isArray(pdjPanel?.details) ? pdjPanel.details : [];
    const editsPanel = sectionEdits.get(wtId);
    const sectionEditable = s === 'suggested' || s === 'needs_review';
    const unresolvedPanel = sectionEditable ? unresolvedSectionIndexes(detsPanel, editsPanel) : [];

    // 2026-07-14: 根拠 evidence_sources (detail GET 同載)。null = 旧形式 → 判定理由 fallback。
    const panelEvidence = bankEvidenceOf(proc);

    // Next-action label for sticky footer
    let primaryFooter: React.ReactNode = null;
    if (s === 'suggested' || s === 'needs_review') {
      primaryFooter = (
        <VStack gap={0.25}>
          {unresolvedPanel.length > 0 && !sectionUnknownAcks.has(wtId) && (
            <span style={{ fontSize: '0.6875rem', color: UNIFIED_COLOR.action_required.fg, fontWeight: 600 }}>
              部門未確定 {unresolvedPanel.length}行 — 部門を指定するか「不明のまま登録」を選択すると承認できます
            </span>
          )}
          {/* 2026-09-19 W13: AI が確証を持てなかった (verdict=disagree) 行の明示 ack。
              AI の指摘そのもの (missing_evidence) を出したうえでチェックを求める。
              チェックしても、部門以外が未確定の行は worker 側で 409 のまま (安全側)。 */}
          {s === 'needs_review' && proc?.reconcile_verdict === 'disagree' && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
                padding: '0.5rem 0.625rem',
                borderRadius: 6,
                border: `1px solid ${UNIFIED_COLOR.action_required.bd}`,
                background: UNIFIED_COLOR.action_required.bg,
              }}
            >
              <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: UNIFIED_COLOR.action_required.fg }}>
                AI の指摘 — この点が確認できていません
              </span>
              <span style={{ fontSize: '0.6875rem', lineHeight: 1.5, color: '#444' }}>
                {String(proc?.missing_evidence || proc?.reconcile_judge_reason || 'AI は合議に至りませんでした (理由の記録なし)')}
              </span>
              {/* 2026-09-24: 人間承認で越えられる行 (部門だけ未確定) にのみ ack を出す。
                  越えられない行にチェックを出すと、チェックしても 409 になる袋小路になる
                  (wt 2422343797 incident)。 */}
              {disagreeUnlockState(pdjPanel, detsPanel).eligible ? (
                <label style={{ display: 'flex', gap: '0.375rem', alignItems: 'flex-start', fontSize: '0.6875rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={disagreeAcks.has(wtId)}
                    onChange={() => toggleDisagreeAck(wtId)}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    部門を指定したうえで承認する（この承認内容は自動学習しません。今後も同じ部門にする場合は
                    「この取引先は常にこの部門にする」にチェックしてください）
                  </span>
                </label>
              ) : (
                <span style={{ fontSize: '0.6875rem', lineHeight: 1.5, color: UNIFIED_COLOR.action_required.fg, fontWeight: 600 }}>
                  {disagreeUnlockState(pdjPanel, detsPanel).reason === 'no_section_gap'
                    ? 'この行は部門が確定済みで、AI の疑いは部門以外の項目です。人間承認では越えられません — 「再サジェスト」で再判定するか、「却下」して修正指示を出してください。'
                    : '取引先・勘定科目・税区分・金額のいずれかが未確定のため、人間承認では越えられません。「再サジェスト」か「却下」→ 修正指示で解消してください。'}
                </span>
              )}
            </div>
          )}
          <HStack gap={0.5}>
            <Button appearance="primary" disabled={working} onClick={() => onApprove(wtId)}>
              {working ? <InlineSpinner isLoading={true} /> : '承認 → 登録 ▸'}
            </Button>
            {proc?.flow_type === 'transfer' && (
              <Button appearance="secondary" disabled={working} onClick={() => onConfirmSelfTransfer(wtId)}>
                振替として確認
              </Button>
            )}
            <Button appearance="tertiary" disabled={working} onClick={() => onRerun(wtId)}>
              再サジェスト
            </Button>
            <Button appearance="secondary" danger disabled={working} onClick={() => onReject(wtId)}>
              却下
            </Button>
          </HStack>
        </VStack>
      );
    } else if (s === 'error' && effectiveReconcileState(proc, d.reconcile_state) === 'reconcile_pending') {
      // 2026-09-14: deal 登録済・消込だけ自動リトライ待ち → 再登録/再分析は出さず freee の deal を案内
      primaryFooter = proc?.freee_deal_id ? (
        <a
          href={`https://secure.freee.co.jp/deals#deal_id=${proc.freee_deal_id}`}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: '0.8125rem', color: '#2e7cf6', fontWeight: 600 }}
        >
          freee で deal #{proc.freee_deal_id} を開く ↗
        </a>
      ) : null;
    } else if (s === 'error') {
      primaryFooter = (
        <HStack gap={0.5}>
          <Button appearance="secondary" disabled={working} onClick={() => onRetry(wtId)}>再登録 ▸</Button>
          <Button appearance="tertiary" disabled={working} onClick={() => onRerun(wtId)}>再分析 ▸</Button>
        </HStack>
      );
    } else if (s === 'approved' && d.status_raw !== 2) {
      // 2026-07-11 監査 fix (②/③型): 承認済で登録が走らなかった行の復旧経路 + 実結果表示
      const panelRegErr = registerErrors.get(wtId);
      primaryFooter = (
        <VStack gap={0.25}>
          {!working && panelRegErr && (
            <span style={{ fontSize: '0.75rem', color: '#c33939', fontWeight: 600, wordBreak: 'break-all' }}>
              前回の登録失敗: {panelRegErr}
            </span>
          )}
          <Button appearance="primary" disabled={working} onClick={() => onRegisterOnly(wtId)}>
            {working ? <InlineSpinner isLoading={true} /> : '登録を実行 ▸ (freee 登録 + 消込)'}
          </Button>
        </VStack>
      );
    } else if ((!s || s === 'pending') && !d.rule_matched && d.status_raw !== 2) {
      primaryFooter = (
        <HStack gap={0.5}>
          <Button appearance="secondary" disabled={working} onClick={() => onEnqueue(wtId)}>
            {working ? <InlineSpinner isLoading={true} /> : '分析依頼 ▸'}
          </Button>
          {proc?.flow_type === 'transfer' && (
            <Button appearance="tertiary" disabled={working} onClick={() => onConfirmSelfTransfer(wtId)}>
              振替として確認
            </Button>
          )}
        </HStack>
      );
    } else if (s === 'skipped') {
      primaryFooter = (
        <Button appearance="tertiary" disabled={working} onClick={() => onRerun(wtId)}>復元 ▸</Button>
      );
    } else if (s === 'mechanical_suggest_stage1') {
      // 2026-08-24 罠A fix: strict flow (bank_auto_debit/bank_transfer_in) は上の
      // 「機械承認→自動freee登録」(学習根拠パネル内) が RULE 31 で 409 になる。
      // rerun-analysis は mechanical_suggest_stage1 を force_ai=true で AI へ回す唯一の脱出経路。
      primaryFooter = (
        <Button appearance="tertiary" disabled={working} onClick={() => onRerun(wtId)}>
          {working ? <InlineSpinner isLoading={true} /> : '再分析 ▸ (AI multi-reader へ)'}
        </Button>
      );
    }

    return (
      <div style={S.sidePanel}>
          {/* Header */}
          <div style={S.sidePanelHeader}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
              <HStack gap={0.25}>
                <span title="前の行 (K)">
                  <Button small appearance="tertiary" onClick={() => navigatePanel(-1)}>
                    ◀
                  </Button>
                </span>
                <span title="次の行 (J)">
                  <Button small appearance="tertiary" onClick={() => navigatePanel(1)}>
                    ▶
                  </Button>
                </span>
              </HStack>
              <span title="閉じる (Esc)">
                <Button small appearance="tertiary" onClick={() => setActivePanelId(null)}>
                  閉じる
                </Button>
              </span>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Text weight="bold" size={0.875}>
                {walletableName(d.walletable_id)}
              </Text>
              <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Text size={0.75} color="burnt">{formatDateMD(d.date)}</Text>
                <span
                  style={{
                    ...S.badge(d.entry_side === 'income' ? '#e6f4ea' : '#fde7e7', d.entry_side === 'income' ? '#338650' : '#c33939'),
                  }}
                >
                  {fmtSigned(d.amount, d.entry_side)}
                </span>
              </div>
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {panelReconBadge ? (
                  <>
                    <UnifiedChip b={panelReconBadge} />
                    <span style={{ fontSize: '0.6875rem', color: '#8a8580' }}>
                      {panelReconBadge.status === 'processing'
                        ? '消込は自動回復を待っています (freee 登録済・操作不要)'
                        : panelReconBadge.label === RECONCILE_STALE_LABEL
                          ? '自動消込が70分以上進んでいません — freee で消込状態を確認してください'
                          : 'freee 側と差額があります — 内容を確認して確定してください'}
                    </span>
                  </>
                ) : (
                  <StatusIcon type={pipelineToStatusType(pipeline)}>{pipeline.label}</StatusIcon>
                )}
                {/* 2026-07-14 部門接地 (A2): 部門未確定の明示バッジ (user 指定 or 明示 ack で解消) */}
                {unresolvedPanel.length > 0 && (
                  <UnifiedChip
                    b={{
                      status: 'action_required',
                      sub: 'judge',
                      label: `部門未確定 ${unresolvedPanel.length}行`,
                      ...UNIFIED_COLOR.action_required,
                    }}
                  />
                )}
              </div>
            </div>
            <div style={{ marginTop: '10px' }}>
              <TabBar
                tabs={['提案', '根拠', '履歴']}
                currentTabIndex={sidePanelTab}
                onClickTab={(i) => setSidePanelTab(i)}
                small
              />
            </div>
          </div>

          {/* Body */}
          <div style={S.sidePanelBody}>
            {/* TAB 0: 提案 */}
            {sidePanelTab === 0 && (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <SubSectionTitle headlineLevel={4}>取引内容</SubSectionTitle>
                  <div style={{ marginTop: '6px', fontSize: '0.8125rem', color: '#252525' }}>
                    {d.description || '—'}
                  </div>
                </div>

                {/* 摘要空白 pattern 分類 (2026-04-25 追加) — 過去 freee 分析 dropdown + コメント分析依頼 */}
                {(!d.description || d.description.trim() === '') && s !== 'registered' && d.status_raw !== 2 && (
                  <EmptyDescPatternPicker
                    walletTxnId={d.wallet_txn_id}
                    walletableId={d.walletable_id}
                    amount={d.amount}
                    date={d.date}
                    entrySide={d.entry_side as 'income' | 'expense'}
                  />
                )}

                {/* 証憑添付 (= 2026-05-08, schema-v33+ / 2026-07-14 確定設計 §3: ARICA候補 + ファイルボックスNo + vision) — 全ステータスで表示 */}
                <ReceiptAttachWidget
                  walletTxnId={d.wallet_txn_id}
                  txnDate={d.date}
                  proposedDealJson={proc?.proposed_deal_json || null}
                  freeeDealId={proc?.freee_deal_id || panelFreeeDeal?.freee_deal_id || null}
                  isRegistered={s === 'registered' || d.status_raw === 2}
                  visionFromDetail={proc?.receipt_vision ?? (d as any).receipt_vision ?? null}
                  onAttached={() => loadRows()}
                />


                {/* ---- freee 仕訳 (registered) ---- */}
                {(s === 'registered' || d.status_raw === 2) && panelFreeeDeal?.deal && (
                  <div style={{ marginBottom: '12px' }}>
                    <SubSectionTitle headlineLevel={4}>freee 仕訳</SubSectionTitle>
                    <div style={{ fontSize: '0.75rem', color: '#6b6560', marginBottom: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>
                      {panelFreeeDeal.freee_deal_id && <span>Deal ID: <b style={{ color: '#252525', fontFamily: 'monospace' }}>#{panelFreeeDeal.freee_deal_id}</b></span>}
                      <span>取引先: <b style={{ color: '#252525' }}>{panelFreeeDeal.partner_name || panelFreeeDeal.deal.partnerName || (panelFreeeDeal.partner_id ? `#${panelFreeeDeal.partner_id}` : '未設定')}</b></span>
                      {panelFreeeDeal.deal.type && <span>種別: <b style={{ color: '#252525' }}>{panelFreeeDeal.deal.type === 'income' ? '収入' : '支出'}</b></span>}
                      {panelFreeeDeal.deal.status && <span>状態: <b style={{ color: '#252525' }}>{panelFreeeDeal.deal.status === 'settled' ? '消込済' : '未消込'}</b></span>}
                      {panelFreeeDeal.deal.issue_date && <span>発生日: <b style={{ color: '#252525' }}>{panelFreeeDeal.deal.issue_date}</b></span>}
                    </div>
                    {/* details */}
                    {(panelFreeeDeal.deal.details && panelFreeeDeal.deal.details.length > 0) && (
                      <ColumnBase paddingSize="zero" border="default" rounded>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                          <thead>
                            <tr>
                              <th style={S.detailTh}>借/貸</th>
                              <th style={S.detailTh}>勘定科目</th>
                              <th style={{ ...S.detailTh, textAlign: 'right' }}>金額</th>
                              <th style={S.detailTh}>税区分</th>
                              <th style={S.detailTh}>部門</th>
                              <th style={S.detailTh}>品目</th>
                              <th style={S.detailTh}>タグ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {panelFreeeDeal.deal.details.map((dd: any, i: number) => (
                              <tr key={`det-${i}`}>
                                <td style={S.detailTd}>{dd.entry_side === 'credit' ? '貸方' : '借方'}</td>
                                <td style={S.detailTd}>{dd.accountName || `#${dd.account_item_id}`}</td>
                                <td style={{ ...S.detailTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(dd.amount)}</td>
                                <td style={S.detailTd}>{dd.taxCodeName || dd.tax_code}</td>
                                <td style={S.detailTd}>{dd.sectionName || '—'}</td>
                                <td style={S.detailTd}>{dd.itemName || '—'}</td>
                                <td style={S.detailTd}>
                                  {Array.isArray(dd.tagNames) && dd.tagNames.length > 0
                                    ? dd.tagNames.map((n: string, ti: number) => (
                                        <span key={ti} style={{ ...S.badge('#e8f0fe', '#1a5cc7'), marginRight: '3px' }}>{n}</span>
                                      ))
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </ColumnBase>
                    )}
                    {/* accruals */}
                    {Array.isArray(panelFreeeDeal.deal.accruals) && panelFreeeDeal.deal.accruals.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginBottom: '4px', fontWeight: 600 }}>
                          未消込 ({panelFreeeDeal.deal.accruals.length}件)
                        </div>
                        <ColumnBase paddingSize="zero" border="default" rounded>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                            <tbody>
                              {panelFreeeDeal.deal.accruals.map((dd: any, i: number) => (
                                <tr key={`acc-${i}`} style={{ background: '#fafafa', color: '#8a8580' }}>
                                  <td style={S.detailTd}>{dd.entry_side === 'credit' ? '貸方' : '借方'}</td>
                                  <td style={S.detailTd}>{dd.accountName || `#${dd.account_item_id}`}</td>
                                  <td style={{ ...S.detailTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(dd.amount)}</td>
                                  <td style={S.detailTd}>{dd.taxCodeName || dd.tax_code}</td>
                                  <td style={S.detailTd}>{dd.sectionName || '—'}</td>
                                  <td style={S.detailTd}>{dd.itemName || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </ColumnBase>
                      </div>
                    )}
                    {/* payments */}
                    {Array.isArray(panelFreeeDeal.deal.payments) && panelFreeeDeal.deal.payments.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginBottom: '4px', fontWeight: 600 }}>決済</div>
                        {panelFreeeDeal.deal.payments.map((p: any, i: number) => (
                          <div key={i} style={{ fontSize: '0.75rem', color: '#252525' }}>
                            {p.date} / {walletableName(p.from_walletable_id)} / {fmt(p.amount)}
                          </div>
                        ))}
                      </div>
                    )}
                    {panelFreeeDeal.external_link && (
                      <div style={{ marginTop: '10px' }}>
                        <a
                          href={panelFreeeDeal.external_link}
                          target="_blank"
                          rel="noreferrer"
                          style={{ ...S.badge('#e6f4ea', '#338650'), textDecoration: 'none' }}
                        >
                          freee で開く ↗ (deal_id: {panelFreeeDeal.freee_deal_id})
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* ---- 口座振替として処理済み (2026-08-20) ---- */}
                {(s === 'registered' || d.status_raw === 2) && transferConfirmed && (
                  <MessageBlock info mb={0.5}>
                    <VStack gap={0.25}>
                      <Text weight="bold">口座振替として処理済み</Text>
                      <Text size={0.75}>
                        {panelFreeeDeal?.transfer_note || '口座振替として処理済みです(freeeは振替にdeal IDを発行しないため対応するdealはありません)。'}
                      </Text>
                    </VStack>
                  </MessageBlock>
                )}

                {/* ---- 仕訳データ未同期 (Bug-1 fix) ---- */}
                {(s === 'registered' || d.status_raw === 2) && notReconciled && !transferConfirmed && (
                  <MessageBlock notice mb={0.5}>
                    <VStack gap={0.25}>
                      <Text weight="bold">仕訳データ未同期</Text>
                      <Text size={0.75}>
                        freee 側では登録済ですが、FREEEMAN 側にまだ仕訳が取り込まれていません。
                        下の「Reconcile 実行」ボタン押下で、現在の walletable の同期処理が開始できます
                        (手動トリガー、Claude には自動依頼されません)。
                      </Text>
                      <Note>
                        hint: {panelFreeeDeal?.hint || '不明'}
                      </Note>
                      <HStack gap={0.25}>
                        <Button
                          small
                          appearance="secondary"
                          onClick={() => {
                            pushToast({
                              kind: 'info',
                              text: `Reconcile は user (keiri) セッションで POST /api/bank-wallet-txns/reconcile-freee-deals に body ${JSON.stringify({ walletable_id: d.walletable_id, start_date: d.date, end_date: d.date })} + X-Freee-Token で手動実行してください。自発呼出は絶対ルールで禁止です。`,
                            });
                          }}
                        >
                          Reconcile 手順を表示
                        </Button>
                        <Button
                          small
                          appearance="tertiary"
                          onClick={() => {
                            // re-fetch only (not actually triggering reconcile)
                            setActivePanelId(null);
                            setTimeout(() => setActivePanelId(wtId), 150);
                          }}
                        >
                          再取得
                        </Button>
                      </HStack>
                    </VStack>
                  </MessageBlock>
                )}

                {/* ---- error ---- */}
                {(s === 'registered' || d.status_raw === 2) && !panelFreeeDeal && panelFreeeDealError && !notReconciled && (
                  <MessageBlock error mb={0.5}>
                    <VStack gap={0.25}>
                      <Text weight="bold">仕訳取得エラー</Text>
                      <Text size={0.75}>{panelFreeeDealError}</Text>
                    </VStack>
                  </MessageBlock>
                )}

                {/* ---- 提案仕訳 (2026-06 fix: entry_side 振り分け / マスタ名称解決 / 純額=Σcr−Σdr) ---- */}
                {(s === 'suggested' || s === 'needs_review' || s === 'mechanical_suggest_stage1') && proc?.proposed_deal_json && (() => {
                  const pdj = proc.proposed_deal_json;
                  const dets: any[] = Array.isArray(pdj.details) ? pdj.details : [];
                  // 借方=debit / 貸方=credit で集計。純額=Σcredit−Σdebit=入金額。
                  let sumCredit = 0;
                  let sumDebit = 0;
                  for (const dd of dets) {
                    const amt = Number(dd.amount) || 0;
                    if (dd.entry_side === 'credit') sumCredit += amt;
                    else sumDebit += amt;
                  }
                  const net = sumCredit - sumDebit;
                  // 取引先: proposed_deal_json.partner_id → proc.partner_id を採用し partnersMap で名称解決。
                  //   解決順: freee マスタ名 (partnersMap) > worker 渡しの proc.partner_name > #id fallback。
                  const pdjPartnerId = pdj.partner_id != null ? Number(pdj.partner_id) : null;
                  const partnerIdShown = pdjPartnerId ?? proc.partner_id ?? null;
                  const partnerLabel =
                    (partnerIdShown != null ? partnersMap.get(Number(partnerIdShown)) : undefined)
                    || proc.partner_name
                    || (partnerIdShown != null ? `#${partnerIdShown}` : '未設定');
                  const resolveAccount = (dd: any) =>
                    dd.accountName || dd.account_item_name
                    || (dd.account_item_id != null ? accountItemsMap.get(Number(dd.account_item_id)) : undefined)
                    || (dd.account_item_id != null ? `#${dd.account_item_id}` : '—');
                  const resolveSection = (dd: any) =>
                    dd.sectionName || dd.section_name
                    || (dd.section_id != null ? sectionsMap.get(Number(dd.section_id)) : undefined)
                    || (dd.section_id != null ? `#${dd.section_id}` : '—');
                  const resolveTax = (dd: any) =>
                    dd.taxCodeName || dd.tax_code_name
                    || (dd.tax_code != null ? taxCodesMap.get(Number(dd.tax_code)) : undefined)
                    || (dd.tax_code != null ? String(dd.tax_code) : '—');
                  // 品目 (item_id): null/未設定 は空表示 (ALSOK は item_id=null)。
                  const resolveItem = (dd: any) => {
                    if (dd.itemName || dd.item_name) return dd.itemName || dd.item_name;
                    if (dd.item_id == null) return '';
                    return itemsMap.get(Number(dd.item_id)) || `#${dd.item_id}`;
                  };
                  // メモタグ (tag_ids[]): 空配列は空表示 (ALSOK は tag_ids=[])。
                  const resolveTags = (dd: any) => {
                    const ids: any[] = Array.isArray(dd.tag_ids) ? dd.tag_ids : [];
                    if (!ids.length) return '';
                    return ids
                      .map((tid) => tagsMap.get(Number(tid)) || `#${tid}`)
                      .join(', ');
                  };
                  // ---- 2026-07-14 部門接地 (確定設計 v1 A1/A2) ----
                  //   editable = user が部門を指定して確定できる状態 (approve endpoint が
                  //   modifications.details を受ける経路)。mechanical_suggest_stage1 は別 endpoint
                  //   (機械承認・modifications 非対応) のため表示のみ。
                  const editable = s === 'suggested' || s === 'needs_review';
                  const editsCur = sectionEdits.get(wtId);
                  const unresolvedIdx = unresolvedSectionIndexes(dets, editsCur);
                  const ackedCur = sectionUnknownAcks.has(wtId);
                  const effSecs = effectiveSectionIds(dets, editsCur);
                  const vendorKey =
                    (partnerIdShown != null ? partnersMap.get(Number(partnerIdShown)) : undefined)
                    || proc.partner_name || null;
                  const canLearnVendor = editable && !!vendorKey && effSecs.length === 1;
                  const sectionUnknownBadge = (dd: any) => (
                    <span
                      style={{
                        ...S.badge(UNIFIED_COLOR.action_required.bg, UNIFIED_COLOR.action_required.fg),
                        border: `1px solid ${UNIFIED_COLOR.action_required.bd}`,
                      }}
                      title={dd?.section_unknown_reason
                        ? `理由: ${String(dd.section_unknown_reason)}`
                        : '部門の根拠が見つからないため未確定です (推測での自動補填はしません)'}
                    >
                      部門未確定
                    </span>
                  );
                  // ---- 2026-08-14: 口座振替 proposal (type='transfer') ----
                  //   freee 上は deal ではなく 口座振替 なので details[] を持たない。
                  //   旧実装は details[] 前提のテーブルだけを描いていたため、承認対象の中身
                  //   (振替先・金額) が画面に一切出なかった (wt 2383168280 役員立替金精算)。
                  //   取引先/勘定科目/部門は振替に存在しない概念なので専用ブロックで描く。
                  if (String(pdj.type || '') === 'transfer') {
                    const fromLabel = pdj.from_walletable_name
                      || (pdj.from_walletable_id != null ? `#${pdj.from_walletable_id}` : '—');
                    const toLabel = pdj.to_walletable_name
                      || (pdj.to_walletable_id != null ? `#${pdj.to_walletable_id}` : '—');
                    const transferRow = (label: string, value: React.ReactNode) => (
                      <tr>
                        <td style={{ ...S.detailTd, width: '28%', color: '#6b6560' }}>{label}</td>
                        <td style={{ ...S.detailTd, wordBreak: 'break-word' }}>{value}</td>
                      </tr>
                    );
                    return (
                      <div style={{ marginBottom: '12px' }}>
                        <SubSectionTitle headlineLevel={4}>提案 (口座振替)</SubSectionTitle>
                        <div style={{ fontSize: '0.75rem', color: '#6b6560', marginBottom: '6px' }}>
                          自社口座間の資金移動として登録します (freee 取引ではなく <b style={{ color: '#252525' }}>口座振替</b>)。
                          取引先・勘定科目・部門は振替に存在しない概念のため設定しません。
                        </div>
                        <ColumnBase paddingSize="zero" border="default" rounded>
                          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <tbody>
                              {transferRow('振替元', <b style={{ color: '#252525' }}>{fromLabel}</b>)}
                              {transferRow('振替先', <b style={{ color: '#252525' }}>{toLabel}</b>)}
                              {transferRow('金額', (
                                <b style={{ color: '#252525', fontVariantNumeric: 'tabular-nums' }}>
                                  {fmt(Number(pdj.amount) || 0)}
                                </b>
                              ))}
                              {transferRow('日付', pdj.date || pdj.issue_date || '—')}
                              {transferRow('摘要', pdj.description || '—')}
                            </tbody>
                          </table>
                        </ColumnBase>
                        <div style={{ marginTop: '6px', fontSize: '0.6875rem', color: '#8a8580' }}>
                          「承認 → 登録 ▸」で freee に口座振替を登録し、この明細を消込みます
                          {proc.reconcile_verdict ? ` / verdict: ${proc.reconcile_verdict}` : ''}
                        </div>
                      </div>
                    );
                  }
                  return (
                  <div style={{ marginBottom: '12px' }}>
                    <SubSectionTitle headlineLevel={4}>提案仕訳</SubSectionTitle>
                    <div style={{ fontSize: '0.75rem', color: '#6b6560', marginBottom: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>
                      <span>取引先: <b style={{ color: '#252525' }}>{partnerLabel}</b>{partnerIdShown != null ? ` (pid:${partnerIdShown})` : ''}</span>
                      {pdj.type && <span>種別: <b style={{ color: '#252525' }}>{pdj.type === 'income' ? '収入' : '支出'}</b></span>}
                      {pdj.issue_date && <span>発生日: <b style={{ color: '#252525' }}>{pdj.issue_date}</b></span>}
                    </div>
                    <ColumnBase paddingSize="zero" border="default" rounded>
                      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                          <tr>
                            <th style={{ ...S.detailTh, width: '8%' }}>借/貸</th>
                            <th style={{ ...S.detailTh, width: '17%' }}>勘定科目</th>
                            <th style={{ ...S.detailTh, textAlign: 'right', width: '13%' }}>金額</th>
                            <th style={{ ...S.detailTh, width: '13%' }}>税区分</th>
                            <th style={{ ...S.detailTh, width: '11%' }}>部門</th>
                            <th style={{ ...S.detailTh, width: '12%' }}>品目</th>
                            <th style={{ ...S.detailTh, width: '12%' }}>メモタグ</th>
                            <th style={{ ...S.detailTh, width: '14%' }}>摘要</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dets.map((dd: any, i: number) => {
                            const isCredit = dd.entry_side === 'credit';
                            const itemName = resolveItem(dd);
                            const tagNames = resolveTags(dd);
                            const desc = dd.description != null ? String(dd.description) : '';
                            return (
                              <tr key={i}>
                                <td style={S.detailTd}>
                                  <span style={S.badge(isCredit ? '#e6f4ea' : '#fde7e7', isCredit ? '#338650' : '#c33939')}>
                                    {isCredit ? '貸方' : '借方'}
                                  </span>
                                </td>
                                <td style={{ ...S.detailTd, wordBreak: 'break-word' }}>{resolveAccount(dd)}</td>
                                <td style={{ ...S.detailTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: isCredit ? '#338650' : '#c33939' }}>{fmt(Number(dd.amount) || 0)}</td>
                                <td style={{ ...S.detailTd, wordBreak: 'break-word' }}>{resolveTax(dd)}</td>
                                <td style={{ ...S.detailTd, wordBreak: 'break-word' }}>
                                  {editable ? (() => {
                                    // A1: 部門編集ドロップダウン (指定は承認時に modifications.details で送信)
                                    const edited = editsCur ? editsCur.get(i) : undefined;
                                    const unknown = isDetailSectionUnknown(dd) && edited == null;
                                    const selValue = edited != null
                                      ? String(edited)
                                      : (dd.section_id != null ? String(dd.section_id) : '');
                                    const changedMark = edited != null
                                      && !(dd.section_id != null && Number(dd.section_id) === edited);
                                    return (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
                                        {unknown && sectionUnknownBadge(dd)}
                                        <select
                                          value={selValue}
                                          onChange={(e) => setSectionEdit(wtId, i, e.target.value === '' ? null : Number(e.target.value))}
                                          aria-label={`明細${i + 1} の部門`}
                                          style={{
                                            width: '100%',
                                            padding: '3px 4px',
                                            border: `1px solid ${unknown ? UNIFIED_COLOR.action_required.bd : '#dbd9d7'}`,
                                            borderRadius: '4px',
                                            fontSize: '0.6875rem',
                                            background: '#fff',
                                          }}
                                        >
                                          <option value="">{dd.section_id != null ? '(提案値に戻す)' : '(部門を選択)'}</option>
                                          {sectionOptions.map(([sid, name]) => (
                                            <option key={sid} value={String(sid)}>{name}</option>
                                          ))}
                                        </select>
                                        {changedMark && (
                                          <span style={{ fontSize: '0.625rem', color: '#1a5cc7', fontWeight: 600 }}>
                                            指定済 — 承認時に反映+学習
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })() : (
                                    // 表示のみ (mechanical_suggest_stage1 等): 不明は不明のまま明示
                                    isDetailSectionUnknown(dd) ? sectionUnknownBadge(dd) : resolveSection(dd)
                                  )}
                                </td>
                                <td style={{ ...S.detailTd, wordBreak: 'break-word', color: itemName ? '#252525' : '#b8b3ae' }}>{itemName || '—'}</td>
                                <td style={{ ...S.detailTd, wordBreak: 'break-word', color: tagNames ? '#252525' : '#b8b3ae' }}>{tagNames || '—'}</td>
                                <td style={{ ...S.detailTd, wordBreak: 'break-word', color: desc ? '#252525' : '#b8b3ae' }}>{desc || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#f7f5f5' }}>
                            <td style={{ ...S.detailTd, fontWeight: 700, borderBottom: 'none' }} colSpan={2}>
                              純額 (貸方 − 借方)
                            </td>
                            <td style={{ ...S.detailTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, borderBottom: 'none', color: net >= 0 ? '#338650' : '#c33939' }}>
                              {fmt(net)}
                            </td>
                            <td style={{ ...S.detailTd, borderBottom: 'none' }} colSpan={5}>
                              <span style={{ fontSize: '0.625rem', color: '#8a8580' }}>
                                貸 {fmt(sumCredit)} / 借 {fmt(sumDebit)}
                              </span>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </ColumnBase>
                    {/* ---- 2026-07-14 部門接地 (A2): 不明の明示 + 「不明のまま登録」明示 ack ---- */}
                    {editable && unresolvedIdx.length > 0 && (
                      <div
                        style={{
                          marginTop: '8px',
                          padding: '10px 12px',
                          background: UNIFIED_COLOR.action_required.bg,
                          border: `1px solid ${UNIFIED_COLOR.action_required.bd}`,
                          borderRadius: '6px',
                        }}
                      >
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: UNIFIED_COLOR.action_required.fg }}>
                          部門未確定 {unresolvedIdx.length}行 — 実データに部門の根拠が見つかりません
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: '#6b6560', marginTop: '4px' }}>
                          不明は不明のまま扱います (推測での自動補填はしません)。上の表の「部門」列で指定するか、
                          不明のまま登録することを明示してください。
                        </div>
                        <div style={{ marginTop: '6px' }}>
                          <CheckBox small checked={ackedCur} onChange={() => toggleSectionUnknownAck(wtId)}>
                            部門は不明のまま登録する (freee には部門なしで登録・監査記録に残ります)
                          </CheckBox>
                        </div>
                      </div>
                    )}
                    {/* A1: 部門修正のサマリ (承認時に modifications.details 送信 → 学習) */}
                    {editable && editsCur && editsCur.size > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '0.6875rem', color: '#1a5cc7' }}>
                        部門修正 {editsCur.size}行 — 「承認 → 登録 ▸」で反映され、修正内容は学習されます (今後の同種取引の提案に活用)。
                      </div>
                    )}
                    {/* A2: 「この業者は常にこの部門」→ 承認成功後に POST /api/learn kind=vendor_default_section */}
                    {canLearnVendor && (
                      <div style={{ marginTop: '6px' }}>
                        <CheckBox
                          small
                          checked={learnVendorChecks.has(wtId)}
                          onChange={() => toggleLearnVendorCheck(wtId)}
                        >
                          この取引先は常にこの部門にする — 「{vendorKey} → {sectionsMap.get(effSecs[0]) || `#${effSecs[0]}`}」を承認時に学習
                        </CheckBox>
                      </div>
                    )}
                    {proc.pattern_confidence != null && (
                      <div style={{ marginTop: '8px', fontSize: '0.6875rem', color: '#8a8580' }}>
                        信頼度:{' '}
                        <b style={{ color: proc.pattern_confidence >= 0.92 ? '#338650' : proc.pattern_confidence >= 0.75 ? '#c67320' : '#c33939' }}>
                          {Math.round(proc.pattern_confidence * 100)}%
                        </b>{' '}
                        / source: {proc.pattern_source || '—'} / verdict: {proc.reconcile_verdict || '—'}
                      </div>
                    )}
                  </div>
                  );
                })()}

                {/* ---- 根拠 (2026-07-14 evidence_sources 新契約) ----
                     構造化根拠 (情報源バッジ + ja_summary + 一致内容) が主役。
                     source_type='none' は「探したが見つからなかったもの」+ 学習導線。
                     旧形式 (evidence_sources 無し) は reconcile_judge_reason をそのまま表示。 */}
                <EvidenceSourcesSection
                  sources={panelEvidence}
                  judgeReason={proc?.reconcile_judge_reason}
                />

                {/* 修正指示 (= 2026-07-14 確定設計 §2: bank_correction_instructions) */}
                <BankCorrectionPanel
                  walletTxnId={wtId}
                  processingStatus={s || 'pending'}
                  isRegistered={s === 'registered' || d.status_raw === 2}
                  ensureSession={ensureSession}
                  onToast={pushToast}
                  onSubmitted={() => loadRows()}
                />

                {(!s || s === 'pending') && !d.rule_matched && d.status_raw !== 2 && (
                  <MessageBlock info mb={0.5}>
                    サジェスト未作成。下の「分析依頼 ▸」を押すと Claude チームに分析依頼されます。
                  </MessageBlock>
                )}

                {(!s || s === 'pending') && d.rule_matched && d.status_raw !== 2 && (
                  <MessageBlock notice mb={0.5}>
                    freee 自動登録ルール命中。freee 側が非同期で反映します。
                  </MessageBlock>
                )}

                {s === 'error' && (
                  <MessageBlock error mb={0.5}>
                    {proc?.error_message || 'エラー'}
                  </MessageBlock>
                )}
              </div>
            )}

            {/* TAB 1: 根拠 */}
            {sidePanelTab === 1 && (
              <div style={{ padding: '16px 20px' }}>
                {/* 2026-07-14: 構造化根拠 (evidence_sources) を根拠タブでも主役として先頭表示 */}
                <EvidenceSourcesSection
                  sources={panelEvidence}
                  judgeReason={proc?.reconcile_judge_reason}
                />

                {/* 学習根拠 (= schema-v33 / 2026-05-08): bank_pattern_index hit 状況を可視化 */}
                {panelLearning && (
                  <div style={{ marginBottom: '16px' }}>
                    <SubSectionTitle headlineLevel={4}>学習根拠 (bank_pattern_index)</SubSectionTitle>
                    {panelLearning.matched_pattern ? (
                      <div style={{ marginTop: '8px', padding: '12px', background: '#f0f4ff', borderRadius: '6px', borderLeft: '3px solid #7c5cff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#5b3fc7' }}>
                            🎯 パターンHit (D1 lookup)
                          </span>
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: panelLearning.matched_pattern.ai_confidence >= 0.85 ? '#338650' : '#dd7a00' }}>
                            信頼度 {Math.round(panelLearning.matched_pattern.ai_confidence * 100)}%
                          </span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#252525' }}>
                          token: <code style={{ background: '#fff', padding: '1px 4px', borderRadius: '2px' }}>{panelLearning.matched_pattern.description_token_key}</code>
                          {' / '}
                          {panelLearning.matched_pattern.entry_side} / {panelLearning.matched_pattern.amount_band}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#252525', marginTop: '4px' }}>
                          推定取引先: <strong>{panelLearning.matched_pattern.best_partner_name || '—'}</strong>
                          {panelLearning.matched_pattern.best_partner_id && ` (pid:${panelLearning.matched_pattern.best_partner_id})`}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b6560', marginTop: '4px' }}>
                          サンプル {panelLearning.matched_pattern.sample_count}件
                          (承認 {panelLearning.matched_pattern.approved_count}
                          {panelLearning.matched_pattern.rejected_count > 0 && ` / 拒否 ${panelLearning.matched_pattern.rejected_count}`}
                          {panelLearning.matched_pattern.modified_count > 0 && ` / 修正 ${panelLearning.matched_pattern.modified_count}`})
                          {panelLearning.matched_pattern.last_confirmed_at && ` / 最終 ${panelLearning.matched_pattern.last_confirmed_at}`}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginTop: '4px' }}>
                          source: {panelLearning.matched_pattern.pattern_source}
                        </div>
                        {/* Stage 1 hit + proposed_deal_json 生成済 → 1-click 承認 button
                            2026-09-25 B案: STRICT flow (カード入金/自動引落) は worker の RULE 31 で
                            必ず 409 (rule_31_strict_flow_type) になるため button 自体を出さない。
                            新規行は dispatch 側で AI へ回るので通常ここには来ないが、
                            例外時代に着地した旧行が残っている場合の袋小路を UI でも塞ぐ。 */}
                        {proc?.processing_status === 'mechanical_suggest_stage1' && proc?.proposed_deal_json && (
                          STRICT_FLOW_TYPES_UI.has(proc?.flow_type || '') ? (
                            <div style={{ marginTop: '10px', fontSize: '0.6875rem', color: '#8a8580', lineHeight: 1.6 }}>
                              カード入金・自動引落は AI 複数チェック必須 (RULE 31) のため機械承認は使えません。
                              下の「再分析 ▸ (AI multi-reader へ)」から進めてください。
                            </div>
                          ) : (
                            <div style={{ marginTop: '10px' }}>
                              <Button
                                appearance="primary"
                                small
                                disabled={working}
                                onClick={() => onMechanicalApprove(wtId)}
                              >
                                {working ? <InlineSpinner isLoading={true} /> : '機械承認 → 自動 freee 登録 ▸'}
                              </Button>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <div style={{ marginTop: '8px', padding: '12px', background: '#f7f5f5', borderRadius: '6px', fontSize: '0.75rem', color: '#8a8580' }}>
                        該当パターン無 (token: {panelLearning.lookup.token_candidates.join(', ') || 'なし'} / {panelLearning.lookup.entry_side} / {panelLearning.lookup.amount_band})
                      </div>
                    )}

                    {/* 過去類似履歴 */}
                    {panelLearning.similar_history.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b6560', fontWeight: 500, marginBottom: '4px' }}>
                          過去類似履歴 ({panelLearning.similar_history.length}件)
                        </div>
                        <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #e5e4e2', borderRadius: '6px' }}>
                          {panelLearning.similar_history.map((h, i) => (
                            <div key={i} style={{ padding: '6px 10px', fontSize: '0.6875rem', borderBottom: i < panelLearning.similar_history.length - 1 ? '1px solid #f0eeec' : 'none' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{h.date}</span>
                                <span style={{ color: '#338650' }}>{h.amount.toLocaleString()}円</span>
                              </div>
                              <div style={{ color: '#6b6560', marginTop: '2px' }}>
                                {h.partner_name || '—'} {h.freee_deal_id && `→ #${h.freee_deal_id}`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Stage decisions */}
                    {panelLearning.stage_decisions.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b6560', fontWeight: 500, marginBottom: '4px' }}>
                          ステージ判定 ({panelLearning.stage_decisions.length}件)
                        </div>
                        <div style={{ border: '1px solid #e5e4e2', borderRadius: '6px' }}>
                          {panelLearning.stage_decisions.map((s, i) => (
                            <div key={i} style={{ padding: '6px 10px', fontSize: '0.6875rem', borderBottom: i < panelLearning.stage_decisions.length - 1 ? '1px solid #f0eeec' : 'none' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span><strong>Stage {s.stage_reached}</strong> → {s.decision}</span>
                                <span style={{ color: '#8a8580' }}>{s.confidence != null && `${Math.round(s.confidence * 100)}%`}</span>
                              </div>
                              {s.reason && <div style={{ color: '#6b6560', marginTop: '2px' }}>{s.reason}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <SubSectionTitle headlineLevel={4}>メタデータ</SubSectionTitle>
                <VStack gap={0.25}>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>wallet_txn_id</span>
                    <span style={S.metaValue}>{d.wallet_txn_id}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>ref_number</span>
                    <span style={{ ...S.metaValue, fontFamily: 'monospace' }}>{proc?.ref_number || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>flow_type</span>
                    <span style={S.metaValue}>{proc?.flow_type || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>partner</span>
                    <span style={S.metaValue}>{proc?.partner_name || '—'} (pid:{proc?.partner_id || '—'})</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>pattern_source</span>
                    <span style={S.metaValue}>{proc?.pattern_source || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>pattern_confidence</span>
                    <span style={S.metaValue}>
                      {proc?.pattern_confidence != null ? `${Math.round(proc.pattern_confidence * 100)}%` : '—'}
                    </span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>readers_passed</span>
                    <span style={S.metaValue}>{proc?.readers_passed ?? '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>reconcile_verdict</span>
                    <span style={S.metaValue}>{proc?.reconcile_verdict || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>updated_at</span>
                    <span style={S.metaValue}>{proc?.last_updated || d.last_changed_at || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>fetched_at</span>
                    <span style={S.metaValue}>{d.synced_at || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>registered_at</span>
                    <span style={S.metaValue}>{proc?.registered_at || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>session_id</span>
                    <span style={S.metaValue}>{proc?.session_id || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>instruction_id</span>
                    <span style={S.metaValue}>{proc?.instruction_id || '—'}</span>
                  </div>
                </VStack>

                {d.auto_register_rule && (
                  <div style={{ marginTop: '12px' }}>
                    <SubSectionTitle headlineLevel={4}>auto_register_rule_cache</SubSectionTitle>
                    <pre style={{ padding: '10px 12px', background: '#f7f5f5', borderRadius: '6px', fontSize: '0.6875rem', color: '#6b6560', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                      {JSON.stringify(d.auto_register_rule, null, 2)}
                    </pre>
                  </div>
                )}

                {proc?.audit_summary && (
                  <div style={{ marginTop: '12px' }}>
                    <SubSectionTitle headlineLevel={4}>audit_summary</SubSectionTitle>
                    <pre style={{ padding: '10px 12px', background: '#f7f5f5', borderRadius: '6px', fontSize: '0.6875rem', color: '#6b6560', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                      {JSON.stringify(proc.audit_summary, null, 2)}
                    </pre>
                  </div>
                )}

                {proc?.missing_evidence && (
                  <div style={{ marginTop: '12px' }}>
                    <SubSectionTitle headlineLevel={4}>不足根拠</SubSectionTitle>
                    <pre style={{ padding: '10px 12px', background: '#fff4e5', borderRadius: '6px', fontSize: '0.6875rem', color: '#c67320', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(proc.missing_evidence, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: 履歴 */}
            {sidePanelTab === 2 && (
              <div style={{ padding: '16px 20px' }}>
                <SubSectionTitle headlineLevel={4}>監査ログ ({panelAudit.length}件)</SubSectionTitle>
                <div style={{ marginTop: '8px' }}>
                  {panelAudit.map((ev, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '10px 12px',
                        borderLeft: '3px solid #e5e4e2',
                        marginBottom: '8px',
                        background: '#fafaf8',
                      }}
                    >
                      <div style={{ fontSize: '0.75rem', color: '#252525', fontWeight: 600 }}>
                        {ev.from ? `${ev.from} → ${ev.to}` : ev.to}
                      </div>
                      <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginTop: '2px' }}>
                        {ev.ts} / {ev.by}
                      </div>
                      {ev.reason && (
                        <div style={{ fontSize: '0.75rem', color: '#6b6560', marginTop: '4px' }}>
                          {ev.reason}
                        </div>
                      )}
                    </div>
                  ))}
                  {panelAudit.length === 0 && <Note>履歴がまだありません</Note>}
                </div>
              </div>
            )}
          </div>

          {/* Sticky footer (primary action) */}
          {primaryFooter && (
            <div style={S.sidePanelFooter}>
              {primaryFooter}
            </div>
          )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------
  return (
    <div style={S.page}>
      <style>{`
        @keyframes bp-skel {
          0% { background-position: 0% 0%; }
          100% { background-position: -200% 0%; }
        }
        /* 2026-04-23 Reader A: panel fade-in + chip pulse */
        @keyframes bp-panel-in {
          from { opacity: 0; transform: translateX(8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes bp-chip-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(221,122,0,0.0); }
          50%      { box-shadow: 0 0 0 3px rgba(221,122,0,0.25); }
        }
        .bp-row:hover { background: #edeae8 !important; }

        /* 2026-04-23 Reader A+B: adaptive columns via container query.
           Container is the list wrapper; column widths/visibility driven by
           pane inline-size (auto-updates on panel open/close, no JS). */
        .bp-table-container {
          container-type: inline-size;
          container-name: bptable;
          overflow-x: auto;
          min-width: 0;
        }
        .bp-table-container > table { table-layout: fixed; width: 100%; border-collapse: collapse; }

        /* Default (full, >=1100px) widths */
        .bp-col-check    { width: 36px; }
        .bp-col-date     { width: 96px; }
        .bp-col-account  { width: auto; }
        .bp-col-partner  { width: 176px; }
        .bp-col-income   { width: 108px; }
        .bp-col-expense  { width: 108px; }
        .bp-col-progress { width: 128px; }
        .bp-col-action   { width: 140px; }

        .bp-date-rel-wrap { display: block; margin-top: 1px; font-size: 0.625rem; color: #8a8580; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .bp-action-icon { display: none; width: 30px; height: 28px; border: 1px solid #dbd9d7; border-radius: 4px; background: #fff; align-items: center; justify-content: center; font-size: 0.875rem; cursor: pointer; }
        .bp-action-icon:hover { background: #f3f1ef; }
        .bp-action-full { display: inline-flex; }

        /* Medium (900-1099px): 進捗 hide */
        @container bptable (max-width: 1099px) {
          .bp-col-progress { width: 0; }
          .bp-th-progress, .bp-td-progress { display: none; }
          .bp-col-action { width: 128px; }
          .bp-col-partner { width: 160px; }
        }

        /* Narrow (700-899px): 進捗+次アクション hide, 日付 relative hide */
        @container bptable (max-width: 899px) {
          .bp-col-action { width: 0; }
          .bp-th-action, .bp-td-action { display: none; }
          .bp-col-partner { width: 144px; }
          .bp-col-income  { width: 96px; }
          .bp-col-expense { width: 96px; }
          .bp-date-rel-wrap { display: none; }
        }

        /* X-Narrow (<700px): compact action icon, smaller chip, date weekday hide */
        @container bptable (max-width: 699px) {
          .bp-col-action { width: 40px; }
          .bp-th-action, .bp-td-action { display: table-cell; }
          .bp-action-full { display: none; }
          .bp-action-icon { display: inline-flex; }
          .bp-col-partner { width: 108px; }
          .bp-col-date { width: 64px; }
          .bp-date-weekday { display: none; }
          .bp-partner-sub { display: none; }
        }

        /* Panel open → 進捗/次アクション は panel 内に表示するのでテーブルから強制 hide */
        .bp-table-container[data-panel-open="true"] .bp-col-progress,
        .bp-table-container[data-panel-open="true"] .bp-th-progress,
        .bp-table-container[data-panel-open="true"] .bp-td-progress { display: none; width: 0; }
        .bp-table-container[data-panel-open="true"] .bp-col-action,
        .bp-table-container[data-panel-open="true"] .bp-th-action,
        .bp-table-container[data-panel-open="true"] .bp-td-action { display: none; width: 0; }

        /* Fallback for browsers without container queries */
        @supports not (container-type: inline-size) {
          .bp-table-container > table { min-width: 840px; }
        }

        /* 2026-06-09 Reader A: panel 760px は wide viewport 前提。中間幅では左一覧が潰れない
           よう panel 幅を段階的にクランプし、狭幅では縦積みにフォールバック。
           インライン grid (mainLayoutWithPanel) の上書き。 */
        @media (max-width: 1480px) {
          .bp-main-layout-panel { grid-template-columns: minmax(0, 1fr) 640px !important; }
        }
        @media (max-width: 1180px) {
          .bp-main-layout-panel { grid-template-columns: minmax(0, 1fr) 560px !important; }
        }
        @media (max-width: 1000px) {
          .bp-main-layout-panel { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <Container width="wide">
        {/* Breadcrumbs */}
        <div style={{ padding: '16px 0 8px' }}>
          <Breadcrumbs
            links={[
              { title: 'FREEEMAN', url: '/' },
              { title: '銀行取引処理' },
              { title: isAllPeriods ? '全期間' : selectedMonth },
              ...(selectedWalletable ? [{ title: walletableName(selectedWalletable) }] : []),
            ]}
          />
        </div>

        {/* Page title + controls */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <PageTitle>銀行取引処理</PageTitle>
            <HStack gap={0.5}>
              <SelectBox
                options={monthOptions}
                value={isAllPeriods ? '__all__' : selectedMonth}
                onChange={(e) => {
                  if (e.target.value === '__all__') { setIsAllPeriods(true); }
                  else { setIsAllPeriods(false); setSelectedMonth(e.target.value); }
                }}
                width="small"
                small
                label="月"
              />
              <SelectBox
                options={walletableOptions}
                value={selectedWalletable ? String(selectedWalletable) : 'all'}
                onChange={(e) => setSelectedWalletable(e.target.value === 'all' ? null : Number(e.target.value))}
                width="medium"
                small
                label="口座"
              />
              <SelectBox
                options={WTYPE_OPTIONS}
                value={selectedWalletableType}
                onChange={(e) => setSelectedWalletableType(e.target.value)}
                width="small"
                small
                label="口座種別"
              />
              <Button
                appearance="primary"
                small
                disabled={!!refreshing?.active}
                onClick={() => performRefresh()}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {refreshing?.active ? (
                    <>
                      <InlineSpinner isLoading />
                      更新中 ({refreshing.done}/{refreshing.total})
                    </>
                  ) : (
                    <>
                      <MdSync size={14} /> 最新の状態に更新
                    </>
                  )}
                </span>
              </Button>
              <Button
                appearance="tertiary"
                small
                disabled={reconcileRunning}
                onClick={() => runBulkReconcile()}
              >
                {reconcileRunning ? 'Reconcile 実行中…' : '全件 Reconcile'}
              </Button>
            </HStack>
          </div>
        </div>

        {/* Error banner */}
        {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}

        {/* 「最新の状態に更新」 progress banner (= 2026-05-08 修正、 persistent UI) */}
        {refreshing && (
          <CardBase paddingSize="small" mb={0.5}>
            <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {refreshing.active ? (
                <>
                  <InlineSpinner isLoading />
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    freee 照会中... {refreshing.done}/{refreshing.total} 口座
                  </span>
                  {refreshing.fetched > 0 && (
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>
                      取得済 {refreshing.fetched} 件 / 変化 {refreshing.drifts} 件
                    </span>
                  )}
                  <span style={{ fontSize: '0.6875rem', color: '#999', marginLeft: 'auto' }}>
                    開始 {refreshing.startedAt}
                  </span>
                </>
              ) : refreshing.errors > 0 ? (
                <>
                  <MdError size={18} color="#c4412e" />
                  <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#c4412e' }}>
                    更新完了 (一部失敗): {refreshing.fetched} 件取得 / {refreshing.errors} 口座エラー
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: '#999' }}>
                    {refreshing.finishedAt}
                  </span>
                  <span style={{ marginLeft: 'auto' }}>
                    <Button small appearance="tertiary" onClick={() => setRefreshing(null)}>
                      閉じる
                    </Button>
                  </span>
                </>
              ) : (
                <>
                  <MdCheckCircle size={18} color="#338650" />
                  <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#338650' }}>
                    更新完了: {refreshing.fetched} 件取得 / {refreshing.drifts} 件変化検出
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: '#999' }}>
                    {refreshing.finishedAt}
                  </span>
                </>
              )}
            </div>
            {refreshing.errorMsgs.length > 0 && (
              <ul style={{ fontSize: '0.75rem', color: '#c4412e', margin: '4px 16px 8px', paddingLeft: '20px' }}>
                {refreshing.errorMsgs.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
          </CardBase>
        )}

        {/* 一括操作 失敗レポート (= 2026-07-11 監査 fix ①型: 赤の大型エラーブロック。
            閉じるまで永続 + 失敗分のみ再試行) */}
        {bulkReport && (
          <div
            role="alert"
            style={{
              background: '#fdecea',
              border: '2px solid #c4412e',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '8px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <MdError size={20} color="#c4412e" />
              <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#c4412e' }}>
                {bulkReport.kind}: 成功 {bulkReport.ok} 件{bulkReport.pending ? ` / 消込待ち ${bulkReport.pending} 件 (freee登録済・自動回復)` : ''} / 失敗 {bulkReport.failures.length} 件
              </span>
              <span style={{ fontSize: '0.6875rem', color: '#8a5550' }}>{bulkReport.finishedAt}</span>
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '8px' }}>
                <Button
                  small
                  appearance="secondary"
                  disabled={bulkWorking}
                  onClick={() => {
                    const failedIds = bulkReport.failures.map((f) => f.id);
                    if (bulkReport.kind === '一括却下') executeBulkReject(failedIds, bulkReport.reason || '');
                    else executeBulkApproveRegister(failedIds);
                  }}
                >
                  {bulkWorking ? <InlineSpinner isLoading={true} /> : `失敗分のみ再試行 ▸ (${bulkReport.failures.length}件)`}
                </Button>
                <Button small appearance="tertiary" onClick={() => setBulkReport(null)}>
                  閉じる
                </Button>
              </span>
            </div>
            <ul style={{ fontSize: '0.75rem', color: '#c4412e', margin: '8px 4px 2px', paddingLeft: '20px', maxHeight: '180px', overflowY: 'auto' }}>
              {bulkReport.failures.map((f) => (
                <li key={f.id} style={{ marginBottom: '2px' }}>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => { setActivePanelId(f.id); setFocusedRowId(f.id); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setActivePanelId(f.id); setFocusedRowId(f.id); } }}
                    style={{ color: '#c4412e', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    wtId={f.id}
                  </span>
                  {' — '}{f.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 一括サジェスト チャンク送信 進捗/結果 banner (= 2026-07-13 自動チャンク化) */}
        {enqueueProgress && (
          <div
            role="status"
            style={{
              background: !enqueueProgress.active && (enqueueProgress.failures.length > 0 || enqueueProgress.error) ? '#fdecea' : UNIFIED_COLOR.processing.bg,
              border: !enqueueProgress.active && (enqueueProgress.failures.length > 0 || enqueueProgress.error) ? '2px solid #c4412e' : `1px solid ${UNIFIED_COLOR.processing.bd}`,
              borderRadius: '6px',
              padding: '10px 16px',
              marginBottom: '8px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {enqueueProgress.active ? (
                <>
                  <InlineSpinner isLoading={true} />
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: UNIFIED_COLOR.processing.fg }}>
                    一括サジェスト送信中… チャンク {enqueueProgress.doneChunks}/{enqueueProgress.totalChunks}
                    {' '}({enqueueProgress.queued}/{enqueueProgress.requested}件 queued)
                  </span>
                </>
              ) : enqueueProgress.error ? (
                /* 2026-07-14: チャンク未送信の中断 (ensureSession 失敗等)。「完了: 0/N件」ではなく
                   失敗として表示する (2026-07-13 audit blocking → session/open 409 incident) */
                <>
                  <MdError size={20} color="#c4412e" />
                  <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#c4412e' }}>
                    一括サジェスト失敗: {enqueueProgress.requested}件 未送信
                    {/session|audit|409|blocking/i.test(enqueueProgress.error) ? ' — session open 不可 (監査ブロックの可能性)' : ''}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#8a5550', wordBreak: 'break-all' }}>
                    {enqueueProgress.error}
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: '#8a5550' }}>{enqueueProgress.finishedAt}</span>
                  <span style={{ marginLeft: 'auto' }}>
                    <Button small appearance="tertiary" onClick={() => setEnqueueProgress(null)}>
                      閉じる
                    </Button>
                  </span>
                </>
              ) : enqueueProgress.failures.length > 0 ? (
                <>
                  <MdError size={20} color="#c4412e" />
                  <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#c4412e' }}>
                    一括サジェスト: {enqueueProgress.queued}件 queued /
                    失敗 {enqueueProgress.failures.reduce((n, f) => n + f.ids.length, 0)}件
                    (失敗チャンク {enqueueProgress.failures.length}/{enqueueProgress.totalChunks})
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: '#8a5550' }}>{enqueueProgress.finishedAt}</span>
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '8px' }}>
                    <Button
                      small
                      appearance="secondary"
                      disabled={bulkWorking}
                      onClick={() => {
                        const failedIds = enqueueProgress.failures.flatMap((f) => f.ids);
                        executeBulkEnqueue(failedIds);
                      }}
                    >
                      {bulkWorking ? <InlineSpinner isLoading={true} /> : `失敗チャンクのみ再試行 ▸ (${enqueueProgress.failures.reduce((n, f) => n + f.ids.length, 0)}件)`}
                    </Button>
                    <Button small appearance="tertiary" onClick={() => setEnqueueProgress(null)}>
                      閉じる
                    </Button>
                  </span>
                </>
              ) : (
                <>
                  <MdCheckCircle size={18} color="#338650" />
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#338650' }}>
                    一括サジェスト完了: {enqueueProgress.queued}/{enqueueProgress.requested}件 queued ({enqueueProgress.totalChunks}チャンク)
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: '#999' }}>{enqueueProgress.finishedAt}</span>
                  <span style={{ marginLeft: 'auto' }}>
                    <Button small appearance="tertiary" onClick={() => setEnqueueProgress(null)}>
                      閉じる
                    </Button>
                  </span>
                </>
              )}
            </div>
            {!enqueueProgress.active && enqueueProgress.failures.length > 0 && (
              <ul style={{ fontSize: '0.75rem', color: '#c4412e', margin: '8px 4px 2px', paddingLeft: '20px', maxHeight: '120px', overflowY: 'auto' }}>
                {enqueueProgress.failures.map((f, i) => (
                  <li key={i} style={{ marginBottom: '2px' }}>
                    失敗チャンク{i + 1} ({f.ids.length}件: wtId {f.ids[0]}〜{f.ids[f.ids.length - 1]}) — {f.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* 学習機構 metrics dashboard (= schema-v33 banner, 2026-05-08) */}
        {learningMetrics && (
          <CardBase paddingSize="small" mb={0.5}>
            <div style={{ padding: '8px 4px', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>学習カバレッジ</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#338650' }}>
                  {learningMetrics.coverage.with_learning_event || 0} / {learningMetrics.coverage.total_registered || 0}
                  {learningMetrics.coverage.total_registered ? ` (${Math.round(((learningMetrics.coverage.with_learning_event || 0) / learningMetrics.coverage.total_registered) * 100)}%)` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>学習パターン</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#2e7cf6' }}>
                  {learningMetrics.pattern_stats.total_patterns || 0}
                  <span style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 400, marginLeft: '4px' }}>
                    (高信頼 {learningMetrics.pattern_stats.mid_conf || 0})
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>AI skip 率 (30日)</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#7c5cff' }}>
                  {learningMetrics.summary.ai_skip_rate_recent != null
                    ? `${(learningMetrics.summary.ai_skip_rate_recent * 100).toFixed(0)}%`
                    : '-'}
                  <span style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 400, marginLeft: '4px' }}>
                    ({learningMetrics.summary.mechanical_decisions_recent || 0} / {learningMetrics.summary.total_stage_decisions_recent || 0})
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>節約 token</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#338650' }}>
                  {(learningMetrics.summary.token_saved_recent || 0).toLocaleString()}
                  <span style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 400, marginLeft: '4px' }}>
                    (≈ ${learningMetrics.summary.token_cost_saved_usd_recent})
                  </span>
                </div>
              </div>
              {(learningMetrics.audit_violations.rule50_skips || 0) > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ fontSize: '0.6875rem', color: '#c33939', fontWeight: 600 }}>
                    RULE 50 違反 {learningMetrics.audit_violations.rule50_skips}
                  </div>
                </div>
              )}
            </div>
          </CardBase>
        )}

        {/* Progress + KPI-as-Filter (Reader B §10.2) */}
        <CardBase paddingSize="small" mb={0.5}>
          <div style={{ padding: '8px 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginBottom: '3px', fontWeight: 500 }}>
                  登録進捗 {registeredCount} / {progressTotal} 件 ({progressPct}%)
                </div>
                <ProgressBar value={registeredCount} maxValue={progressTotal} width="full" ariaLabel="登録進捗" />
              </div>
              <Note>{lastLoadedAt ? `最終更新 ${lastLoadedAt}` : ''}</Note>
            </div>
            <div style={S.chipRow}>
              {CHIP_DEFS.map((def) => {
                const active = chipKey === def.key;
                const cnt = chipCounts[def.key];
                return (
                  <SegmentControlButton
                    key={def.key}
                    current={active}
                    small
                    onClick={() => setChipKey(def.key)}
                  >
                    {def.label}
                    <span style={{ marginLeft: '6px', opacity: active ? 1 : 0.7 }}>
                      {cnt}
                    </span>
                  </SegmentControlButton>
                );
              })}
            </div>
            {/* 2026-07-13: 分析進捗の可視化 — analyzing 行数 + ミニPC (jobs経路) 処理中の注記 */}
            {chipCounts.analyzing > 0 && (
              <div
                role="status"
                style={{
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                  padding: '6px 10px',
                  background: UNIFIED_COLOR.processing.bg,
                  border: `1px solid ${UNIFIED_COLOR.processing.bd}`,
                  borderRadius: '4px',
                }}
              >
                <InlineSpinner isLoading={true} />
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: UNIFIED_COLOR.processing.fg }}>
                  分析中 {chipCounts.analyzing} 件 — ミニPC で処理中 (jobs 経路)
                </span>
                <span style={{ fontSize: '0.6875rem', color: UNIFIED_COLOR.processing.fg }}>
                  完了すると自動で一覧に反映されます (30秒ごとに自動更新)
                </span>
              </div>
            )}
          </div>
        </CardBase>

        {/* Filter bar: search + advanced toggle */}
        <CardBase paddingSize="zero" mb={0.5}>
          <div style={S.filterBar}>
            <SearchField
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="摘要/取引先で検索 (/)"
              width="large"
              small
            />
            <Button appearance="tertiary" small onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced ? '▾ 高度な絞込を閉じる' : '▸ 高度な絞込'}
            </Button>
            {showAdvanced && (
              <>
                <SelectBox
                  options={FLOW_OPTIONS}
                  value={flowFilter}
                  onChange={(e) => setFlowFilter(e.target.value)}
                  width="small"
                  small
                  label="分類"
                />
                <SelectBox
                  options={[
                    { name: '全収支', value: 'all' },
                    { name: '入金のみ', value: 'income' },
                    { name: '出金のみ', value: 'expense' },
                  ]}
                  value={entrySideFilter}
                  onChange={(e) => setEntrySideFilter(e.target.value)}
                  width="small"
                  small
                  label="収支"
                />
                <HStack gap={0.25} alignItems="center">
                  <input
                    type="number"
                    placeholder="min"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                    style={{ width: '88px', padding: '5px 8px', fontSize: '0.75rem', border: '1px solid #dbd9d7', borderRadius: '4px' }}
                  />
                  <span style={{ color: '#8a8580', fontSize: '0.75rem' }}>〜</span>
                  <input
                    type="number"
                    placeholder="max"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                    style={{ width: '88px', padding: '5px 8px', fontSize: '0.75rem', border: '1px solid #dbd9d7', borderRadius: '4px' }}
                  />
                </HStack>
              </>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <Note>総{total}件中 {filtered.length}件表示</Note>
            </div>
          </div>
        </CardBase>

        {/* Main two-column */}
        <div className={activePanelId ? 'bp-main-layout-panel' : undefined} style={activePanelId ? S.mainLayoutWithPanel : S.mainLayout}>
          {/* LEFT: Table */}
          <CardBase paddingSize="zero" overflowHidden={false}>
            <div className="bp-table-container" data-panel-open={activePanelId ? 'true' : 'false'}>
              <table ref={listElRef}>
                <colgroup>
                  <col className="bp-col-check" />
                  <col className="bp-col-date" />
                  <col className="bp-col-account" />
                  <col className="bp-col-partner" />
                  <col className="bp-col-income" />
                  <col className="bp-col-expense" />
                  <col className="bp-col-progress" />
                  <col className="bp-col-action" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="bp-th-check" style={{ ...S.thCell, textAlign: 'center' }}>
                      <CheckBox
                        checked={filtered.length > 0 && selectedIds.size === filtered.length}
                        onChange={toggleSelectAll}
                        small
                      />
                    </th>
                    <th
                      className="bp-th-date"
                      style={{
                        ...S.thCell, ...S.thSortable,
                        ...(sortKey === 'date' ? S.thSortActive : {}),
                      }}
                      onClick={() => onSortClick('date')}
                    >
                      日付{renderSortArrow('date')}
                    </th>
                    <th
                      className="bp-th-account"
                      style={{
                        ...S.thCell, ...S.thSortable,
                        ...(sortKey === 'account' ? S.thSortActive : {}),
                      }}
                      onClick={() => onSortClick('account')}
                    >
                      取引内容{renderSortArrow('account')}
                    </th>
                    <th className="bp-th-partner" style={{ ...S.thCell }}>
                      取引先
                    </th>
                    {/* 2026-04-23 Reader A: 金額 column split into 入金 / 出金 */}
                    <th
                      className="bp-th-income"
                      style={{
                        ...S.thCell, ...S.thSortable,
                        ...(sortKey === 'income' ? S.thSortActive : {}),
                        textAlign: 'right',
                      }}
                      onClick={() => onSortClick('income')}
                    >
                      入金{renderSortArrow('income')}
                    </th>
                    <th
                      className="bp-th-expense"
                      style={{
                        ...S.thCell, ...S.thSortable,
                        ...(sortKey === 'expense' ? S.thSortActive : {}),
                        textAlign: 'right',
                      }}
                      onClick={() => onSortClick('expense')}
                    >
                      出金{renderSortArrow('expense')}
                    </th>
                    <th
                      className="bp-th-progress"
                      style={{
                        ...S.thCell, ...S.thSortable,
                        ...(sortKey === 'status' ? S.thSortActive : {}),
                        textAlign: 'center',
                      }}
                      onClick={() => onSortClick('status')}
                    >
                      進捗{renderSortArrow('status')}
                    </th>
                    <th className="bp-th-action" style={{ ...S.thCell, textAlign: 'center' }}>
                      次アクション
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && filtered.length === 0 && Array.from({ length: 8 }).map((_, i) => (
                    <tr key={`skel-${i}`}>
                      <td colSpan={8} style={S.skeletonRow}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          <span style={S.skeletonBar('60px')} />
                          <span style={S.skeletonBar('200px')} />
                          <span style={S.skeletonBar('120px')} />
                          <span style={{ ...S.skeletonBar('90px'), marginLeft: 'auto' }} />
                        </div>
                      </td>
                    </tr>
                  ))}

                  {!loading && filtered.map((r) => {
                    const isActive = activePanelId === r.wallet_txn_id;
                    const isSelected = selectedIds.has(r.wallet_txn_id);
                    const isFocused = focusedRowId === r.wallet_txn_id;
                    const proc = r.processing;
                    const pipeline = classifyPipeline(r);
                    // 2026-07-13: error 行の消込副状態 (reconcile_state) を区別表示。
                    // 応答にフィールドが無ければ null → 通常の error 表示に fallback。
                    const reconBadge = reconcileBadgeFor(r);
                    const isIncome = r.entry_side === 'income';
                    // 2026-04-23 Reader A+B: 5-way partner resolution + relative date
                    const partner = resolvePartner(r);
                    const dateDisp = formatDateMD_v2(r.date);
                    return (
                      <tr
                        key={r.wallet_txn_id}
                        className="bp-row"
                        style={S.row(isSelected, isActive, isFocused)}
                        onClick={() => handleRowClick(r)}
                        onMouseEnter={() => setFocusedRowId(r.wallet_txn_id)}
                        tabIndex={0}
                      >
                        <td className="bp-td-check" style={{ ...S.tdCell, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <CheckBox checked={isSelected} onChange={() => toggleSelect(r.wallet_txn_id)} small />
                        </td>
                        <td
                          className="bp-td-date"
                          style={{ ...S.tdCell, padding: '10px 8px' }}
                          title={`${dateDisp.tooltip}${dateDisp.secondary ? ' · ' + dateDisp.secondary : ''}`}
                        >
                          <div style={{
                            fontSize: '0.8125rem', color: '#252525',
                            fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.35,
                          }}>
                            {dateDisp.primary.split(' ')[0]}
                            <span className="bp-date-weekday" style={{ marginLeft: '3px', fontSize: '0.6875rem', color: '#8a8580' }}>
                              {dateDisp.primary.slice(dateDisp.primary.indexOf(' ') + 1)}
                            </span>
                          </div>
                          {dateDisp.secondary && (
                            <span className="bp-date-rel-wrap">{dateDisp.secondary}</span>
                          )}
                        </td>
                        <td className="bp-td-account" style={S.tdCell}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                              aria-hidden="true"
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '18px', height: '18px', borderRadius: '50%',
                                fontSize: '0.625rem', fontWeight: 700, flexShrink: 0,
                                background: isIncome ? '#e6f4ea' : '#fde7e7',
                                color: isIncome ? '#338650' : '#c33939',
                              }}
                              title={isIncome ? '入金' : '出金'}
                            >
                              {isIncome ? '▲' : '●'}
                            </span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: 500, color: '#252525', fontSize: '0.8125rem',
                                  whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis',
                                  maxWidth: '100%',
                                }}
                                title={r.description || ''}
                              >
                                {r.description || <span style={{ color: '#c4c0bc' }}>—</span>}
                              </div>
                              <div style={{ marginTop: '2px', fontSize: '0.6875rem', color: '#8a8580' }}>
                                {walletableName(r.walletable_id)}
                                {r.change_count > 0 && (
                                  <span style={{ marginLeft: '6px', fontSize: '0.625rem', color: '#c67320' }}>· 再取得 {r.change_count}回</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="bp-td-partner" style={S.tdCell}>
                          {partner.kind === 'confirmed' && (
                            <div>
                              <div style={{
                                fontSize: '0.8125rem', color: '#252525', fontWeight: 500,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }} title={partner.name}>
                                {partner.name}
                              </div>
                              <div className="bp-partner-sub" style={{ fontSize: '0.625rem', color: '#338650', marginTop: '2px' }}>✓ 登録済</div>
                            </div>
                          )}
                          {partner.kind === 'suggested' && (
                            <div>
                              <div style={{
                                fontSize: '0.8125rem', color: '#6b6560', fontStyle: 'italic', fontWeight: 500,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }} title={partner.name}>
                                {partner.name}
                                <span style={{ marginLeft: '4px', color: '#c4c0bc', fontSize: '0.625rem' }}>(推定)</span>
                              </div>
                              <div className="bp-partner-sub" style={{
                                fontSize: '0.625rem',
                                color: partner.confidence >= 0.9 ? '#338650' : partner.confidence >= 0.75 ? '#c67320' : '#c33939',
                                marginTop: '2px',
                              }}>
                                信頼 {Math.round(partner.confidence * 100)}% · {partner.sourceLabel}
                              </div>
                              {partner.evidenceNote && (
                                <div className="bp-partner-sub" style={{
                                  fontSize: '0.625rem', color: '#8a8580', marginTop: '1px',
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }} title={partner.evidenceNote}>
                                  {partner.evidenceNote}
                                </div>
                              )}
                            </div>
                          )}
                          {partner.kind === 'auto_rule' && (
                            <span
                              style={{
                                display: 'inline-block', fontSize: '0.625rem', fontWeight: 600,
                                padding: '2px 8px', borderRadius: '10px', lineHeight: '1.4',
                                background: '#fff4e5', color: '#c67320', whiteSpace: 'nowrap',
                              }}
                              title={partner.note}
                            >
                              自動で経理
                            </span>
                          )}
                          {partner.kind === 'unknown' && (
                            <span style={{ fontSize: '0.75rem', color: '#c4c0bc', fontStyle: 'italic' }}>
                              未特定 (分析待ち)
                            </span>
                          )}
                          {proc?.pattern_confidence != null && proc.pattern_confidence < 0.75 && partner.kind === 'confirmed' && (
                            <div className="bp-partner-sub" style={{ marginTop: '2px', fontSize: '0.625rem', color: '#c67320' }}>
                              低信頼 {Math.round(proc.pattern_confidence * 100)}%
                            </div>
                          )}
                        </td>
                        {/* 2026-04-23 Reader A: 入金 (緑) / 出金 (赤) separate columns */}
                        <td
                          className="bp-td-income"
                          style={{
                            ...S.tdCell,
                            textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                          }}
                        >
                          {fmtIncomeCell(r)}
                        </td>
                        <td
                          className="bp-td-expense"
                          style={{
                            ...S.tdCell,
                            textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                          }}
                        >
                          {fmtExpenseCell(r)}
                        </td>
                        <td className="bp-td-progress" style={{ ...S.tdCell, textAlign: 'center' }}>
                          {reconBadge ? (
                            // error だが実態は消込待ち(自動回復中) / 差額要確認 — 処理失敗と区別する
                            <UnifiedChip b={reconBadge} />
                          ) : (
                            <StatusIcon type={pipelineToStatusType(pipeline)}>{pipeline.label}</StatusIcon>
                          )}
                        </td>
                        <td className="bp-td-action" style={{ ...S.tdCell, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <span className="bp-action-full">{renderNextActionButton(r)}</span>
                          <button
                            type="button"
                            className="bp-action-icon"
                            title={reconBadge ? reconBadge.label : pipeline.label}
                            aria-label={reconBadge ? reconBadge.label : pipeline.label}
                            onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}
                          >
                            ▸
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {!loading && filtered.length === 0 && !error && (
                    <tr>
                      <td colSpan={8} style={{ padding: '56px 24px', textAlign: 'center' }}>
                        <VStack gap={0.5} alignItems="center">
                          {chipKey !== 'all' || searchText || minAmount || maxAmount ? (
                            <>
                              <NoSearchResultsIllust />
                              <Text size={0.875} color="burnt">該当する取引がありません</Text>
                              <Button appearance="tertiary" small onClick={() => {
                                setChipKey('all'); setSearchText(''); setMinAmount(''); setMaxAmount(''); setEntrySideFilter('all'); setFlowFilter('all');
                              }}>
                                フィルターをクリア
                              </Button>
                            </>
                          ) : (
                            <>
                              <NoDataIllust />
                              <Text size={0.875} color="burnt">取引データがありません</Text>
                              <Note>freee 同期が走ると自動で一覧に現れます</Note>
                            </>
                          )}
                        </VStack>
                      </td>
                    </tr>
                  )}

                  {!loading && error && filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ padding: '56px 24px', textAlign: 'center' }}>
                        <VStack gap={0.5} alignItems="center">
                          <AlertSwallow />
                          <Text size={0.875} color="burnt">接続できません</Text>
                          <Note>{error}</Note>
                          <Button appearance="secondary" small onClick={loadRows}>再読込</Button>
                        </VStack>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* 2026-07-11 監査 fix (⑧型): 「もっと見る」追加読込。未読込分を可視化しゼロにする経路 */}
            {!loading && nextOffset != null && (
              <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid #edebea', background: '#fffaf0' }}>
                <Button appearance="secondary" small disabled={loadingMore} onClick={loadMoreRows}>
                  {loadingMore ? <InlineSpinner isLoading={true} /> : `もっと見る ▸ (未読込 ${Math.max(total - rows.length, 0)} 件)`}
                </Button>
                <div style={{ marginTop: '6px', fontSize: '0.6875rem', color: '#a46a12', fontWeight: 600 }}>
                  総 {total} 件中 {rows.length} 件を読込済 — 残り {Math.max(total - rows.length, 0)} 件はまだ表示されていません
                </div>
              </div>
            )}
          </CardBase>

          {/* RIGHT: Side panel */}
          {activePanelId && renderSidePanel()}
        </div>

        {/* Footer summary (always visible、⑧型: N件中M件表示 = 不可視ゼロ保証) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', marginTop: '8px' }}>
          <Note>
            総 {total} 件中 {rows.length} 件読込・{filtered.length} 件表示
            {nextOffset != null && ` (未読込 ${Math.max(total - rows.length, 0)} 件 → 「もっと見る」で表示)`}
            {summary && ` | 全件 ${summary.totals.wallet_txn_count}件`}
          </Note>
          <Note>
            登録済 {registeredCount}件
          </Note>
        </div>
      </Container>

      {/* Floating bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          style={{
            position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 40,
            background: '#252525', color: '#fff',
            borderRadius: '8px', padding: '10px 16px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}
        >
          <Text color="white" weight="bold" size={0.875}>{selectedIds.size}件選択</Text>
          <Button appearance="primary" small disabled={bulkWorking} onClick={onBulkEnqueue}>
            一括サジェスト ▸
          </Button>
          <Button appearance="secondary" small disabled={bulkWorking} onClick={onBulkApprove}>
            一括承認 → 登録 ▸
          </Button>
          <Button appearance="secondary" small danger disabled={bulkWorking} onClick={onBulkReject}>
            却下
          </Button>
          <Button appearance="tertiary" small onClick={() => setSelectedIds(new Set())}>
            ✕
          </Button>
        </div>
      )}

      {/* Confirm dialog (replaces window.confirm) */}
      <MessageDialogConfirm
        isOpen={confirmDialog.open}
        title={confirmDialog.title}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog((d) => ({ ...d, open: false }));
        }}
        onRequestClose={() => setConfirmDialog((d) => ({ ...d, open: false }))}
        confirmButtonLabel={confirmDialog.primaryLabel}
        closeButtonLabel="キャンセル"
        danger={!!confirmDialog.danger}
      >
        <div style={{ fontSize: '0.875rem', color: '#252525' }}>
          {confirmDialog.body}
        </div>
      </MessageDialogConfirm>

      {/* Prompt dialog (replaces window.prompt) */}
      <TaskDialog
        isOpen={promptDialog.open}
        title={promptDialog.title}
        onPrimaryAction={() => {
          promptDialog.onSubmit(promptValue);
          setPromptDialog((d) => ({ ...d, open: false }));
        }}
        onRequestClose={() => setPromptDialog((d) => ({ ...d, open: false }))}
        primaryButtonLabel={promptDialog.primaryLabel}
        closeButtonLabel="キャンセル"
        danger={!!promptDialog.danger}
        shouldCloseOnOverlayClickOrEsc
      >
        <div style={{ padding: '4px 0' }}>
          <Text size={0.875}>{promptDialog.label}</Text>
          <TextArea
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            placeholder={promptDialog.placeholder}
            width="full"
            height={5}
          />
        </div>
      </TaskDialog>

      {/* Floating toasts */}
      {toasts.map((t) => (
        <FloatingMessageBlock
          key={t.id}
          success={t.kind === 'success'}
          error={t.kind === 'error'}
          notice={t.kind === 'notice'}
          info={t.kind === 'info'}
          onClose={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
        >
          {t.text}
        </FloatingMessageBlock>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ReceiptAttachWidget — schema-v33 / 2026-05-08 (+ 2026-07-14 確定設計 §3)
// PDF アップロード or 既存 freee filebox receipt 検索 → wtp.proposed_deal_json.receipt_ids[] 追加
// 添付済 receipt の filename / status / freee filebox link 表示
// JCB入金は ARICA → freee filebox 自動 attach button あり
// 2026-07-14 追加:
//   - ARICA 候補 (GET /api/bank-wallet-txns/:id/evidence-candidates) + [添付候補に採用]
//     (汎用 POST /api/freee-public/arica-stage — per-family stage 乱立の集約先)
//   - ファイルボックスNo 指定添付 (worker が receipt id へ解決)
//   - 証憑 vision 分析 (job type 'bank_receipt_vision_extract' — [この証憑で再分析])
//   いずれも worker 並行実装 — endpoint 未配備 (404) / flag off (503) は防御的に案内表示。
// ---------------------------------------------------------------------------
const normalizeVisionList = (v: any): BankReceiptVisionResult[] =>
  Array.isArray(v) ? v.filter((x): x is BankReceiptVisionResult => !!x && typeof x === 'object') : [];

const parseVisionResult = (rj: any): any => {
  if (rj == null) return null;
  if (typeof rj === 'string') {
    try { return JSON.parse(rj); } catch { return { raw: rj }; }
  }
  return rj;
};

const visionChipSt = (bg: string, fg: string): React.CSSProperties => ({
  padding: '1px 6px', borderRadius: '3px', fontSize: '0.625rem', fontWeight: 700,
  background: bg, color: fg, whiteSpace: 'nowrap',
});

interface ReceiptAttachWidgetProps {
  walletTxnId: number;
  txnDate: string;
  proposedDealJson: any;
  freeeDealId: number | null;
  isRegistered: boolean;
  /** detail GET 同載の wallet_txn_receipt_vision 行 (応答に無ければ null — 防御的) */
  visionFromDetail: any;
  onAttached: () => void;
}

const ReceiptAttachWidget: React.FC<ReceiptAttachWidgetProps> = ({ walletTxnId, txnDate, proposedDealJson, freeeDealId, isRegistered, visionFromDetail, onAttached }) => {
  const [mode, setMode] = useState<'arica_auto' | 'upload' | 'filebox_no' | 'search' | 'jcb_auto' | 'nicos_auto'>('upload');
  const [uploading, setUploading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<FreeeReceipt[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [attachedDetails, setAttachedDetails] = useState<FreeeReceiptDetail[]>([]);
  const [pending, setPending] = useState<PendingReceipt[]>([]);
  const [autoAttaching, setAutoAttaching] = useState(false);
  const [detaching, setDetaching] = useState<number | null>(null);
  const [deletingPending, setDeletingPending] = useState<number | null>(null);
  const [previewingId, setPreviewingId] = useState<number | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // 2026-07-14 確定設計 §3: ARICA 候補 / ファイルボックスNo / vision
  const [candidates, setCandidates] = useState<BankEvidenceCandidate[] | null>(null);
  const [candLoading, setCandLoading] = useState(false);
  const [candError, setCandError] = useState<string | null>(null);
  const [adopting, setAdopting] = useState<number | null>(null);
  const [fileboxNo, setFileboxNo] = useState('');
  const [fileboxAttaching, setFileboxAttaching] = useState(false);
  const [vision, setVision] = useState<BankReceiptVisionResult[]>([]);
  const [visionRequesting, setVisionRequesting] = useState<string | null>(null);
  const [visionExpanded, setVisionExpanded] = useState<string | null>(null);

  // pending receipts list を fetch (= R2 仮保存中の PDFs)
  const reloadPending = useCallback(async () => {
    try {
      const r = await listPendingReceipts(walletTxnId);
      // 確定済 (= uploaded_to_freee_at != null) は freee 側に移動済なので pending list から除外
      const stillPending = (r.items || []).filter((it) => !it.uploaded_to_freee_at);
      setPending(stillPending);
    } catch { /* ignore */ }
  }, [walletTxnId]);

  useEffect(() => { reloadPending(); }, [reloadPending]);

  // ---- 証憑 vision (2026-07-14 確定設計 §3) ----
  // 正本は detail GET 同載 (visionFromDetail)。GET /receipt-vision が配備済ならそれで上書き
  // (未配備 404 は握って seed 継続 — ページは壊さない)。
  const reloadVision = useCallback(async () => {
    try {
      const r = await fetchBankReceiptVision(walletTxnId);
      setVision(r.items);
    } catch { /* endpoint 未配備 → detail 同載 seed を維持 */ }
  }, [walletTxnId]);

  useEffect(() => {
    setVision(normalizeVisionList(visionFromDetail));
    setVisionExpanded(null);
    reloadVision();
  }, [walletTxnId, visionFromDetail, reloadVision]);

  const visionFor = useCallback((kind: BankReceiptVisionRefKind, refId: number): BankReceiptVisionResult | undefined =>
    vision.find((x) => String(x.ref_kind) === kind && Number(x.ref_id) === refId), [vision]);

  const handleVision = async (kind: BankReceiptVisionRefKind, refId: number) => {
    const key = `${kind}:${refId}`;
    setError(null);
    setInfo(null);
    setVisionRequesting(key);
    try {
      await requestBankReceiptVision(walletTxnId, kind, refId);
      // 楽観 upsert (GET 未配備でも「分析中」を出す) → GET 配備済なら reloadVision が真値で上書き
      setVision((prev) => [
        ...prev.filter((x) => !(String(x.ref_kind) === kind && Number(x.ref_id) === refId)),
        { wallet_txn_id: walletTxnId, ref_kind: kind, ref_id: refId, status: 'queued' },
      ]);
      setInfo('証憑 vision 分析ジョブを投入しました (ミニPC実行 — 完了後の再分析で提案に反映されます)');
      await reloadVision();
    } catch (err) {
      const e = err as Error & { status?: number };
      if (e.status === 404) setError('証憑 vision API 未配備 (worker 側実装待ち)');
      else if (e.status === 503) setError('証憑 vision 機能は現在 OFF です (ops_flag bank_receipt_vision)');
      else setError(e.message);
    } finally {
      setVisionRequesting(null);
    }
  };

  // 証憑行の vision チップ + [この証憑で再分析] ボタン (仮保存/添付済 両リスト共通)
  const renderVisionRow = (kind: BankReceiptVisionRefKind, refId: number, borderColor: string) => {
    const v = visionFor(kind, refId);
    const key = `${kind}:${refId}`;
    const st = v?.status;
    const parsed = st === 'done' ? parseVisionResult(v?.result_json) : null;
    const expanded = visionExpanded === key;
    return (
      <div style={{ padding: '4px 10px 6px', borderTop: `1px dashed ${borderColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {st === 'queued' && <span style={visionChipSt('#e8f0fe', '#1a5cc7')}>vision 分析中 (ミニPC)</span>}
          {st === 'done' && <span style={visionChipSt('#e6f4ea', '#338650')}>vision 分析済</span>}
          {st === 'error' && <span style={visionChipSt('#fde7e7', '#c33939')}>vision 失敗</span>}
          <Button
            small
            appearance="tertiary"
            disabled={visionRequesting === key || st === 'queued'}
            onClick={() => handleVision(kind, refId)}
          >
            {visionRequesting === key ? '投入中…' : st ? 'この証憑で再分析 (vision 再実行) ▸' : 'この証憑で再分析 ▸'}
          </Button>
          <span style={{ fontSize: '0.625rem', color: '#8a8580' }}>
            PDF を vision 抽出 → Reader A/B/C の根拠として次回分析に供給
          </span>
          {st === 'done' && parsed != null && (
            <Button small appearance="tertiary" onClick={() => setVisionExpanded(expanded ? null : key)}>
              {expanded ? '結果を閉じる' : '結果を表示'}
            </Button>
          )}
        </div>
        {expanded && parsed != null && (
          <pre style={{ margin: '4px 0 0', padding: '6px 8px', background: '#fff', border: '1px solid #e5e4e2', borderRadius: '4px', fontSize: '0.625rem', maxHeight: '220px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(parsed, null, 2)}
          </pre>
        )}
      </div>
    );
  };

  // ---- ARICA 自動取得候補 (2026-07-14 確定設計 §3) ----
  const loadCandidates = useCallback(async () => {
    setCandLoading(true);
    setCandError(null);
    try {
      const r = await fetchBankEvidenceCandidates(walletTxnId);
      setCandidates(r.candidates);
    } catch (err) {
      const e = err as Error & { status?: number };
      setCandidates([]);
      if (e.status === 404) setCandError('候補提示 API 未配備 (worker 側実装待ち)。JCB/NICOS 自動取得か手動アップロードをご利用ください');
      else if (e.status === 503) setCandError('証憑自動取得は現在 OFF です (ops_flag bank_evidence_autofetch)');
      else setCandError(e.message);
    } finally {
      setCandLoading(false);
    }
  }, [walletTxnId]);

  useEffect(() => {
    if (mode === 'arica_auto' && candidates == null && !candLoading) loadCandidates();
  }, [mode, candidates, candLoading, loadCandidates]);

  const handleAdopt = async (c: BankEvidenceCandidate, idx: number) => {
    setError(null);
    setInfo(null);
    setAdopting(idx);
    try {
      const r = await stageAricaEvidence(walletTxnId, {
        ...(c.source ? { source: c.source } : {}),
        ...(c.source_ref != null ? { source_ref: String(c.source_ref) } : {}),
        ...(c.kind ? { kind: c.kind } : {}),
        ...(c.filename ? { filename: c.filename } : {}),
      });
      const stagedOk = r?.ok === true || r?.pending_id != null
        || (Array.isArray(r?.staged) && (r.staged as any[]).length > 0);
      if (!stagedOk) throw new Error(`仮保存失敗: ${JSON.stringify(r ?? {}).slice(0, 150)}`);
      setInfo('候補を添付候補に採用しました (R2 仮保存 — 承認時に freee filebox へ確定移動)');
      await reloadPending();
      await loadCandidates();
    } catch (err) {
      const e = err as Error & { status?: number };
      if (e.status === 404) setError('汎用 arica-stage API 未配備 (worker 側実装待ち)。JCB/NICOS 専用ボタンをご利用ください');
      else setError(e.message);
    } finally {
      setAdopting(null);
    }
  };

  // ---- ファイルボックスNo 指定添付 (2026-07-14 確定設計 §3 手動経路) ----
  const handleFileboxAttach = async () => {
    const num = Number(fileboxNo.trim());
    if (!Number.isFinite(num) || num <= 0) {
      setError('ファイルボックスNo は正の数値で入力してください');
      return;
    }
    setError(null);
    setInfo(null);
    setFileboxAttaching(true);
    try {
      const r = await attachReceiptNumberToWalletTxn(walletTxnId, num);
      const added = Array.isArray(r?.added) ? r.added : [];
      setInfo(`ファイルボックスNo ${num} を添付しました${added.length > 0 ? ` (receipt #${added.join(', #')})` : ''}`);
      setFileboxNo('');
      onAttached();
    } catch (err) {
      const e = err as Error & { status?: number; body?: any };
      const bodyErr = typeof e.body?.error === 'string' ? e.body.error : '';
      if (e.status === 400 && bodyErr.includes('receipt_ids')) {
        setError('worker がファイルボックスNo 指定に未対応です (実装待ち)。「既存 receipt 検索」から添付してください');
      } else if (bodyErr === 'receipt_number_not_found' || JSON.stringify(e.body?.resolution ?? e.body?.errors ?? '').includes('receipt_number_not_found')) {
        setError(`ファイルボックスNo ${num} が見つかりません (freee filebox の「No」表示値を確認してください)`);
      } else {
        setError(e.message);
      }
    } finally {
      setFileboxAttaching(false);
    }
  };

  // 現在 proposed_deal_json に attached 済 receipt_ids
  const attachedIds: number[] = useMemo(() => {
    try {
      const p = typeof proposedDealJson === 'string' ? JSON.parse(proposedDealJson) : proposedDealJson;
      return Array.isArray(p?.receipt_ids) ? p.receipt_ids.map(Number) : [];
    } catch {
      return [];
    }
  }, [proposedDealJson]);

  // attached receipt の詳細を fetch
  useEffect(() => {
    if (attachedIds.length === 0) {
      setAttachedDetails([]);
      return;
    }
    let alive = true;
    (async () => {
      const results = await Promise.allSettled(
        attachedIds.map((id) => fetchFreeeReceiptDetail(id))
      );
      if (!alive) return;
      const details = results
        .filter((r) => r.status === 'fulfilled')
        .map((r: any) => r.value.receipt as FreeeReceiptDetail);
      setAttachedDetails(details);
    })();
    return () => { alive = false; };
  }, [attachedIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setInfo(null);
    setUploading(true);
    try {
      const r = await stageManualUpload(walletTxnId, file);
      if (!r.ok || !r.pending_id) throw new Error('staging 失敗');
      setInfo(`PDF を仮保存しました (pending #${r.pending_id})。 承認時に freee filebox にアップロード`);
      await reloadPending();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = async () => {
    setError(null);
    setSearching(true);
    try {
      // 取引日 ±7日 で検索
      const dateFrom = new Date(txnDate);
      dateFrom.setDate(dateFrom.getDate() - 7);
      const dateTo = new Date(txnDate);
      dateTo.setDate(dateTo.getDate() + 7);
      const r = await fetchFreeeReceipts({
        category: 'without_deal',
        start_date: dateFrom.toISOString().slice(0, 10),
        end_date: dateTo.toISOString().slice(0, 10),
        description: searchQuery || undefined,
        limit: 30,
      });
      setSearchResults(r.receipts || []);
      if ((r.receipts || []).length === 0) {
        setInfo('該当の receipt が見つかりません (= 未紐付け only)');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSearching(false);
    }
  };

  const handleAttach = async (receiptId: number) => {
    setError(null);
    try {
      await attachReceiptsToWalletTxn(walletTxnId, [receiptId]);
      setInfo(`receipt #${receiptId} を添付しました`);
      onAttached();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDetach = async (receiptId: number) => {
    setDetaching(receiptId);
    setError(null);
    try {
      await detachReceiptFromWalletTxn(walletTxnId, receiptId);
      setInfo(`receipt #${receiptId} を取り外しました`);
      onAttached();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDetaching(null);
    }
  };

  const handleJcbAutoStage = async () => {
    setError(null);
    setInfo(null);
    setAutoAttaching(true);
    try {
      const r = await stageJcbForWalletTxn(walletTxnId, txnDate);
      if (r.ok && r.staged.length > 0) {
        const newCount = r.staged.filter((s) => !s.already_staged).length;
        setInfo(`JCB PDF ${r.staged.length} 件を ARICA から取得 + 仮保存完了 (新規 ${newCount})`);
        await reloadPending();
      } else if (r.arica_pdf_count === 0) {
        setError('ARICA に該当 transfer_date の PDF なし。 ARICA scraper 待ちか手動 upload で対応');
      } else {
        setError(`仮保存失敗: ${JSON.stringify(r.errors).slice(0, 150)}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAutoAttaching(false);
    }
  };

  // 2026-05-08: NICOS / 三菱UFJニコス 自動 stage (= ARICA mufg-transfer 振込明細 PDF)
  const handleNicosAutoStage = async () => {
    setError(null);
    setInfo(null);
    setAutoAttaching(true);
    try {
      const r = await stageNicosForWalletTxn(walletTxnId, txnDate);
      if (r.ok && r.staged.length > 0) {
        const newCount = r.staged.filter((s) => !s.already_staged).length;
        setInfo(`NICOS PDF ${r.staged.length} 件を ARICA から取得 + 仮保存完了 (新規 ${newCount}、 dedupe 後 ${r.arica_pdf_count_unique}/${r.arica_pdf_count_raw})`);
        await reloadPending();
      } else if (r.arica_pdf_count_raw === 0) {
        setError('ARICA に該当 transfer_date の PDF なし。 mini PC PDF 復号 (= 06:30 JST) 待ちか手動 upload で対応');
      } else {
        setError(`仮保存失敗: ${JSON.stringify(r.errors).slice(0, 150)}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAutoAttaching(false);
    }
  };

  const handleDeletePending = async (pendingId: number) => {
    setDeletingPending(pendingId);
    setError(null);
    try {
      await deletePendingReceipt(pendingId);
      setInfo(`仮保存 #${pendingId} を削除しました`);
      await reloadPending();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingPending(null);
    }
  };

  const togglePreview = (pendingId: number) => {
    setPreviewingId(previewingId === pendingId ? null : pendingId);
  };

  return (
    <div style={{ marginTop: '12px', marginBottom: '12px', padding: '10px 12px', background: '#f0f4ff', borderRadius: '6px', borderLeft: '3px solid #7c5cff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <Text size={0.875} weight="bold">証憑 PDF</Text>
        {pending.length > 0 && (
          <span style={{ fontSize: '0.6875rem', color: '#dd7a00', fontWeight: 600 }}>
            仮保存 {pending.length} 件
          </span>
        )}
        <span style={{ fontSize: '0.6875rem', color: '#5b3fc7', fontWeight: 600 }}>
          freee 添付済 {attachedIds.length} 件
        </span>
      </div>

      {/* 仮保存 (= R2 staged、 承認時に commit + freee 移動) */}
      {pending.length > 0 && (
        <div style={{ marginBottom: '10px', background: '#fff7ed', border: '1px solid #ffd9a8', borderRadius: '4px' }}>
          <div style={{ padding: '4px 8px', fontSize: '0.625rem', color: '#dd7a00', fontWeight: 600, borderBottom: '1px solid #ffd9a8' }}>
            仮保存 PDF (= 承認時に freee filebox へ移動)
          </div>
          {pending.map((pr) => (
            <div key={pr.id} style={{ borderBottom: '1px solid #ffd9a8' }}>
              <div style={{ padding: '6px 10px', fontSize: '0.6875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>
                    📥 #{pr.id} ({pr.source})
                    <span style={{ marginLeft: '6px', color: '#8a8580', fontWeight: 400 }}>
                      {(pr.file_size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                  <div style={{ color: '#6b6560', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.625rem' }}>
                    {pr.filename}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <Button small appearance={previewingId === pr.id ? 'primary' : 'tertiary'} onClick={() => togglePreview(pr.id)}>
                    {previewingId === pr.id ? '閉じる' : 'プレビュー'}
                  </Button>
                  <a
                    href={pendingReceiptPreviewUrl(pr.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.6875rem', color: '#2e7cf6', textDecoration: 'none', padding: '2px 6px', border: '1px solid #2e7cf6', borderRadius: '3px' }}
                  >
                    別タブ ↗
                  </a>
                  {!isRegistered && (
                    <Button
                      small
                      appearance="tertiary"
                      disabled={deletingPending === pr.id}
                      onClick={() => handleDeletePending(pr.id)}
                    >
                      {deletingPending === pr.id ? '削除中' : '削除'}
                    </Button>
                  )}
                </div>
              </div>
              {renderVisionRow('pending', pr.id, '#ffd9a8')}
              {previewingId === pr.id && (
                <iframe
                  src={pendingReceiptPreviewUrl(pr.id)}
                  style={{ width: '100%', height: '500px', border: 'none', borderTop: '1px solid #ffd9a8' }}
                  title={`preview-${pr.id}`}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* 添付済 receipt list (= freee filebox 確定済、 freee link + inline preview iframe) */}
      {attachedIds.length > 0 && (
        <div style={{ marginBottom: '10px', background: '#fff', border: '1px solid #d8d2ff', borderRadius: '4px' }}>
          {attachedIds.map((rid) => {
            const detail = attachedDetails.find((dd) => dd.id === rid);
            const isExpanded = previewingId === rid;
            return (
              <div key={rid} style={{ borderBottom: '1px solid #f0eeec' }}>
                <div style={{ padding: '6px 10px', fontSize: '0.6875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>
                      📎 #{rid}
                      {detail?.status && <span style={{ marginLeft: '6px', color: detail.status === 'confirmed' ? '#338650' : '#dd7a00' }}>[{detail.status}]</span>}
                      {detail?.mime_type && <span style={{ marginLeft: '6px', color: '#8a8580' }}>{detail.mime_type}</span>}
                    </div>
                    {detail?.description && <div style={{ color: '#6b6560', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail.description}</div>}
                    {detail?.created_at && <div style={{ color: '#8a8580', fontSize: '0.625rem' }}>upload: {detail.created_at.replace('T', ' ').slice(0, 19)}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Button small appearance={isExpanded ? 'primary' : 'tertiary'} onClick={() => togglePreview(rid)}>
                      {isExpanded ? '閉じる' : 'プレビュー'}
                    </Button>
                    <a
                      href={freeeReceiptViewUrl(rid)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.6875rem', color: '#2e7cf6', textDecoration: 'none', padding: '2px 6px', border: '1px solid #2e7cf6', borderRadius: '3px' }}
                    >
                      freee で開く ↗
                    </a>
                    {!isRegistered && (
                      <Button
                        small
                        appearance="tertiary"
                        disabled={detaching === rid}
                        onClick={() => handleDetach(rid)}
                      >
                        {detaching === rid ? '取り外し中...' : '取り外し'}
                      </Button>
                    )}
                  </div>
                </div>
                {renderVisionRow('receipt', rid, '#d8d2ff')}
                {isExpanded && (
                  <iframe
                    src={freeeReceiptDownloadUrl(rid)}
                    style={{ width: '100%', height: '500px', border: 'none', borderTop: '1px solid #d8d2ff' }}
                    title={`receipt-${rid}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {attachedIds.length === 0 && (
        <div style={{ marginBottom: '10px', padding: '8px 10px', background: '#fff', border: '1px dashed #ccc', borderRadius: '4px', fontSize: '0.6875rem', color: '#8a8580', textAlign: 'center' }}>
          証憑 PDF 未添付
        </div>
      )}

      {/* registered 後は detach/attach 不可 (= 警告のみ) */}
      {isRegistered ? (
        <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>
          {freeeDealId
            ? `freee deal #${freeeDealId} に紐付け済。 追加添付は freee 画面から直接`
            : '登録済 wallet_txn です'}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <Button small appearance={mode === 'arica_auto' ? 'primary' : 'tertiary'} onClick={() => setMode('arica_auto')}>
              ARICA 候補 (自動取得)
            </Button>
            <Button small appearance={mode === 'upload' ? 'primary' : 'tertiary'} onClick={() => setMode('upload')}>
              アップロード
            </Button>
            <Button small appearance={mode === 'filebox_no' ? 'primary' : 'tertiary'} onClick={() => setMode('filebox_no')}>
              ファイルボックスNo
            </Button>
            <Button small appearance={mode === 'search' ? 'primary' : 'tertiary'} onClick={() => setMode('search')}>
              既存 receipt 検索
            </Button>
            <Button small appearance={mode === 'jcb_auto' ? 'primary' : 'tertiary'} onClick={() => setMode('jcb_auto')}>
              JCB 自動取得 (ARICA)
            </Button>
            <Button small appearance={mode === 'nicos_auto' ? 'primary' : 'tertiary'} onClick={() => setMode('nicos_auto')}>
              NICOS 自動取得 (ARICA)
            </Button>
          </div>
          {mode === 'arica_auto' && (
            <div>
              <Note>
                ARICA 取込済データから、この明細 ({txnDate}) の証憑候補を自動検索します。
                「添付候補に採用」で R2 仮保存 → プレビュー確認後、「承認 → 登録 ▸」で freee filebox へ確定移動。
              </Note>
              <div style={{ marginTop: '8px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                <Button small appearance="secondary" onClick={loadCandidates} disabled={candLoading}>
                  {candLoading ? '検索中…' : '候補を再検索 ▸'}
                </Button>
                {candidates != null && !candLoading && (
                  <span style={{ fontSize: '0.6875rem', color: '#6b6560' }}>候補 {candidates.length} 件</span>
                )}
              </div>
              {candError && (
                <div style={{ marginTop: '6px', fontSize: '0.6875rem', color: '#c67320' }}>{candError}</div>
              )}
              {candidates != null && candidates.length === 0 && !candError && !candLoading && (
                <div style={{ marginTop: '8px', padding: '8px 10px', background: '#fff', border: '1px dashed #ccc', borderRadius: '4px', fontSize: '0.6875rem', color: '#8a8580', textAlign: 'center' }}>
                  ARICA に該当候補なし — 手動アップロードか既存 receipt 検索で対応
                </div>
              )}
              {candidates != null && candidates.length > 0 && (
                <div style={{ marginTop: '8px', maxHeight: '260px', overflowY: 'auto', border: '1px solid #e5e4e2', borderRadius: '4px', background: '#fff' }}>
                  {candidates.map((c, idx) => {
                    const staged = c.already_staged === true;
                    return (
                      <div key={idx} style={{ padding: '6px 10px', fontSize: '0.6875rem', borderBottom: '1px solid #f0eeec', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            📄 {c.label || c.filename || `${c.source || 'arica'}:${c.source_ref ?? idx}`}
                          </div>
                          <div style={{ color: '#8a8580', fontSize: '0.625rem' }}>
                            {[
                              c.source && `source: ${c.source}`,
                              c.kind && `kind: ${c.kind}`,
                              c.vendor,
                              c.date,
                              c.amount != null ? `¥${Number(c.amount).toLocaleString('ja-JP')}` : null,
                            ].filter(Boolean).join(' / ') || '—'}
                          </div>
                        </div>
                        <Button
                          small
                          appearance={staged ? 'tertiary' : 'primary'}
                          disabled={staged || adopting === idx}
                          onClick={() => handleAdopt(c, idx)}
                        >
                          {staged ? '採用済' : adopting === idx ? '採用中…' : '添付候補に採用'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {mode === 'filebox_no' && (
            <div>
              <Note>
                freee ファイルボックスの「No」表示値で既存証憑を指定して添付します (worker が receipt id へ解決)。
              </Note>
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={fileboxNo}
                  onChange={(e) => setFileboxNo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleFileboxAttach(); } }}
                  placeholder="ファイルボックス No (数値)"
                  style={{ width: '200px', padding: '4px 8px', fontSize: '0.75rem', border: '1px solid #e5e4e2', borderRadius: '4px' }}
                />
                <Button small appearance="primary" onClick={handleFileboxAttach} disabled={fileboxAttaching || !fileboxNo.trim()}>
                  {fileboxAttaching ? '添付中…' : '添付 ▸'}
                </Button>
              </div>
            </div>
          )}
          {mode === 'upload' && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/*"
                onChange={handleUpload}
                disabled={uploading}
                style={{ fontSize: '0.75rem' }}
              />
              {uploading && <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#6b6560' }}>アップロード中...</span>}
            </div>
          )}
          {mode === 'search' && (
            <div>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="description キーワード (任意)"
                  style={{ flex: 1, padding: '4px 8px', fontSize: '0.75rem', border: '1px solid #e5e4e2', borderRadius: '4px' }}
                />
                <Button small appearance="secondary" onClick={handleSearch} disabled={searching}>
                  {searching ? '検索中...' : '検索'}
                </Button>
              </div>
              {searchResults.length > 0 && (
                <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #e5e4e2', borderRadius: '4px' }}>
                  {searchResults.map((r) => (
                    <div key={r.id} style={{ padding: '6px 10px', fontSize: '0.6875rem', borderBottom: '1px solid #f0eeec', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                      <div style={{ flex: 1 }}>
                        <div>#{r.id} {r.issue_date && `(${r.issue_date})`}</div>
                        <div style={{ color: '#6b6560' }}>{r.description || '—'}</div>
                      </div>
                      <Button
                        small
                        appearance="primary"
                        disabled={attachedIds.includes(r.id)}
                        onClick={() => handleAttach(r.id)}
                      >
                        {attachedIds.includes(r.id) ? '添付済' : '添付'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {mode === 'jcb_auto' && (
            <div>
              <Note>
                ARICA に取込み済の JCB 振込情報 PDF を取引日 ({txnDate}) で取得 → R2 仮保存。
                プレビュー確認後、 「承認 → 登録 ▸」 で freee filebox に確定移動。
                JCB入金 (= 株式会社ジェーシービー) 行のみ対象。
              </Note>
              <div style={{ marginTop: '8px' }}>
                <Button appearance="primary" onClick={handleJcbAutoStage} disabled={autoAttaching}>
                  {autoAttaching ? '取得中...' : 'ARICA から仮保存 ▸'}
                </Button>
              </div>
            </div>
          )}
          {mode === 'nicos_auto' && (
            <div>
              <Note>
                ARICA に取込み済の MUFG NICOS 振込明細 PDF を取引日 ({txnDate}) で取得 → R2 仮保存。
                1 PDF が 8037 / 8028 両 merchant を cover しているため content_hash で dedupe。
                プレビュー確認後、 「承認 → 登録 ▸」 で freee filebox に確定移動。
                NICOS入金 (= 三菱ＵＦＪニコス株式会社) 行のみ対象。
              </Note>
              <div style={{ marginTop: '8px' }}>
                <Button appearance="primary" onClick={handleNicosAutoStage} disabled={autoAttaching}>
                  {autoAttaching ? '取得中...' : 'ARICA から仮保存 ▸'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {error && <div style={{ marginTop: '6px', fontSize: '0.6875rem', color: '#c33939' }}>{error}</div>}
      {info && <div style={{ marginTop: '6px', fontSize: '0.6875rem', color: '#338650' }}>{info}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// BankCorrectionPanel — 2026-07-14 確定設計 §2 (銀行 修正指示コメント欄)
// JournalReviewPage の修正指示 UX を移植 (textarea + 学習checkbox既定ON +
// 状態チップ + 履歴 + 解決ボタン)。journal 版との構造差:
//   - POST /api/bank-wallet-txns/:id/corrections が D1 台帳 INSERT + 再分析 dispatch
//     (force_ai) まで一括で行う — createJob / createInstruction の二重書込みは不要。
//   - 反映は complete 連鎖 (bankSubmitSuggestionCore) 経由 = proposal 正本
//     (wallet_txn_processing.proposed_deal_json) の書込経路は不変 (新経路を作らない)。
//   - learn=OFF は一回限りの適用 (worker 側 gate により /api/learn に構造的に到達しない)。
//   - registered 行は 409 (deal 化済 → 仕訳レビューページの修正指示を使用)。
// endpoint 未配備 (404) / flag off (503) は防御的に案内表示。
// ---------------------------------------------------------------------------
const BANK_CORRECTABLE_STATUSES = ['suggested', 'needs_review', 'error', 'pending', 'mechanical_suggest_stage1'];
// 2026-09-25 B案: worker 側 STRICT_FLOW_TYPES と同一 (mechanical-approve が RULE 31 で 409 になる flow)
const STRICT_FLOW_TYPES_UI = new Set(['bank_auto_debit', 'bank_transfer_in']);

const isOpenBankCorrection = (c: BankCorrection) => c.status === 'open' || c.status === 'in_progress';

const bankCorrectionChipLabel = (c: BankCorrection): string => {
  switch (c.status) {
    case 'open': return '修正待ち(指示済み)';
    case 'in_progress': return '修正中';
    case 'done': return '修正完了';
    default: return correctionStatusToUnified(c.status).label;
  }
};

interface BankCorrectionPanelProps {
  walletTxnId: number;
  processingStatus: string;
  isRegistered: boolean;
  ensureSession: () => Promise<string>;
  onToast: (t: { kind: 'success' | 'error' | 'notice' | 'info'; text: string }) => void;
  onSubmitted: () => void;
}

const BankCorrectionPanel: React.FC<BankCorrectionPanelProps> = ({
  walletTxnId, processingStatus, isRegistered, ensureSession, onToast, onSubmitted,
}) => {
  const [items, setItems] = useState<BankCorrection[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const [draft, setDraft] = useState('');
  const [learn, setLearn] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<number | null>(null);

  const reload = useCallback(async () => {
    try {
      const r = await fetchBankCorrections([walletTxnId]);
      setItems(r.corrections.filter((c) => Number(c.wallet_txn_id) === walletTxnId));
      setUnavailable(false);
    } catch (err) {
      const e = err as Error & { status?: number };
      if (e.status === 404) setUnavailable(true);
      // その他の取得失敗は履歴なし表示のまま (ページは壊さない)
    }
  }, [walletTxnId]);

  useEffect(() => {
    setItems([]);
    setUnavailable(false);
    setDraft('');
    setLearn(true);
    setSendError(null);
    reload();
  }, [walletTxnId, reload]);

  const openItems = items.filter(isOpenBankCorrection);
  const correctable = !isRegistered && BANK_CORRECTABLE_STATUSES.includes(processingStatus);

  const submit = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSendError(null);
    setSending(true);
    try {
      // dispatch 監査用 session (open 失敗でも指示は台帳保存され、次回 dispatch が
      // 自動搬送する設計 W-2 = 自己修復。session なしで送信継続)
      let sid: string | undefined;
      try { sid = await ensureSession(); } catch { sid = undefined; }
      const r = await createBankCorrection({
        wallet_txn_id: walletTxnId,
        instruction_text: text,
        learn,
        created_by: 'web_app_user',
        ...(sid ? { session_id: sid } : {}),
      });
      setDraft('');
      onToast({
        kind: 'success',
        text: `修正指示${r?.id != null ? ` #${r.id}` : ''} を送信 — 再分析キューに投入しました (分析完了で提案が更新されます)`,
      });
      await reload();
      onSubmitted();
    } catch (err) {
      const e = err as Error & { status?: number; body?: any };
      let msg: string;
      if (e.status === 503) msg = '修正指示機能は現在 OFF です (ops_flag bank_correction_enabled)';
      else if (e.status === 404) msg = '修正指示 API 未配備 (worker 側実装待ち)';
      else if (e.status === 409 && e.body?.error === 'correction_already_open') msg = '未解決の修正指示が既にあります — 「解決にする」で解決してから新しい指示を送信してください';
      else if (e.status === 409) msg = e.body?.hint || e.body?.message || 'この状態では修正指示できません (登録済は仕訳レビューページ / 承認済は登録実行 or 却下が先)';
      else if (e.status === 502) msg = '指示は保存されましたが再分析 dispatch に失敗しました — 次回の再分析時に自動適用されます (自己修復)';
      else msg = e.message;
      setSendError(msg);
      if (e.status === 502) { await reload(); onSubmitted(); }
    } finally {
      setSending(false);
    }
  };

  const resolve = async (c: BankCorrection) => {
    if (resolving != null) return;
    setResolving(c.id);
    try {
      await updateBankCorrectionStatus(c.id, 'done');
      onToast({ kind: 'success', text: `修正指示 #${c.id} を解決にしました` });
      await reload();
    } catch (err) {
      onToast({ kind: 'error', text: `更新失敗: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setResolving(null);
    }
  };

  return (
    <div style={{ marginTop: '12px', marginBottom: '12px', border: '1px solid #dce4eb', borderRadius: '6px', background: '#f8fafc', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 13px', borderBottom: '1px solid #e1e7ec', background: '#f4f7fa', flexWrap: 'wrap' }}>
        <Text size={0.875} weight="bold">この取引への修正指示</Text>
        {openItems.length > 0 && (
          <span style={{ padding: '2px 7px', borderRadius: '3px', fontSize: '0.625rem', fontWeight: 800, color: '#33699e', background: '#fff', border: '1px solid #b9d0e8', whiteSpace: 'nowrap' }}>
            修正待ち {openItems.length} 件
          </span>
        )}
        <span style={{ fontSize: '0.6875rem', color: '#6b6560' }}>
          (指示を書くと AI 担当が再分析して提案仕訳を更新します。反映が済むと「修正完了」になります)
        </span>
      </div>

      {/* 指示履歴 + 状態チップ */}
      {items.map((c) => {
        const open = isOpenBankCorrection(c);
        const b = correctionStatusToUnified(c.status);
        const learnOff = c.learn === 0 || c.learn === false;
        return (
          <div
            key={c.id}
            style={{
              display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '8px 13px',
              borderBottom: '1px solid #e1e7ec', fontSize: '0.75rem',
              background: open ? '#eef4fb' : '#fff', opacity: open ? 1 : 0.75,
            }}
          >
            <span
              style={{
                flex: '0 0 auto', padding: '2px 7px', borderRadius: '3px', fontSize: '0.625rem', fontWeight: 800,
                whiteSpace: 'nowrap', color: b.fg, background: b.bg, border: `1px solid ${b.bd}`,
              }}
              title={c.status}
            >
              {bankCorrectionChipLabel(c)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.instruction_text}</div>
              <div style={{ marginTop: '3px', color: '#8a8580', fontSize: '0.625rem' }}>
                指示 {c.created_at ? c.created_at.replace('T', ' ').slice(0, 16) : '—'}
                {learnOff && ' ／ 一回限り(学習なし)'}
                {open && ' — 再分析で対応中 (完了で提案が更新されます)'}
                {c.status === 'done' && ` → 完了 ${c.updated_at ? c.updated_at.replace('T', ' ').slice(0, 16) : ''}`}
                {c.status === 'done' && c.result_note && ` (${c.result_note})`}
              </div>
            </div>
            {open && (
              <Button small appearance="tertiary" disabled={resolving === c.id} onClick={() => resolve(c)}>
                {resolving === c.id ? '更新中…' : '解決にする'}
              </Button>
            )}
          </div>
        );
      })}

      {/* 入力フォーム or 状態別の案内 */}
      {unavailable ? (
        <div style={{ padding: '10px 13px', fontSize: '0.6875rem', color: '#c67320' }}>
          修正指示 API 未配備 (worker 側実装待ち) — 配備後にこの欄から指示できます。
        </div>
      ) : !correctable ? (
        <div style={{ padding: '10px 13px', fontSize: '0.6875rem', color: '#8a8580' }}>
          {isRegistered
            ? 'この取引は freee 登録済 (deal 化済) のため、修正指示は仕訳レビューページの修正指示欄から行ってください。'
            : processingStatus === 'approved'
              ? '承認済みの行には指示できません — 先に「登録を実行 ▸」するか却下してください。'
              : processingStatus === 'queued' || processingStatus === 'processing'
                ? '分析中です — 完了後に修正指示できます。'
                : 'この状態では修正指示できません。'}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', padding: '10px 13px', flexWrap: 'wrap' }}>
          <textarea
            style={{
              flex: 1, minWidth: '220px', minHeight: '56px', padding: '8px',
              border: '1px solid #dce4eb', borderRadius: '4px', fontSize: '0.78125rem',
              fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
            }}
            maxLength={2000}
            placeholder="例: 部門を渋谷店に変更 ／ 勘定科目を消耗品費→修繕費 (Ctrl+Enter で送信)"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); submit(); }
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-end' }}>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.6875rem', color: '#42576e', cursor: 'pointer' }}
              title="ON: この指示を今後の同種明細のルールとして学習 / OFF: この1件だけに適用"
            >
              <input type="checkbox" checked={learn} onChange={(e) => setLearn(e.target.checked)} />
              この指示を今後のルールとして学習する
            </label>
            <Button appearance="primary" small disabled={!draft.trim() || sending} onClick={submit}>
              {sending ? <InlineSpinner isLoading /> : '指示を送信'}
            </Button>
            {!learn && (
              <span style={{ fontSize: '0.625rem', color: '#8a8580' }}>一回限りの適用(学習しない)</span>
            )}
            {sendError && <span style={{ color: '#c33939', fontSize: '0.6875rem', maxWidth: '260px', textAlign: 'right' }}>{sendError}</span>}
          </div>
        </div>
      )}
    </div>
  );
};
